import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  LayoutTemplate,
  Image as ImageIcon,
      Type,
  Shapes,
  Upload,
  Square,
  Circle,
  Triangle,
  Star,
  Minus,
  ArrowRight,
  Search,
  X,
  Pentagon,
  Hexagon,
  Diamond,
  Plus,
  Heart,
  Layers,
  Lock,
  Unlock,
  ArrowUp,
  ArrowDown,
  Save,
  ChevronDown,
  Trash2,
  Copy,
  ClipboardPaste,
  Tag,
  FilePlus,
  ChevronRight,
  ChevronUp,
  LayoutGrid,
  List,
  CalendarDays,
  MoreVertical,
  Loader2,
  Octagon,
  Zap,
  Check,
  Cloud,
  MessageSquare,
  ArrowLeftRight,
  Activity,
} from 'lucide-react';

import { apiFetch } from './api/client';
import { apiUrl } from './api/config';
import { apiAxios, assertAxiosJsonOk } from './api/axiosClient';
import {
  GCP_IMAGES_MAX,
  listGcpImages,
  gcpImageItemUrl,
  gcpImageStreamUrl,
  gcpImageThumbnailUrl,
  loadGcpImageForCanvas,
  isGcpDisplayOnlyUrl,
  resolveGcpImageForCanvas,
  isGcpImageStreamUrl,
  seedGcpSignedUrlCache,
  clearGcpExportImageCache,
  normalizeKonvaSceneImages,
  uploadGcpImageFile,
  removeGcpImage,
} from './api/gcpImagesApi';
import {
  LIVE_EVENT_FIELD_ALIAS_LABELS,
  LIVE_EVENT_PLACEHOLDER_CATALOG,
} from './data/liveEventPlaceholderCatalog';
import cfsLogo from './assets/cfs-logo.png';
import { subscribeGlobalApiLoading } from './api/apiLoading';
import TemplateFabricThumb from './components/TemplateFabricThumb';
import ColorPickerField from './components/ColorPickerField';
import PlaceholderFieldDropdown from './components/PlaceholderFieldDropdown';
import CanvasHorizontalScrollbar from './components/CanvasHorizontalScrollbar';
import {
  snapshotTextFieldConfig,
  applyTextLayout,
  applyDynamicFieldText,
  absorbTextScaleIntoBox,
  applyTextContainInBox,
  syncTextboxClipPath,
  ensureTextObjectVisible,
  fitTextObjectsForExport,
  countIntendedLines,
  deriveAnchor,
} from './utils/textLayoutEngine';
import {
  TEXT_BOX_GROUP_FLAG,
  TEXT_BOX_PAIR_ID,
  TEXT_BOX_FRAME_FLAG,
  TEXT_BOX_LABEL_FLAG,
  isTextBoxGroup,
  isTextBoxFrame,
  isTextBoxLabel,
  resolveEditorTextBoxLabel,
  getEditorTextBoxResizeTarget,
  addResizableTextboxToCanvas,
  migrateEditorTextboxesToResizableGroups,
  restoreAllTextBoxGroupsOnCanvas,
  beginTextBoxFrameResizeSession,
  clearTextBoxFrameResizeSession,
  resizeTextBoxFrameFromTransform,
  commitTextBoxFrameResize,
  promoteTextBoxFrameSelection,
  getTextBoxPairId,
  syncTextBoxPairGeometry,
  enterTextBoxGroupEditing,
  syncTextBoxGroupAfterTextEdit,
} from './utils/resizableTextbox';
import { jsPDF } from 'jspdf';
import { getJsPdfPageFormat } from './utils/pdfPageSize';
import {
  loadSceneFontsAndRefresh,
  applyFontFamilyQuick,
  refreshKonvaTextStyles,
  warmSceneFontsLocally,
  downloadSceneFontsLocally,
} from './utils/googleFonts';
import { EDITOR_FONTS } from './constants/editorFonts';
import { exportSceneToVectorPdf, exportCanvasToVectorPdf } from './export/vectorPdfExport';
import { clearMeasureCache } from './typography/measureText';
import { exportKonvaPagesToPdf } from './export/htmlFlyerExport';
import { captureKonvaDesignSurface, exportKonvaPagesWysiwygPdf } from './export/konvaWysiwygExport';
import {
  buildKonvaSceneForPdf,
  ensureKonvaExportImagesReady,
  inlineSceneAssetsForPdf,
} from './export/konvaPdfScene';
import { computeMediumImageDimensions } from './utils/imagePlacement';
import { templateJsonNeedsServerFontMigration } from './sceneGraph/serverPdfAdapter';
import { applyMappingsToSceneGraph } from './sceneGraph/fabricAdapter';
import KonvaDesignCanvas from './editor/KonvaDesignCanvas';
import { getKonvaShapeSpec, konvaShapeSpecAt } from './editor/konvaShapeSpecs';
import {
  serializeKonvaDocument,
  serializeKonvaMultiPageDocument,
  parseEditorDocument,
  parseEditorDocumentPages,
  fabricJsonToKonvaScene,
  cloneScene,
} from './editor/editorDocument';
import { useEditorStore } from './store/useEditorStore';

/** Konva + Zustand + HTML text overlay (set REACT_APP_USE_KONVA_EDITOR=1 in .env). */
const USE_KONVA_EDITOR =
  process.env.REACT_APP_USE_KONVA_EDITOR === '1' ||
  process.env.REACT_APP_USE_KONVA_EDITOR === 'true';

function getKonvaSelectedSceneNode() {
  if (!USE_KONVA_EDITOR) return null;
  const st = useEditorStore.getState();
  const id = st.selectedId || st.selectedIds[0];
  return id ? st.getObjectById(id) : null;
}

/** fieldConfig snapshot for Konva scene text nodes (matches Fabric snapshot shape). */
function snapshotKonvaTextFieldConfig(node) {
  if (!node || node.type !== 'text') return undefined;
  const t = node.transform || {};
  const ty = node.typography || {};
  const pad = node.constraints?.padding ?? 2;
  return {
    maxWidth: Math.max(24, Number(t.width) || 200),
    maxHeight: Math.max(24, Number(t.height) || 80),
    behavior: 'fitBox',
    baseFontSize: Math.max(6, Number(ty.fontSize) || 12),
    minFontSize: node.constraints?.minFontSize ?? 6,
    anchor: node.constraints?.anchor || 'top-left',
    intendedLines: 1,
    padding: pad,
    textAlign: ty.textAlign || 'left',
    lineHeight: ty.lineHeight ?? 1.16,
    charSpacing: ty.letterSpacing ?? 0,
  };
}

function isKonvaTextSelection(obj) {
  return Boolean(obj?._konvaEditor && obj?.type === 'text');
}

function normalizeTemplateId(id) {
  if (id == null || id === '') return null;
  const s = String(id).trim();
  return /^\d+$/.test(s) ? s : null;
}

const DEFAULT_TEXT_PLACEHOLDERS = [
  { label: 'Flyer Header', key: 'flyerHeader' },
  { label: 'Event Location', key: 'eventLocation' },
];
const DEFAULT_IMAGE_PLACEHOLDERS = [
  { label: 'Manufacturer Logo 1', key: 'manufacturerLogoOneSignedURL' },
  { label: 'Program Logo', key: 'programLogoSignedURL' },
];

const INITIAL_PLACEHOLDERS = {
  text: [...DEFAULT_TEXT_PLACEHOLDERS],
  image: [...DEFAULT_IMAGE_PLACEHOLDERS],
};

const STATIC_VALUES_URL =
  process.env.REACT_APP_STATIC_VALUES_URL ||
  'https://dev-cfs.royalcyber.org/api/v1/cfs-liveevents-broker/liveevents/assets/staticValues';

/** Shown in the header when user starts a blank design via “New”. */
const UNSAVED_TEMPLATE_LABEL = '{unsaved template}';

/** GCP grid thumb — signed URL for preview; API stream + auth when no signed URL. */
function GcpLibraryThumbnail({ item }) {
  const [displayUrl, setDisplayUrl] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    setDisplayUrl('');
    setLoaded(false);
    setFailed(false);
    if (!item?.objectKey) return undefined;
    const thumbSrc = gcpImageThumbnailUrl(item);
    if (!thumbSrc) return undefined;

    if (!isGcpImageStreamUrl(thumbSrc)) {
      setDisplayUrl(thumbSrc);
      return undefined;
    }

    (async () => {
      try {
        const resolved = await resolveGcpImageForCanvas(thumbSrc, item.objectKey);
        if (cancelled) {
          if (resolved.startsWith('blob:')) URL.revokeObjectURL(resolved);
          return;
        }
        if (resolved.startsWith('blob:')) blobUrl = resolved;
        setDisplayUrl(resolved);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      if (blobUrl && blobUrl.startsWith('blob:')) URL.revokeObjectURL(blobUrl);
    };
  }, [item?.objectKey, item?.signedUrl]);
  return (
    <span className="relative block w-full h-full">
      <span className="absolute inset-0 z-0 flex items-center justify-center bg-slate-100">
        {!loaded && !failed && <Loader2 size={20} className="animate-spin text-slate-400" aria-hidden />}
        {failed && (
          <span className="text-[10px] text-slate-500 px-2 text-center leading-tight">Couldn’t load preview</span>
        )}
      </span>
      {displayUrl ? (
        <img
          src={displayUrl}
          alt=""
          className={`absolute inset-0 z-[1] w-full h-full object-cover transition-opacity duration-200 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          decoding="async"
        />
      ) : null}
    </span>
  );
}

/** Human-readable label for `fieldKey`. Checks placeholder lists, then broker alias names. */
function resolvePlaceholderLabel(fieldKey, objType, textList, imageList) {
  if (!fieldKey) return null;
  const isText = objType === 'i-text' || objType === 'textbox' || objType === 'text';
  const primary = isText ? textList : imageList;
  const secondary = isText ? imageList : textList;
  const found = primary.find((o) => o.key === fieldKey) || secondary.find((o) => o.key === fieldKey);
  if (found) return found.label;
  if (LIVE_EVENT_FIELD_ALIAS_LABELS[fieldKey]) return LIVE_EVENT_FIELD_ALIAS_LABELS[fieldKey];
  return fieldKey;
}

/** Objects currently in Fabric's selection (single, multi, or activeSelection). */
function getActiveTargets(canvas) {
  const active = canvas?.getActiveObject?.();
  if (!active) return [];
  if (active.type === 'activeSelection') {
    if (typeof active.getObjects === 'function') return active.getObjects();
    if (active._objects && Array.isArray(active._objects)) return active._objects;
    return [];
  }
  return [active];
}

/** Fabric loadFromJSON reviver — custom props are not always restored without this. */
const FABRIC_JSON_EXTRA_PROPS = ['fieldKey', 'fieldConfig', '_shapeLabelGroup', 'backgroundImage'];

function isShapeLabelGroup(obj) {
  if (!obj || obj.type !== 'group') return false;
  return !!(obj._shapeLabelGroup || (typeof obj.get === 'function' && obj.get('_shapeLabelGroup')));
}

/** Non-text part of a double-click shape caption group (rect, path, etc.). */
function isShapeLabelGeometry(obj) {
  if (!obj) return false;
  const g = obj.group;
  if (!g || !isShapeLabelGroup(g)) return false;
  return obj.type !== 'i-text' && obj.type !== 'textbox' && obj.type !== 'text';
}

/** Only true image placeholders may be swapped for mapping URLs — never shape fill geometry. */
function shouldReplaceObjectWithMappingImage(obj) {
  if (!obj || isShapeLabelGeometry(obj)) return false;
  if (obj.type === 'image') return true;
  if (!['rect', 'circle', 'triangle', 'polygon', 'path'].includes(obj.type)) return false;
  const fc = obj.fieldConfig || (typeof obj.get === 'function' ? obj.get('fieldConfig') : null);
  if (fc?.role === 'image') return true;
  return !obj.group;
}

function refreshFabricGroupsForExport(canvas) {
  forEachCanvasObjectDeep(canvas, (o) => {
    o.setCoords?.();
    if (o.type === 'group' && typeof o.addWithUpdate === 'function') {
      try {
        o.addWithUpdate();
      } catch (_) {
        /* Fabric version without addWithUpdate */
      }
    }
  });
  canvas?.renderAll?.();
}

function fabricCustomPropsReviver(jsonObj, fabricObj) {
  if (!jsonObj || !fabricObj) return;
  if (jsonObj.fieldKey != null && jsonObj.fieldKey !== '') {
    fabricObj.fieldKey = jsonObj.fieldKey;
    fabricObj.set?.('fieldKey', jsonObj.fieldKey);
  }
  if (jsonObj.fieldConfig != null) {
    fabricObj.fieldConfig = jsonObj.fieldConfig;
    fabricObj.set?.('fieldConfig', jsonObj.fieldConfig);
  }
  if (jsonObj._shapeLabelGroup) {
    fabricObj._shapeLabelGroup = true;
    fabricObj.set?.('_shapeLabelGroup', true);
  }
  if (jsonObj[TEXT_BOX_GROUP_FLAG] || jsonObj._textBoxGroup) {
    fabricObj[TEXT_BOX_GROUP_FLAG] = true;
    fabricObj.set?.(TEXT_BOX_GROUP_FLAG, true);
    if (jsonObj._boxWidth != null) fabricObj._boxWidth = jsonObj._boxWidth;
    if (jsonObj._boxHeight != null) fabricObj._boxHeight = jsonObj._boxHeight;
  }
  if (jsonObj[TEXT_BOX_PAIR_ID]) {
    fabricObj[TEXT_BOX_PAIR_ID] = jsonObj[TEXT_BOX_PAIR_ID];
    fabricObj.set?.(TEXT_BOX_PAIR_ID, jsonObj[TEXT_BOX_PAIR_ID]);
  }
  if (jsonObj[TEXT_BOX_FRAME_FLAG] || jsonObj._textBoxFrame) {
    fabricObj[TEXT_BOX_FRAME_FLAG] = true;
    fabricObj.set?.(TEXT_BOX_FRAME_FLAG, true);
  }
  if (jsonObj[TEXT_BOX_LABEL_FLAG] || jsonObj._textBoxLabel) {
    fabricObj[TEXT_BOX_LABEL_FLAG] = true;
    fabricObj.set?.(TEXT_BOX_LABEL_FLAG, true);
  }
  if (fabricObj.type === 'textbox') {
    fabricObj.clipPath = null;
    fabricObj.set?.('clipPath', null);
  }
}

function normalizeFabricJsonInput(json) {
  if (json == null) return null;
  let raw = json;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
}

function getObjectFieldKey(obj) {
  if (!obj) return null;
  if (obj.fieldKey != null && String(obj.fieldKey).trim() !== '') return String(obj.fieldKey);
  if (typeof obj.get === 'function') {
    const v = obj.get('fieldKey');
    if (v != null && String(v).trim() !== '') return String(v);
  }
  return null;
}

/** Text object that should receive dynamic copy (standalone or inner label in a group). */
function resolveFieldTextTarget(obj) {
  if (!obj) return null;
  const editorLabel = resolveEditorTextBoxLabel(obj);
  if (editorLabel) return editorLabel;
  if (obj.type === 'i-text' || obj.type === 'textbox' || obj.type === 'text') return obj;
  if (obj.type === 'group' && typeof obj.getObjects === 'function') {
    return (
      getInnerShapeLabelText(obj) ||
      obj.getObjects().find((c) => c.type === 'i-text' || c.type === 'textbox' || c.type === 'text') ||
      null
    );
  }
  return null;
}

/** Walk all canvas objects including nested groups (shape labels, grouped layouts). */
function forEachCanvasObjectDeep(canvas, fn) {
  if (!canvas?.getObjects) return;
  const walk = (obj) => {
    fn(obj);
    if (obj.type === 'group' && typeof obj.getObjects === 'function') {
      obj.getObjects().forEach(walk);
    }
  };
  canvas.getObjects().forEach(walk);
}

/** Shape caption placeholder is editor-only unless the label has a data fieldKey. */
function clearNonFieldPlaceholderText(canvas) {
  forEachCanvasObjectDeep(canvas, (o) => {
    if (o.type !== 'i-text' && o.type !== 'textbox' && o.type !== 'text') return;
    if (o.fieldKey) return;
    if (isShapeLabelPlaceholderText(o.text)) {
      o.set({ text: '' });
      o.setCoords?.();
    }
  });
}

/** fieldKey on legacy shape groups → inner textbox; strip keys from shape geometry so PDF export keeps fills. */
function finalizeCanvasTextBoxes(canvas) {
  if (!canvas) return;
  const { fabric } = require('fabric');
  try {
    restoreAllTextBoxGroupsOnCanvas(canvas);
    migrateEditorTextboxesToResizableGroups(canvas, fabric);
  } catch (_) {}
}

function migrateShapeGroupFieldKeyToLabel(group) {
  if (!group || group.type !== 'group' || !isShapeLabelGroup(group)) return;
  const parts = group.getObjects?.() || [];
  const inner =
    getInnerShapeLabelText(group) ||
    parts.find((c) => c.type === 'i-text' || c.type === 'textbox' || c.type === 'text');
  const groupKey = getObjectFieldKey(group);
  if (inner && groupKey && !getObjectFieldKey(inner)) {
    inner.fieldKey = groupKey;
    inner.fieldConfig = group.fieldConfig || snapshotTextFieldConfig(inner);
    inner.set?.('fieldKey', inner.fieldKey);
    inner.set?.('fieldConfig', inner.fieldConfig);
  }
  delete group.fieldKey;
  delete group.fieldConfig;
  group.set?.('fieldKey', undefined);
  group.set?.('fieldConfig', undefined);
  for (const part of parts) {
    if (part === inner) continue;
    if (part.type === 'i-text' || part.type === 'textbox' || part.type === 'text') continue;
    delete part.fieldKey;
    delete part.fieldConfig;
    part.set?.('fieldKey', undefined);
    part.set?.('fieldConfig', undefined);
  }
}

function migrateAllFieldKeysOnCanvas(canvas) {
  forEachCanvasObjectDeep(canvas, migrateShapeGroupFieldKeyToLabel);
}

/** Fallback sample values for known catalog keys — used in preview mode when no real data exists. */
const PREVIEW_SAMPLE_VALUES = (() => {
  const out = {};
  for (const item of LIVE_EVENT_PLACEHOLDER_CATALOG) {
    if (item.kind === 'image') continue; // image placeholders need a real URL
    out[item.key] = item.label; // use the label as placeholder text
  }
  // Override with more realistic samples
  Object.assign(out, {
    organizationName: 'Acme Organization',
    programName: 'Spring Fundraiser 2025',
    eventLocation: '123 Main St, Springfield',
    flyerHeader: 'Join Us for a Special Event!',
    fundraiserBenefits: 'Support our community programs',
    dtLiveEventStart: 'June 15, 2025',
    tmLiveEventStart: '6:00 PM',
    tmLiveEventStartHR: '6',
    tmLiveEventStartMIN: '00',
    tmLiveEventStartAMPM: 'PM',
    dtLiveEventEnd: 'June 15, 2025',
    tmLiveEventEnd: '9:00 PM',
    tmLiveEventEndHR: '9',
    tmLiveEventEndMIN: '00',
    tmLiveEventEndAMPM: 'PM',
    dealerEmail: 'info@example.com',
    vipCoupon: 'VIP2025',
    referralCoupon: 'REF100',
    annualType: 'Annual',
    king: '$899',
    queen: '$699',
    full: '$599',
    twin: '$499',
  });
  return out;
})();

/**
 * Build preview mappings: merge user-entered data with sample fallbacks for every
 * dynamic field key present in the current scene that has no user-provided value.
 */
function buildPreviewMappings(userMappings, sceneObjects) {
  const merged = { ...userMappings };
  for (const node of sceneObjects || []) {
    const key = node.fieldKey;
    if (!key || merged[key] != null) continue;
    if (node.type === 'text') {
      const catalog = LIVE_EVENT_PLACEHOLDER_CATALOG.find((c) => c.key === key);
      const label = catalog?.label || key;
      merged[key] = PREVIEW_SAMPLE_VALUES[key] || label;
    }
    // Image nodes with fieldKey get a placeholder only if a real URL is provided via user mappings;
    // we skip auto-filling images to avoid broken image icons.
  }
  return merged;
}

function lookupMappingValue(mappings, key) {
  if (!mappings || key == null) return undefined;
  if (mappings[key] !== undefined && mappings[key] !== null) return mappings[key];
  const lk = String(key).toLowerCase();
  for (const k of Object.keys(mappings)) {
    if (String(k).toLowerCase() === lk && mappings[k] !== undefined && mappings[k] !== null) {
      return mappings[k];
    }
  }
  return undefined;
}

/** Apply placeholder JSON mappings onto a canvas (or clone) for preview/export. */
async function applyMappingsToFabricCanvas(canvas, mappings) {
  if (!canvas || !mappings) return;
  migrateAllFieldKeysOnCanvas(canvas);
  const { fabric } = require('fabric');
  const objs = [];
  forEachCanvasObjectDeep(canvas, (o) => objs.push(o));
  const appliedText = new Set();
  for (const obj of objs) {
    const textObj = resolveFieldTextTarget(obj);
    if (textObj) {
      if (appliedText.has(textObj)) continue;
      const key = getObjectFieldKey(textObj) || getObjectFieldKey(obj);
      if (!key) continue;
      const value = lookupMappingValue(mappings, key);
      if (value === undefined || value === null) continue;
      appliedText.add(textObj);
      const str = String(value);
      if (str && textObj._isShapePlaceholder) {
        delete textObj._isShapePlaceholder;
        if (textObj.fill === '#64748b') textObj.set({ fill: '#111827' });
      }
      let merged = { ...(textObj.fieldConfig || textObj.get?.('fieldConfig') || {}) };
      if (!merged.maxWidth || !merged.maxHeight || !merged.baseFontSize) {
        const snap = snapshotTextFieldConfig(textObj);
        if (snap) merged = { ...snap, ...merged };
      }
      try {
        applyDynamicFieldText(canvas, textObj, str, merged);
      } catch (_) {
        textObj.set({ text: str, splitByGrapheme: true, fontSize: merged.baseFontSize || textObj.fontSize });
        if (typeof textObj.initDimensions === 'function') textObj.initDimensions();
      }
      continue;
    }
    const key = getObjectFieldKey(obj);
    if (!key) continue;
    const value = lookupMappingValue(mappings, key);
    if (value === undefined || value === null) continue;
    if (shouldReplaceObjectWithMappingImage(obj)) {
      if (typeof value === 'string' && (value.startsWith('http') || value.startsWith('data:'))) {
        await new Promise((resolve) => {
          fabric.Image.fromURL(value, (img) => {
            obj.setCoords();
            const rect = obj.getBoundingRect(true);
            img.set({
              left: rect.left,
              top: rect.top,
              scaleX: rect.width / (img.width || 1),
              scaleY: rect.height / (img.height || 1),
              angle: obj.angle || 0,
              originX: 'left',
              originY: 'top',
            });
            canvas.remove(obj);
            canvas.add(img);
            resolve();
          }, { crossOrigin: 'anonymous' });
        });
      }
    }
  }
  clearNonFieldPlaceholderText(canvas);
  canvas.renderAll();
}

/** Parse Fabric fill (hex / rgb) for jsPDF setTextColor. */
function fabricFillToRgb(fill) {
  if (!fill || typeof fill !== 'string') return [17, 24, 39];
  const s = fill.trim();
  if (s.startsWith('#')) {
    let h = s.slice(1);
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length === 6) {
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    }
  }
  const m = s.match(/rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) return [Number(m[1]), Number(m[2]), Number(m[3])];
  return [17, 24, 39];
}

function pdfJsFontFamily(fabricFamily) {
  const s = String(fabricFamily || '').toLowerCase();
  if (s.includes('times') || s.includes('georgia') || s.includes('serif')) return 'times';
  if (s.includes('courier') || s.includes('mono')) return 'courier';
  return 'helvetica';
}

function pdfJsFontStyle(fabricWeight, fabricStyle) {
  const w = String(fabricWeight || '').toLowerCase();
  const st = String(fabricStyle || '').toLowerCase();
  const bold = w === 'bold' || w === '700' || w === '600' || (Number(w) >= 600 && Number.isFinite(Number(w)));
  const italic = st === 'italic' || st === 'oblique';
  if (bold && italic) return 'bolditalic';
  if (bold) return 'bold';
  if (italic) return 'italic';
  return 'normal';
}

/** Walk canvas tree including nested groups (for export-time text fitting). */
function forEachFabricTextDeep(canvas, fn) {
  if (!canvas?.getObjects) return;
  const walk = (obj) => {
    fn(obj);
    if (obj.type === 'group' && typeof obj.getObjects === 'function') {
      obj.getObjects().forEach(walk);
    }
  };
  canvas.getObjects().forEach(walk);
}

/**
 * Vector PDF: scene graph → SVG → PDF (no canvas PNG rasterization).
 * Fabric canvas remains the editor; export uses normalized scene graph + typography engine.
 */
async function exportFabricCanvasToPdfSelectableText(cloned, pageW, pageH, pdfFileName, exportMeta = {}) {
  refreshFabricGroupsForExport(cloned);
  cloned.renderAll?.();
  await exportCanvasToVectorPdf(cloned, pageW, pageH, pdfFileName, exportMeta);
}

function getInnerShapeLabelText(group) {
  if (!group || group.type !== 'group' || typeof group.getObjects !== 'function') return null;
  return group.getObjects().find((c) => c.type === 'i-text' || c.type === 'textbox' || c.type === 'text') || null;
}

function getPlaceholderTargetFromSelection(canvas) {
  if (USE_KONVA_EDITOR) {
    const node = getKonvaSelectedSceneNode();
    if (!node) return null;
    if (node.type === 'text' || node.type === 'image') return node;
    return null;
  }
  const raw = canvas?.getActiveObject?.();
  if (!raw) return null;
  if (isTextBoxGroup(raw) || isTextBoxFrame(raw)) return resolveEditorTextBoxLabel(raw) || raw;
  const inner = getInnerShapeLabelText(raw);
  if (inner) return inner;
  const editorLabel = resolveEditorTextBoxLabel(raw);
  if (editorLabel) return editorLabel;
  return raw;
}

const CANVAS_TOOLBAR_PAD = 4;
const PLACEHOLDER_DROPDOWN_MAX_H = 220;

/**
 * Keep the selection toolbar + placeholder dropdown inside the scaled canvas overlay.
 * @returns {{ left: number, top: number, dropdownOpensUp: boolean, dropdownMaxH: number, dropdownShiftX: number }}
 */
function clampPlaceholderToolbarToCanvas({
  leftScreen,
  topScreen,
  dropdownOpensUp,
  dropdownOpen,
  toolbarW,
  toolbarH,
  canvasW,
  canvasH,
  centerToolbarX,
}) {
  const pad = CANVAS_TOOLBAR_PAD;
  const gap = 4;
  let left = leftScreen;
  let top = topScreen;
  let opensUp = dropdownOpensUp;

  const dropdownMaxH = dropdownOpen
    ? Math.min(PLACEHOLDER_DROPDOWN_MAX_H, Math.max(96, canvasH - pad * 2 - toolbarH - gap))
    : PLACEHOLDER_DROPDOWN_MAX_H;

  if (centerToolbarX) {
    const half = toolbarW / 2;
    left = Math.max(pad + half, Math.min(left, canvasW - pad - half));
  } else {
    left = Math.max(pad, Math.min(left, canvasW - pad - toolbarW));
  }

  if (dropdownOpen) {
    const needBelow = toolbarH + gap + dropdownMaxH;
    const needAbove = dropdownMaxH + gap;
    const spaceBelow = canvasH - pad - top;
    const spaceAbove = top - pad;

    if (!opensUp && spaceBelow < needBelow) {
      opensUp = spaceAbove >= needBelow || spaceAbove > spaceBelow;
    }
    if (opensUp && spaceAbove < needAbove + toolbarH * 0.35) {
      opensUp = spaceBelow >= needBelow || spaceBelow > spaceAbove;
    }

    if (!opensUp) {
      top = Math.min(top, canvasH - pad - needBelow);
    } else {
      top = Math.max(top, pad + needAbove);
    }
  }

  top = Math.max(pad, Math.min(top, canvasH - pad - toolbarH));

  const toolbarLeft = centerToolbarX ? left - toolbarW / 2 : left;
  const dropdownW = Math.min(260, canvasW - pad * 2);
  let dropdownShiftX = 0;
  if (toolbarLeft + dropdownW > canvasW - pad) {
    dropdownShiftX = canvasW - pad - dropdownW - toolbarLeft;
  } else if (toolbarLeft < pad) {
    dropdownShiftX = pad - toolbarLeft;
  }

  return { left, top, dropdownOpensUp: opensUp, dropdownMaxH, dropdownShiftX };
}

/** Single Fabric objects that can receive an embedded caption (not existing caption groups). */
function isEmbeddableBareShape(o) {
  if (!o || o.type === 'activeSelection') return false;
  if (o.type === 'i-text' || o.type === 'textbox' || o.type === 'text' || o.type === 'image') return false;
  if (o.type === 'group') return false;
  return true;
}

const SHAPE_LABEL_PLACEHOLDER = 'Type here…';

function getShapePartFromLabelGroup(group) {
  if (!group || group.type !== 'group' || !isShapeLabelGroup(group) || typeof group.getObjects !== 'function') return null;
  return (
    group.getObjects().find((c) => c.type !== 'i-text' && c.type !== 'textbox' && c.type !== 'text') ||
    group.getObjects()[0] ||
    null
  );
}

function isShapeLabelPlaceholderText(text) {
  const t = String(text || '').trim();
  return !t || t === SHAPE_LABEL_PLACEHOLDER || t === 'Double-click to edit';
}

/** Keep shape caption textbox inside the shape bounds (anchor-pinned, no object scale). */
function fitShapeLabelInGroup(group, canvas) {
  const shape = getShapePartFromLabelGroup(group);
  const label = getInnerShapeLabelText(group);
  if (!shape || !label || !canvas) return;
  const sw = (shape.width || 0) * (shape.scaleX || 1);
  const sh = (shape.height || 0) * (shape.scaleY || 1);
  if (sw < 8 || sh < 8) return;
  const pad = Math.max(4, Math.min(sw, sh) * 0.1);
  const maxW = Math.max(20, sw - pad * 2);
  const maxH = Math.max(16, sh - pad * 2);
  const capFs = Math.max(9, Math.min(32, sh * 0.22));
  applyTextLayout(canvas, label, {
    text: label.text || '',
    config: {
      maxWidth: maxW,
      maxHeight: maxH,
      baseFontSize: Math.min(capFs, label.fontSize || capFs),
      behavior: 'fitBox',
      anchor: deriveAnchor(label),
      intendedLines: countIntendedLines(label.text),
      textAlign: label.textAlign || 'center',
    },
  });
}

function enterShapeLabelEditing(canvas, group) {
  const inner = getInnerShapeLabelText(group);
  if (!inner || !canvas) return;
  canvas.setActiveObject(inner);
  if (typeof inner.enterEditing === 'function') {
    inner.enterEditing();
    if (inner._isShapePlaceholder && isShapeLabelPlaceholderText(inner.text) && typeof inner.selectAll === 'function') {
      inner.selectAll();
    }
  }
  canvas.requestRenderAll();
}

const DEFAULT_CANVAS_SELECTION_STYLE = {
  borderColor: '#000000',
  cornerColor: '#000000',
  cornerStrokeColor: '#000000',
  borderScaleFactor: 1,
  cornerStyle: 'rect',
  cornerSize: 13,
};
const PLACEHOLDER_CANVAS_SELECTION_STYLE = {
  borderColor: '#a5b4fc',
  cornerColor: '#c7d2fe',
  cornerStrokeColor: '#818cf8',
  borderScaleFactor: 1.35,
  cornerStyle: 'rect',
  cornerSize: 13,
};

/** Max edge of bbox (px) below which text/image uses a single small resize dot. */
const TEXT_IMAGE_COMPACT_SELECTION_PX = 88;
const TEXT_IMAGE_SELECTION_STYLE = {
  borderColor: '#2a318b',
  cornerColor: '#ffffff',
  cornerStrokeColor: '#2a318b',
  cornerStyle: 'circle',
  cornerSize: 11,
  transparentCorners: false,
  borderScaleFactor: 1.2,
};
const TEXT_IMAGE_SELECTION_STYLE_COMPACT = {
  ...TEXT_IMAGE_SELECTION_STYLE,
  cornerSize: 6,
};
const TEXT_IMAGE_PLACEHOLDER_SELECTION_STYLE = {
  borderColor: '#2a318b',
  cornerColor: '#ffffff',
  cornerStrokeColor: '#2a318b',
  cornerStyle: 'circle',
  cornerSize: 11,
  transparentCorners: false,
  borderScaleFactor: 1.28,
};
const TEXT_IMAGE_PLACEHOLDER_SELECTION_STYLE_COMPACT = {
  ...TEXT_IMAGE_PLACEHOLDER_SELECTION_STYLE,
  cornerSize: 6,
};

const ALL_FABRIC_CONTROL_KEYS = ['tl', 'tr', 'bl', 'br', 'ml', 'mr', 'mt', 'mb', 'mtr'];

/** Middle-left / middle-right as vertical pills; top/bottom as horizontal pills. */
function installFabricMidHandlePills(fabric) {
  const utils = fabric?.controlsUtils;
  if (!utils?.renderSquareControl || !fabric?.Object?.prototype?.controls) return;
  const renderPillMidV = function renderPillMidV(ctx, left, top, styleOverride, fabricObject) {
    utils.renderSquareControl.call({ sizeX: 9, sizeY: 26 }, ctx, left, top, styleOverride, fabricObject);
  };
  const renderPillMidH = function renderPillMidH(ctx, left, top, styleOverride, fabricObject) {
    utils.renderSquareControl.call({ sizeX: 26, sizeY: 9 }, ctx, left, top, styleOverride, fabricObject);
  };
  const applyPillRenders = (ctrls) => {
    if (!ctrls) return;
    if (ctrls.ml) ctrls.ml.render = renderPillMidV;
    if (ctrls.mr) ctrls.mr.render = renderPillMidV;
    if (ctrls.mt) ctrls.mt.render = renderPillMidH;
    if (ctrls.mb) ctrls.mb.render = renderPillMidH;
  };
  applyPillRenders(fabric.Object.prototype.controls);
  if (fabric.Textbox?.prototype?.controls) {
    applyPillRenders(fabric.Textbox.prototype.controls);
  }
}

/** Fabric Textbox: enable all handles + corner proportional scale (width and height). */
function installTextboxFourSideResize(fabric) {
  if (!fabric.Textbox?.prototype || !fabric.Object?.prototype?.controls) return;
  const proto = fabric.Textbox.prototype;
  const utils = fabric.controlsUtils;
  const Control = fabric.Control;
  proto.lockScalingY = false;
  proto.lockScalingX = false;
  proto.lockUniScaling = false;
  proto.noScaleCache = true;
  if ('dynamicMinHeight' in proto) proto.dynamicMinHeight = false;
  if ('dynamicMinWidth' in proto) proto.dynamicMinWidth = false;

  const objCtrls = fabric.Object.prototype.controls;
  const scaleEqually = utils?.scalingEqually || utils?.scaleObjectFromCorner;
  const scaleX = utils?.scalingX;
  const scaleY = utils?.scalingY;

  const withHandler = (key, handler) => {
    const base = objCtrls[key];
    if (!base || !Control || !handler) return base;
    return new Control({
      x: base.x,
      y: base.y,
      offsetX: base.offsetX,
      offsetY: base.offsetY,
      cursorStyle: base.cursorStyle,
      cursorStyleHandler: base.cursorStyleHandler,
      actionHandler: handler,
      actionName: base.actionName,
      render: base.render,
    });
  };

  proto.controls = {
    ...(proto.controls || {}),
    tl: withHandler('tl', scaleEqually) || objCtrls.tl,
    tr: withHandler('tr', scaleEqually) || objCtrls.tr,
    bl: withHandler('bl', scaleEqually) || objCtrls.bl,
    br: withHandler('br', scaleEqually) || objCtrls.br,
    mt: withHandler('mt', scaleY) || objCtrls.mt,
    mb: withHandler('mb', scaleY) || objCtrls.mb,
    ml: withHandler('ml', scaleX) || objCtrls.ml,
    mr: withHandler('mr', scaleX) || objCtrls.mr,
    mtr: objCtrls.mtr,
  };
}

function fabricTypeIsTextOrImage(type) {
  return type === 'i-text' || type === 'textbox' || type === 'text' || type === 'image';
}

function resetFabricControlVisibility(obj) {
  if (!obj || obj.type === 'activeSelection' || typeof obj.setControlVisible !== 'function') return;
  ALL_FABRIC_CONTROL_KEYS.forEach((k) => {
    try {
      obj.setControlVisible(k, true);
    } catch (_) {}
  });
}

/** Selection chrome only — must not overwrite text fill, fontSize, or text content. */
function applyFabricObjectSelectionChrome(obj, style) {
  if (!obj || !style) return;
  obj.set({
    borderColor: style.borderColor,
    cornerColor: style.cornerColor,
    cornerStrokeColor: style.cornerStrokeColor,
    cornerStyle: style.cornerStyle,
    cornerSize: style.cornerSize,
    transparentCorners: style.transparentCorners,
    borderScaleFactor: style.borderScaleFactor,
  });
}

// Convert mm to px at given dpi
function mmToPx(mm, dpi) {
  return Math.round((mm * dpi) / 25.4);
}
function pxToMm(px, dpi) {
  return (Number(px) * 25.4) / dpi;
}

/** Prevent runaway layout / scroll when API state or Fabric bbox is NaN or extreme. */
const MAX_TEMPLATE_PX = 12000;
const MAX_OVERLAY_BBOX_PX = 24000;
const ZOOM_LEVELS = [20, 35, 50, 75, 100, 125, 150, 200];

function snapZoomToLevel(zoom) {
  const z = clampFiniteNumber(
    zoom,
    ZOOM_LEVELS[0],
    ZOOM_LEVELS[ZOOM_LEVELS.length - 1],
    100,
  );
  let best = ZOOM_LEVELS[0];
  let bestDist = Math.abs(z - best);
  for (const level of ZOOM_LEVELS) {
    const dist = Math.abs(z - level);
    if (dist < bestDist) {
      best = level;
      bestDist = dist;
    }
  }
  return best;
}

function stepZoomLevel(current, delta) {
  const snapped = snapZoomToLevel(current);
  const idx = ZOOM_LEVELS.indexOf(snapped);
  const nextIdx = Math.min(ZOOM_LEVELS.length - 1, Math.max(0, idx + delta));
  return ZOOM_LEVELS[nextIdx];
}

function clampFiniteNumber(n, min, max, fallback) {
  const x = Number(n);
  if (!Number.isFinite(x)) return fallback;
  return Math.min(max, Math.max(min, x));
}

function clampTemplatePx(px) {
  return Math.round(clampFiniteNumber(px, 1, MAX_TEMPLATE_PX, 650));
}

function documentSizeMin(unitName) {
  if (unitName === 'Inch') return 1;
  if (unitName === 'Millimeter') return 25;
  return 100;
}

function documentSizeStep(unitName) {
  if (unitName === 'Inch') return 0.25;
  if (unitName === 'Millimeter') return 5;
  return 50;
}

/** Resize page content when horizontal/vertical document size changes; text reflows inside its box. */
function scaleTopLevelObjectsForPageResize(canvas, oldW, oldH, newW, newH) {
  if (!canvas?.getObjects || oldW < 1 || oldH < 1 || newW < 1 || newH < 1) return;
  const scaleX = newW / oldW;
  const scaleY = newH / oldH;
  if (!Number.isFinite(scaleX) || !Number.isFinite(scaleY)) return;

  const reflowText = (obj) => {
    if (obj.type === 'textbox') {
      const sx = Math.abs(obj.scaleX || 1);
      const sy = Math.abs(obj.scaleY || 1);
      obj.set({
        left: (obj.left || 0) * scaleX,
        top: (obj.top || 0) * scaleY,
        scaleX: 1,
        scaleY: 1,
      });
      const cfg = { ...(obj.fieldConfig || {}), ...snapshotTextFieldConfig(obj) };
      cfg.maxWidth = Math.max(24, (obj.width || 100) * sx * scaleX);
      cfg.maxHeight = Math.max(16, (obj.height || 40) * sy * scaleY);
      applyTextContainInBox(canvas, obj, { config: cfg });
      return;
    }
    const sx = Math.abs(obj.scaleX || 1);
    const sy = Math.abs(obj.scaleY || 1);
    let boxW;
    let boxH;
    obj.set({
      left: (obj.left || 0) * scaleX,
      top: (obj.top || 0) * scaleY,
      scaleX: sx * scaleX,
      scaleY: sy * scaleY,
    });
    obj.setCoords?.();
    const br = obj.getBoundingRect(true);
    boxW = Math.max(24, br.width);
    boxH = Math.max(16, br.height);
    const cfg = { ...(obj.fieldConfig || {}), ...snapshotTextFieldConfig(obj) };
    cfg.maxWidth = boxW;
    cfg.maxHeight = boxH;
    obj.set({
      left: (obj.left || 0) * scaleX,
      top: (obj.top || 0) * scaleY,
      scaleX: 1,
      scaleY: 1,
      fieldConfig: cfg,
    });
    applyTextLayout(canvas, obj, {
      text: obj.text || '',
      config: { ...cfg, behavior: 'wrap', baseFontSize: obj.fontSize || cfg.baseFontSize || 12 },
    });
    obj.setCoords?.();
  };

  canvas.getObjects().forEach((obj) => {
    if (obj.type === 'i-text' || obj.type === 'textbox' || obj.type === 'text') {
      reflowText(obj);
      return;
    }
    if (isTextBoxFrame(obj)) {
      obj.set({
        left: (obj.left || 0) * scaleX,
        top: (obj.top || 0) * scaleY,
        scaleX: (obj.scaleX || 1) * scaleX,
        scaleY: (obj.scaleY || 1) * scaleY,
      });
      obj.setCoords?.();
      const label = resolveEditorTextBoxLabel(obj);
      if (label) syncTextBoxPairGeometry(obj, label);
      commitTextBoxFrameResize(canvas, obj);
      return;
    }
    if (obj.type === 'group' && isTextBoxGroup(obj)) {
      obj.set({
        left: (obj.left || 0) * scaleX,
        top: (obj.top || 0) * scaleY,
        scaleX: (obj.scaleX || 1) * scaleX,
        scaleY: (obj.scaleY || 1) * scaleY,
      });
      obj.setCoords?.();
      commitTextBoxFrameResize(canvas, obj);
      return;
    }
    if (obj.type === 'group' && isShapeLabelGroup(obj)) {
      obj.set({
        left: (obj.left || 0) * scaleX,
        top: (obj.top || 0) * scaleY,
        scaleX: (obj.scaleX || 1) * scaleX,
        scaleY: (obj.scaleY || 1) * scaleY,
      });
      obj.setCoords?.();
      fitShapeLabelInGroup(obj, canvas);
      return;
    }
    obj.set({
      left: (obj.left || 0) * scaleX,
      top: (obj.top || 0) * scaleY,
      scaleX: (obj.scaleX || 1) * scaleX,
      scaleY: (obj.scaleY || 1) * scaleY,
    });
    obj.setCoords?.();
  });
  canvas.renderAll?.();
}

function forEachFabricTransformTarget(target, fn) {
  if (!target) return;
  if (target.type === 'activeSelection' && typeof target.getObjects === 'function') {
    target.getObjects().forEach(fn);
    return;
  }
  fn(target);
}

function reflowAllTextboxesOnCanvas(canvas) {
  if (!canvas) return;
  forEachCanvasObjectDeep(canvas, (o) => {
    if (o.type === 'textbox' || o.type === 'i-text' || o.type === 'text') {
      ensureTextObjectVisible(o);
    }
  });
  canvas.renderAll?.();
}

/** Returns safe rect or null (caller should skip overlay / clear selection chrome). */
function sanitizeBoundingRect(rect, canvasW, canvasH) {
  if (!rect) return null;
  const left = Number(rect.left);
  const top = Number(rect.top);
  const width = Number(rect.width);
  const height = Number(rect.height);
  if (![left, top, width, height].every((v) => Number.isFinite(v))) return null;
  if (width <= 0 || height <= 0) return null;
  const capW =
    Number.isFinite(canvasW) && canvasW > 0 ? Math.min(MAX_OVERLAY_BBOX_PX, canvasW * 2.5) : MAX_OVERLAY_BBOX_PX;
  const capH =
    Number.isFinite(canvasH) && canvasH > 0 ? Math.min(MAX_OVERLAY_BBOX_PX, canvasH * 2.5) : MAX_OVERLAY_BBOX_PX;
  if (width > capW || height > capH) return null;
  if (Math.abs(left) > capW * 2 || Math.abs(top) > capH * 2) return null;
  return { left, top, width, height };
}

const PRESETS = [
  // US paper — dimensions in inches (stored in width/height state when unit is Inch)
  { name: 'Portrait (8.5×11 in)', widthIn: 8.5, heightIn: 11 },
  { name: 'Landscape (11×8.5 in)', widthIn: 11, heightIn: 8.5 },
  { name: 'Poster (11×17 in)', widthIn: 11, heightIn: 17 },
  { name: 'Custom size' },
];

// Built-in canvas templates (starter layouts). Empty — use saved templates from API only.
function getTemplates() {
  return [];
}

function templateStatusOf(tpl) {
  return tpl?.status === 'active' ? 'active' : 'inactive';
}

function templateMatchesStatusFilter(tpl, filter) {
  if (!filter || filter === 'all') return true;
  return templateStatusOf(tpl) === filter;
}

export default function App() {
  const canvasRef = useRef(null);
  const fabricRef = useRef(null);
  const bgInputRef = useRef(null);
  const uploadInputRef = useRef(null);
  const isRestoringHistoryRef = useRef(false);
  const suppressKonvaHistoryRef = useRef(false);
  const konvaHistoryBaselineRef = useRef(0);
  /** Skip per-item history snapshots when removing several objects in one user action (e.g. multiselect delete). */
  const suppressObjectRemovedHistoryRef = useRef(false);
  const pushHistoryCanvasRef = useRef(() => {});
  const pendingTemplateIdRef = useRef(null);
  const pendingSavedTemplateJsonRef = useRef(null);
  /** While true, template dimension effect must not run the resize/scale branch (would race async loadFromJSON). */
  const isFabricTemplateJsonLoadingRef = useRef(false);
  /** Bumped when applying saved JSON; stale loadFromJSON completion callbacks no-op. */
  const fabricTemplateJsonApplyGenRef = useRef(0);
  const konvaStageRef = useRef(null);
  const prePreviewKonvaSceneRef = useRef(null);

  // Document (right panel): stored in mm for display when unit is mm
  const [widthMm, setWidthMm] = useState(8.5);
  const [heightMm, setHeightMm] = useState(11);
  const [unit, setUnit] = useState('Inch');
  /** Fixed export / mm↔px DPI (no UI control). */
  const resolution = 300;
  const [bleedMm, setBleedMm] = useState(3);
  const [clipContent, setClipContent] = useState(false);
  const [presetName, setPresetName] = useState('Portrait (8.5×11 in)');

  // Canvas pixel dimensions (derived from mm + dpi or preset); clamp so Math.max(1, NaN) never yields NaN.
  const widthPxRaw =
    unit === 'Millimeter' ? mmToPx(widthMm, resolution) : unit === 'Inch' ? widthMm * resolution : widthMm;
  const heightPxRaw =
    unit === 'Millimeter' ? mmToPx(heightMm, resolution) : unit === 'Inch' ? heightMm * resolution : heightMm;
  const template = { width: clampTemplatePx(widthPxRaw), height: clampTemplatePx(heightPxRaw) };

  const [selectedObj, setSelectedObj] = useState(null);
  const [textProps, setTextProps] = useState({
    text: '',
    fontSize: 56,
    fontFamily: 'Arial',
    fill: '#111827',
    fontWeight: 'normal',
    fontStyle: 'normal',
    underline: false,
    textAlign: 'left',
  });
  const [dynamicKey, setDynamicKey] = useState('');
  const [placeholderDropdownOpen, setPlaceholderDropdownOpen] = useState(false);
  const lastCanvasSelectionStyledRef = useRef(null);
  const [clipboard, setClipboard] = useState(null);
  const [selectionBounds, setSelectionBounds] = useState(null);
  const canvasWrapperRef = useRef(null);
  const mainCanvasAreaRef = useRef(null);
  const updateSelectionBoundsRef = useRef(null);
  const applyCanvasSelectionHighlightRef = useRef(() => {});

  useEffect(() => {
    setPlaceholderDropdownOpen(false);
  }, [selectedObj]);

  const [layersKey, setLayersKey] = useState(0);
  const [bgType, setBgType] = useState('solid');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [bgGradientFrom, setBgGradientFrom] = useState('#6366f1');
  const [bgGradientTo, setBgGradientTo] = useState('#a855f7');
  const [bgImageUrl, setBgImageUrl] = useState(null);
  const [templateViewMode, setTemplateViewMode] = useState('grid');
  /** 'all' | 'active' | 'inactive' */
  const [templateStatusFilter, setTemplateStatusFilter] = useState('all');
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [templatesLoadError, setTemplatesLoadError] = useState(null);
  const templatesFetchGenRef = useRef(0);
  const templatesListLoadedAtRef = useRef(0);
  const templatesFetchInFlightRef = useRef(false);
  /** Keys for templates whose `preview_image` failed in the browser (invalid/truncated data URL). */
  const [brokenTemplatePreviewKeys, setBrokenTemplatePreviewKeys] = useState({});
  const [globalApiBusy, setGlobalApiBusy] = useState(false);

  useEffect(() => subscribeGlobalApiLoading(setGlobalApiBusy), []);
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [isUnsavedDraft, setIsUnsavedDraft] = useState(true);

  // Refresh GCP image URLs whenever a template is loaded. Converts any stale signed GCS URLs
  // in the scene to stream proxy URLs (which are always fresh via server-side auth).
  // This keeps images visible in the editor even when a template's saved signed URLs have expired.
  useEffect(() => {
    if (!USE_KONVA_EDITOR) return;
    const id = setTimeout(() => {
      const scene = useEditorStore.getState().scene;
      if (!scene?.objects?.length) return;
      const normalized = normalizeKonvaSceneImages(scene);
      if (normalized !== scene) useEditorStore.getState().setScene(normalized);
    }, 300);
    return () => clearTimeout(id);
  }, [currentTemplateId]);
  const [applyTemplateKey, setApplyTemplateKey] = useState(0);
  const konvaTemplateLoadedKeyRef = useRef(-1);
  const [templateBarName, setTemplateBarName] = useState(UNSAVED_TEMPLATE_LABEL);
  const [templateBarError, setTemplateBarError] = useState(null);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateModalMode, setTemplateModalMode] = useState(null);
  const [templateModalName, setTemplateModalName] = useState('');
  const [templateModalBusy, setTemplateModalBusy] = useState(false);
  const [templateModalError, setTemplateModalError] = useState(null);
  /** Unsaved draft: whether the next save should create the template as active (multiple templates may be active). */
  const [draftStatusActive, setDraftStatusActive] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [showCanvasGrid, setShowCanvasGrid] = useState(false);
  const autoSaveTimerRef = useRef(null);
  const [templateStatusBusy, setTemplateStatusBusy] = useState(false);
  /** { draft: boolean, next: 'active'|'inactive' } | null */
  const [statusConfirm, setStatusConfirm] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [templateMenuOpenId, setTemplateMenuOpenId] = useState(null);
  const [renamingTemplateId, setRenamingTemplateId] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const renameInputRef = useRef(null);
  const [downloadError, setDownloadError] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const gcpUploadInputRef = useRef(null);
  const libraryScrollRef = useRef(null);
  const [gcpLibrary, setGcpLibrary] = useState({ items: [], count: 0, max: GCP_IMAGES_MAX });
  const [gcpLibraryLoading, setGcpLibraryLoading] = useState(false);
  const [gcpLibraryError, setGcpLibraryError] = useState(null);
  const [gcpLimitModalOpen, setGcpLimitModalOpen] = useState(false);
  const [gcpImageDeleteConfirm, setGcpImageDeleteConfirm] = useState(null);
  const [gcpDeleteBusy, setGcpDeleteBusy] = useState(false);
  const GOOGLE_FONTS = EDITOR_FONTS;

  const [mappingsJson, setMappingsJson] = useState(
    JSON.stringify(
      {
        flyerHeader: 'Summer Fundraiser 2025',
        eventLocation: 'Downtown Hall',
        manufacturerLogoOneSignedURL: 'https://picsum.photos/200',
      },
      null,
      2
    )
  );

  /** Designer placeholder options for text/image objects (`key` stored on canvas as `fieldKey`). */
  const [textPlaceholders, setTextPlaceholders] = useState(() => [...INITIAL_PLACEHOLDERS.text]);
  const [imagePlaceholders, setImagePlaceholders] = useState(() => [...INITIAL_PLACEHOLDERS.image]);

  useEffect(() => {
    apiFetch(STATIC_VALUES_URL, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((rows) => {
        if (!Array.isArray(rows)) return;
        const text = [];
        const image = [];
        rows.forEach((row) => {
          if (!row || !row.dynamicField) return;
          const item = { label: row.frontendValue || row.dynamicField, key: row.dynamicField };
          const typ = String(row.type || '').toLowerCase();
          if (typ === 'text') text.push(item);
          else if (typ === 'image') image.push(item);
        });
        if (text.length > 0 || image.length > 0) {
          setTextPlaceholders(text);
          setImagePlaceholders(image);
        }
      })
      .catch(() => {});
  }, []);

  // Top bar
  const [zoom, setZoom] = useState(100);
  const [preview, setPreview] = useState(false);
  const previewRef = useRef(false);
  previewRef.current = preview;
  const showCanvasGridRef = useRef(showCanvasGrid);
  showCanvasGridRef.current = showCanvasGrid;
  const previewObjectSnapRef = useRef(null);
  const prePreviewCanvasJsonRef = useRef(null);

  useEffect(() => {
    if (Number.isFinite(zoom) && ZOOM_LEVELS.includes(zoom)) return;
    setZoom(snapZoomToLevel(zoom));
  }, [zoom]);

  useEffect(() => {
    const f = fabricRef.current;
    if (!f) return;
    if (preview) {
      const snap = f.getObjects().map((o) => ({
        o,
        selectable: o.selectable,
        evented: o.evented,
      }));
      previewObjectSnapRef.current = snap;
      forEachCanvasObjectDeep(f, (obj) => {
        try {
          if (typeof obj.exitEditing === 'function') obj.exitEditing();
        } catch (_) {}
        obj.set({ selectable: false, evented: false });
      });
      f.discardActiveObject();
      f.selection = false;
      f.requestRenderAll();
      setSelectedObj(null);
      setSelectionBounds(null);
    } else {
      const snap = previewObjectSnapRef.current;
      if (snap) {
        snap.forEach(({ o, selectable, evented }) => {
          if (o.canvas) o.set({ selectable, evented });
        });
        previewObjectSnapRef.current = null;
        f.selection = true;
        f.requestRenderAll();
      }
    }
  }, [preview]);

  useEffect(() => {
    if (USE_KONVA_EDITOR) {
      if (preview) {
        prePreviewKonvaSceneRef.current = useEditorStore.getState().getSerializedScene();
        const scene = cloneScene(prePreviewKonvaSceneRef.current);
        const previewMappings = buildPreviewMappings(getMappings(), scene.objects);
        applyMappingsToSceneGraph(scene, previewMappings, lookupMappingValue);
        useEditorStore.getState().setScene(scene);
        setLayersKey((k) => k + 1);
      } else if (prePreviewKonvaSceneRef.current) {
        useEditorStore.getState().setScene(prePreviewKonvaSceneRef.current);
        prePreviewKonvaSceneRef.current = null;
        setLayersKey((k) => k + 1);
      }
      return;
    }
    const f = fabricRef.current;
    if (!f) return;
    if (preview) {
      try {
        prePreviewCanvasJsonRef.current = f.toJSON(['fieldKey', 'fieldConfig', '_shapeLabelGroup', TEXT_BOX_GROUP_FLAG, TEXT_BOX_PAIR_ID, TEXT_BOX_FRAME_FLAG, TEXT_BOX_LABEL_FLAG, '_boxWidth', '_boxHeight']);
      } catch (_) {
        prePreviewCanvasJsonRef.current = null;
      }
      // Collect field keys from canvas objects for sample data fallback
      const canvasObjects = [];
      try { forEachCanvasObjectDeep(f, (o) => canvasObjects.push(o)); } catch (_) {}
      const fabricSceneNodes = canvasObjects
        .filter((o) => o.fieldKey)
        .map((o) => ({ fieldKey: o.fieldKey, type: o.type === 'image' ? 'image' : 'text' }));
      const previewMappings = buildPreviewMappings(getMappings(), fabricSceneNodes);
      applyMappingsToCanvas(previewMappings);
      return;
    }
    if (!prePreviewCanvasJsonRef.current) return;
    const snap = prePreviewCanvasJsonRef.current;
    prePreviewCanvasJsonRef.current = null;
    const applyGen = ++fabricTemplateJsonApplyGenRef.current;
    isRestoringHistoryRef.current = true;
    f.loadFromJSON(
      snap,
      () => {
        if (fabricTemplateJsonApplyGenRef.current !== applyGen) return;
        isRestoringHistoryRef.current = false;
        try {
          forEachCanvasObjectDeep(f, migrateShapeGroupFieldKeyToLabel);
          finalizeCanvasTextBoxes(f);
        } catch (_) {}
        f.renderAll();
        setLayersKey((k) => k + 1);
      },
      fabricCustomPropsReviver,
    );
  }, [preview]);

  const [unselectedPlaceholderOverlays, setUnselectedPlaceholderOverlays] = useState([]);
  const lastPhOverlaySnapRef = useRef('');
  const syncUnselectedPlaceholderOverlaysRef = useRef(() => {});
  const [historyState, setHistoryState] = useState({ history: [], index: -1 });
  const konvaSceneObjects = useEditorStore((s) => s.scene.objects);
  const konvaSelectedIds = useEditorStore((s) => s.selectedIds);
  const HISTORY_LIMIT = 50;

  useEffect(() => {
    if (!USE_KONVA_EDITOR) return;
    updateSelectionBoundsRef.current?.();
  }, [konvaSelectedIds, konvaSceneObjects]);

  /** While in the maker, cache fonts locally (IndexedDB) for canvas + PDF export. */
  useEffect(() => {
    if (!USE_KONVA_EDITOR) return undefined;
    const timer = setTimeout(() => {
      warmSceneFontsLocally(konvaSceneObjects, konvaStageRef.current);
    }, 400);
    return () => clearTimeout(timer);
  }, [konvaSceneObjects]);
  const history = historyState.history;
  const historyIndex = historyState.index;

  // Pages
  const [pages, setPages] = useState([null]); // null = empty page state to be filled
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const pagesRef = useRef(pages);
  const currentPageIndexRef = useRef(currentPageIndex);
  useEffect(() => {
    pagesRef.current = pages;
    currentPageIndexRef.current = currentPageIndex;
  }, [pages, currentPageIndex]);

  // Left sidebar active tool (for highlight)
  const [activeTool, setActiveTool] = useState(null);
  const [librarySearch, setLibrarySearch] = useState('');
  const [shapeFillColor, setShapeFillColor] = useState('#e2e8f0');
  const [shapeStrokeColor, setShapeStrokeColor] = useState('#94a3b8');

  const SHAPE_COLORS = [
    { fill: '#e2e8f0', stroke: '#94a3b8' },
    { fill: '#fecaca', stroke: '#dc2626' },
    { fill: '#bbf7d0', stroke: '#16a34a' },
    { fill: '#bfdbfe', stroke: '#2563eb' },
    { fill: '#fde68a', stroke: '#ca8a04' },
    { fill: '#e9d5ff', stroke: '#9333ea' },
    { fill: '#fbcfe8', stroke: '#db2777' },
    { fill: '#fcd34d', stroke: '#b45309' },
    { fill: '#a5f3fc', stroke: '#0891b2' },
    { fill: '#f97316', stroke: '#c2410c' },
    { fill: '#ffffff', stroke: '#64748b' },
    { fill: '#1e293b', stroke: '#475569' },
  ];

  const handleKonvaSelection = useCallback((node) => {
    if (!node) {
      setSelectedObj(null);
      setSelectionBounds(null);
      setTextProps({
        text: '',
        fontSize: 56,
        fontFamily: 'Arial',
        fill: '#111827',
        fontWeight: 'normal',
        fontStyle: 'normal',
        underline: false,
        textAlign: 'left',
      });
      setDynamicKey('');
      return;
    }
    if (node.type === 'shape') {
      setSelectedObj({
        type: node.fabricType || 'rect',
        id: node.id,
        fill: node.fill,
        stroke: node.stroke,
        strokeWidth: node.strokeWidth,
        _konvaEditor: true,
      });
      if (node.fill != null) setShapeFillColor(node.fill);
      if (node.stroke != null) setShapeStrokeColor(node.stroke);
      setDynamicKey('');
      requestAnimationFrame(() => updateSelectionBoundsRef.current?.());
      return;
    }
    if (node.type === 'image') {
      setSelectedObj({
        type: 'image',
        id: node.id,
        fieldKey: node.fieldKey,
        lockAspectRatio: node.lockAspectRatio !== false,
        _konvaEditor: true,
      });
      setDynamicKey(node.fieldKey || '');
      requestAnimationFrame(() => updateSelectionBoundsRef.current?.());
      return;
    }
    const ty = node.typography || {};
    setSelectedObj({ type: 'text', id: node.id, fieldKey: node.fieldKey, _konvaEditor: true });
    setTextProps({
      text: node.text ?? '',
      fontSize: node.layout?.fontSize || ty.fontSize || 12,
      fontFamily: ty.fontFamily || 'Arial',
      fill: ty.fill || '#111827',
      fontWeight: ty.fontWeight || 'normal',
      fontStyle: ty.fontStyle || 'normal',
      underline: ty.underline || false,
      textAlign: ty.textAlign || 'left',
    });
    setDynamicKey(node.fieldKey || '');
    requestAnimationFrame(() => updateSelectionBoundsRef.current?.());
  }, []);

  useEffect(() => {
    if (USE_KONVA_EDITOR) return undefined;
    const { fabric } = require('fabric');
    const customs = fabric.Object.customProperties || [];
    if (!customs.includes('fieldKey')) customs.push('fieldKey');
    if (!customs.includes('fieldConfig')) customs.push('fieldConfig');
    if (!customs.includes('_shapeLabelGroup')) customs.push('_shapeLabelGroup');
    if (!customs.includes(TEXT_BOX_GROUP_FLAG)) customs.push(TEXT_BOX_GROUP_FLAG);
    if (!customs.includes(TEXT_BOX_PAIR_ID)) customs.push(TEXT_BOX_PAIR_ID);
    if (!customs.includes(TEXT_BOX_FRAME_FLAG)) customs.push(TEXT_BOX_FRAME_FLAG);
    if (!customs.includes(TEXT_BOX_LABEL_FLAG)) customs.push(TEXT_BOX_LABEL_FLAG);
    if (!customs.includes('_boxWidth')) customs.push('_boxWidth');
    if (!customs.includes('_boxHeight')) customs.push('_boxHeight');
    fabric.Object.customProperties = customs;
    installFabricMidHandlePills(fabric);
    installTextboxFourSideResize(fabric);
    if (fabric.Image?.prototype) {
      fabric.Image.prototype.lockUniScaling = true;
    }
    fabric.Object.prototype.set({
      borderColor: '#000000',
      cornerColor: '#000000',
      cornerStrokeColor: '#000000',
      transparentCorners: false,
      cornerStyle: 'rect',
      cornerSize: 13,
    });
    const c = new fabric.Canvas(canvasRef.current, {
      width: template.width,
      height: template.height,
      backgroundColor: '#ffffff',
      preserveObjectStacking: true,
      selection: true,
      selectionColor: 'rgba(42,49,139,0.08)',
      selectionBorderColor: '#2a318b',
    });
    fabricRef.current = c;

    const schedulePhOverlaySync = () => {
      requestAnimationFrame(() => {
        try {
          syncUnselectedPlaceholderOverlaysRef.current?.();
        } catch (_) {}
      });
    };

    const drawGridOverObjects = () => {
      if (!showCanvasGridRef.current || previewRef.current) return;
      const ctx = c.getContext();
      if (!ctx) return;
      const w = c.getWidth();
      const h = c.getHeight();
      const step = 20;
      ctx.save();
      ctx.strokeStyle = 'rgba(148, 163, 184, 0.55)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let x = step; x < w; x += step) {
        ctx.moveTo(x + 0.5, 0);
        ctx.lineTo(x + 0.5, h);
      }
      for (let y = step; y < h; y += step) {
        ctx.moveTo(0, y + 0.5);
        ctx.lineTo(w, y + 0.5);
      }
      ctx.stroke();
      ctx.restore();
    };

    const onAfterRender = () => {
      drawGridOverObjects();
      schedulePhOverlaySync();
    };
    c.on('after:render', onAfterRender);

    c.on('selection:created', onObjectSelected);
    c.on('selection:updated', onObjectSelected);
    c.on('selection:cleared', () => {
      const prev = lastCanvasSelectionStyledRef.current;
      if (prev) {
        resetCanvasSelectionHighlight(prev);
        lastCanvasSelectionStyledRef.current = null;
        fabricRef.current?.renderAll();
      }
      setSelectedObj(null);
      setSelectionBounds(null);
    });

    const pushHistory = () => {
      if (isRestoringHistoryRef.current) return;
      try {
        const json = c.toJSON(['fieldKey', 'fieldConfig', '_shapeLabelGroup', TEXT_BOX_GROUP_FLAG, TEXT_BOX_PAIR_ID, TEXT_BOX_FRAME_FLAG, TEXT_BOX_LABEL_FLAG, '_boxWidth', '_boxHeight']);
        setHistoryState((prev) => {
          const next = prev.history.slice(0, prev.index + 1);
          next.push(json);
          if (next.length > HISTORY_LIMIT) next.shift();
          return { history: next, index: next.length - 1 };
        });
      } catch (_) {}
    };
    pushHistoryCanvasRef.current = pushHistory;
    function normalizeTextGeometryAfterTransform(target) {
      if (!target || target.type === 'activeSelection' || isRestoringHistoryRef.current) return;
      const resizeTarget = getEditorTextBoxResizeTarget(target);
      if (resizeTarget && isTextBoxFrame(resizeTarget)) {
        clearTextBoxFrameResizeSession(resizeTarget);
        commitTextBoxFrameResize(c, resizeTarget);
        return;
      }
      if (resizeTarget && isTextBoxGroup(resizeTarget)) {
        commitTextBoxFrameResize(c, resizeTarget);
        return;
      }
      if (target.type === 'textbox') {
        absorbTextScaleIntoBox(c, target);
        ensureTextObjectVisible(target);
        target.__lastSyncedTextboxWidth = target.width;
      } else if (target.type === 'i-text' || target.type === 'text') {
        absorbTextScaleIntoBox(c, target);
        ensureTextObjectVisible(target);
      }
    }

    function syncTextPropsFromFabricTarget(target) {
      if (!target || target.type === 'activeSelection') return;
      const innerLabel = getInnerShapeLabelText(target);
      const textObj =
        resolveEditorTextBoxLabel(target) ||
        (target.type === 'i-text' || target.type === 'textbox' || target.type === 'text' ? target : innerLabel);
      if (!textObj) return;
      setTextProps({
        text: textObj.text,
        fontSize: textObj.fontSize || 56,
        fontFamily: textObj.fontFamily || 'Arial',
        fill: textObj.fill || '#111827',
        fontWeight: textObj.fontWeight || 'normal',
        fontStyle: textObj.fontStyle || 'normal',
        underline: textObj.underline || false,
        textAlign: textObj.textAlign || 'left',
      });
    }

    c.on('before:transform', (e) => {
      const t = e.transform?.target;
      if (!t || isRestoringHistoryRef.current) return;
      const corner = e.transform?.corner;
      forEachFabricTransformTarget(t, (obj) => {
        const resizeTarget = getEditorTextBoxResizeTarget(obj);
        if (resizeTarget && isTextBoxFrame(resizeTarget)) {
          resizeTarget.__corner = corner;
          beginTextBoxFrameResizeSession(resizeTarget);
        }
      });
    });

    c.on('object:added', () => { pushHistory(); setLayersKey((k) => k + 1); });
    c.on('object:moving', (e) => {
      const t = e.target;
      if (!t || isRestoringHistoryRef.current) return;
      if (isTextBoxFrame(t)) {
        const label = resolveEditorTextBoxLabel(t);
        if (label) syncTextBoxPairGeometry(t, label);
      }
    });
    c.on('object:rotating', (e) => {
      const t = e.target;
      if (!t || isRestoringHistoryRef.current) return;
      if (isTextBoxFrame(t)) {
        const label = resolveEditorTextBoxLabel(t);
        if (label) syncTextBoxPairGeometry(t, label);
      }
    });
    c.on('object:scaling', (e) => {
      const t = e.target;
      if (!t || isRestoringHistoryRef.current) return;
      const corner = e.transform?.corner;
      forEachFabricTransformTarget(t, (obj) => {
        const resizeTarget = getEditorTextBoxResizeTarget(obj);
        if (resizeTarget && isTextBoxFrame(resizeTarget)) {
          resizeTarget.__corner = corner;
          resizeTextBoxFrameFromTransform(c, resizeTarget, { live: true, corner });
        } else if (obj.type === 'textbox') {
          absorbTextScaleIntoBox(c, obj, { live: true });
          obj.__lastSyncedTextboxWidth = obj.width;
        } else if (obj.type === 'i-text' || obj.type === 'text') {
          absorbTextScaleIntoBox(c, obj, { live: true });
        } else if (obj.type === 'group' && isShapeLabelGroup(obj)) {
          const label = getInnerShapeLabelText(obj);
          if (label) absorbTextScaleIntoBox(c, label, { live: true });
        }
      });
    });

    c.on('object:modified', (e) => {
      const t = e.target;
      if (!t || isRestoringHistoryRef.current) return;
      forEachFabricTransformTarget(t, (obj) => {
        if (obj.type === 'group' && isShapeLabelGroup(obj)) {
          fitShapeLabelInGroup(obj, c);
        }
        if (isTextBoxFrame(obj)) {
          const label = resolveEditorTextBoxLabel(obj);
          if (label) syncTextBoxPairGeometry(obj, label);
          normalizeTextGeometryAfterTransform(obj);
          applyCanvasSelectionHighlightRef.current(obj);
        } else if (isTextBoxGroup(obj)) {
          normalizeTextGeometryAfterTransform(obj);
          applyCanvasSelectionHighlightRef.current(obj);
        } else if (fabricTypeIsTextOrImage(obj.type)) {
          normalizeTextGeometryAfterTransform(obj);
          applyCanvasSelectionHighlightRef.current(obj);
        }
      });
      pushHistory();
      if (e.target === c.getActiveObject()) {
        requestAnimationFrame(() => {
          updateSelectionBoundsRef.current?.();
          syncTextPropsFromFabricTarget(e.target);
          c.requestRenderAll();
        });
      }
    });
    c.on('object:removed', (e) => {
      const obj = e.target;
      if (obj && (isTextBoxFrame(obj) || isTextBoxLabel(obj))) {
        const pairId = getTextBoxPairId(obj);
        const partnerKind = isTextBoxFrame(obj) ? 'label' : 'frame';
        if (pairId) {
          const partner = c
            .getObjects()
            .find(
              (o) =>
                getTextBoxPairId(o) === pairId &&
                (partnerKind === 'frame' ? isTextBoxFrame(o) : isTextBoxLabel(o)),
            );
          if (partner) {
            suppressObjectRemovedHistoryRef.current = true;
            c.remove(partner);
            suppressObjectRemovedHistoryRef.current = false;
          }
        }
      }
      setLayersKey((k) => k + 1);
      if (!suppressObjectRemovedHistoryRef.current) pushHistory();
    });
    setTimeout(() => {
      try {
        finalizeCanvasTextBoxes(c);
        setHistoryState({
          history: [c.toJSON(['fieldKey', 'fieldConfig', '_shapeLabelGroup', TEXT_BOX_GROUP_FLAG, TEXT_BOX_PAIR_ID, TEXT_BOX_FRAME_FLAG, TEXT_BOX_LABEL_FLAG, '_boxWidth', '_boxHeight'])],
          index: 0,
        });
      } catch (_) {}
    }, 0);

    function embedLabelInShape(shape) {
      if (!shape || previewRef.current) return;
      const f = fabricRef.current;
      if (!f) return;
      const left = shape.left;
      const top = shape.top;
      const scaleX = shape.scaleX || 1;
      const scaleY = shape.scaleY || 1;
      const angle = shape.angle || 0;
      const skewX = shape.skewX || 0;
      const skewY = shape.skewY || 0;
      const opacity = shape.opacity;
      const fk = shape.fieldKey;
      const fc = shape.fieldConfig;
      shape.clone((clonedShape) => {
        f.remove(shape);
        clonedShape.set({
          originX: 'center',
          originY: 'center',
          left: 0,
          top: 0,
          scaleX: 1,
          scaleY: 1,
          angle: 0,
          skewX: 0,
          skewY: 0,
        });
        const sw = clonedShape.getScaledWidth?.() || clonedShape.width || 120;
        const sh = clonedShape.getScaledHeight?.() || clonedShape.height || 80;
        const pad = Math.max(4, Math.min(sw, sh) * 0.1);
        const textWidth = Math.max(24, sw - pad * 2);
        const fs = Math.min(28, Math.max(11, sh * 0.18));
        const label = new fabric.Textbox(SHAPE_LABEL_PLACEHOLDER, {
          originX: 'center',
          originY: 'center',
          left: 0,
          top: 0,
          width: textWidth,
          fontSize: fs,
          fill: '#64748b',
          fontFamily: 'Arial',
          editable: true,
          fontWeight: 'normal',
          textAlign: 'center',
          splitByGrapheme: true,
          _isShapePlaceholder: true,
        });
        if (fk) {
          label.fieldKey = fk;
          if (fc) label.fieldConfig = fc;
        }
        const grp = new fabric.Group([clonedShape, label], {
          subTargetCheck: true,
          originX: 'center',
          originY: 'center',
          left,
          top,
          scaleX,
          scaleY,
          angle,
          skewX,
          skewY,
          _shapeLabelGroup: true,
        });
        if (opacity != null) grp.set({ opacity });
        fitShapeLabelInGroup(grp, f);
        if (label.fieldKey && !label.fieldConfig) {
          label.fieldConfig = snapshotTextFieldConfig(label);
        }
        f.add(grp).setActiveObject(grp);
        enterShapeLabelEditing(f, grp);
        setLayersKey((k) => k + 1);
      });
    }

    c.on('text:editing:entered', (opt) => {
      const target = opt.target;
      if (!target?._isShapePlaceholder || !isShapeLabelPlaceholderText(target.text)) return;
      target.set({ text: '', fill: '#111827' });
      c.requestRenderAll();
    });

    c.on('text:editing:exited', (opt) => {
      const target = opt.target;
      if (!target) return;
      if (target.type === 'textbox' || target.type === 'i-text' || target.type === 'text') {
        ensureTextObjectVisible(target);
      }
      const parent = target.group;
      if (isTextBoxLabel(target) || resolveTextBoxFrame(target)) {
        syncTextBoxGroupAfterTextEdit(c, target);
      }
      if (parent && parent._shapeLabelGroup) fitShapeLabelInGroup(parent, c);
      if (!target._isShapePlaceholder) return;
      if (isShapeLabelPlaceholderText(target.text)) {
        target.set({ text: SHAPE_LABEL_PLACEHOLDER, fill: '#64748b', _isShapePlaceholder: true });
      } else {
        target.set({ fill: target.fill === '#64748b' ? '#111827' : target.fill });
        delete target._isShapePlaceholder;
      }
      c.requestRenderAll();
    });

    c.on('mouse:down', (opt) => {
      if (previewRef.current || isRestoringHistoryRef.current) return;
      const t = opt.target;
      if (!t) return;
      promoteTextBoxFrameSelection(c, t);
    });

    c.on('mouse:dblclick', (opt) => {
      if (previewRef.current) return;
      const t = opt.target;
      if (!t) return;
      if (isTextBoxFrame(t) || isTextBoxLabel(t) || isTextBoxGroup(t)) {
        enterTextBoxGroupEditing(c, t);
        return;
      }
      if (t.type === 'group' && t._shapeLabelGroup) {
        enterShapeLabelEditing(c, t);
        return;
      }
      if (isEmbeddableBareShape(t)) embedLabelInShape(t);
    });

    return () => {
      c.off('after:render', onAfterRender);
      pushHistoryCanvasRef.current = () => {};
      c.dispose();
      fabricRef.current = null;
    };
  }, []);

  useEffect(() => {
    const f = fabricRef.current;
    if (!f) return;
    f.renderAll();
  }, [showCanvasGrid, preview]);

  const loadSavedTemplates = useCallback(async (options = {}) => {
    const force = options.force === true;
    if (templatesFetchInFlightRef.current && !force) return;
    if (!force && templatesListLoadedAtRef.current > 0 && Date.now() - templatesListLoadedAtRef.current < 15000) {
      return;
    }

    const gen = ++templatesFetchGenRef.current;
    templatesFetchInFlightRef.current = true;
    setTemplatesLoadError(null);
    try {
      const r = await apiAxios.get('/api/templates', {
        timeout: 45000,
        skipGlobalLoader: true,
      });
      if (gen !== templatesFetchGenRef.current) return;
      if (r.status < 200 || r.status >= 300) {
        const msg =
          (r.data && typeof r.data === 'object' && r.data.error) ||
          (typeof r.data === 'string' && r.data.trim().startsWith('<')
            ? 'Templates API returned HTML (check proxy / REACT_APP_API_URL).'
            : `Could not load templates (HTTP ${r.status}).`);
        throw new Error(msg);
      }
      if (!Array.isArray(r.data)) {
        throw new Error('Templates API returned an unexpected response.');
      }
      const fromApi = (r.data || []).map((t) => {
        const sid = t._id != null ? String(t._id) : t.id != null ? String(t.id) : '';
        return { ...t, _id: sid || t._id, id: sid || t.id };
      });
      setSavedTemplates(fromApi);
      templatesListLoadedAtRef.current = Date.now();
    } catch (err) {
      if (gen !== templatesFetchGenRef.current) return;
      const canceled =
        axios.isCancel?.(err) ||
        err?.code === 'ERR_CANCELED' ||
        err?.name === 'CanceledError' ||
        String(err?.message || '').toLowerCase().includes('cancel');
      if (canceled) return;
      const msg = err?.message || 'Could not load templates';
      console.error('[templates] load failed:', msg);
      setTemplatesLoadError(msg);
      setToastMessage(msg);
      setSavedTemplates([]);
    } finally {
      if (gen === templatesFetchGenRef.current) {
        templatesFetchInFlightRef.current = false;
      }
    }
  }, []);

  useEffect(() => {
    loadSavedTemplates({ force: true });
  }, [loadSavedTemplates]);

  useEffect(() => {
    if (activeTool === 'templates') loadSavedTemplates({ force: false });
  }, [activeTool, loadSavedTemplates]);

  useEffect(() => {
    if (!fabricRef.current) return;
    const f = fabricRef.current;
    const savedJson = pendingSavedTemplateJsonRef.current;
    if (savedJson) {
      pendingSavedTemplateJsonRef.current = null;
      const applyGen = ++fabricTemplateJsonApplyGenRef.current;
      isFabricTemplateJsonLoadingRef.current = true;
      f.clear();
      f.setBackgroundColor('#ffffff');
      f.setWidth(template.width);
      f.setHeight(template.height);
      try {
        f.loadFromJSON(
          savedJson,
          () => {
            if (fabricTemplateJsonApplyGenRef.current !== applyGen) return;
            isFabricTemplateJsonLoadingRef.current = false;
            if (fabricRef.current !== f) return;
            try {
              forEachCanvasObjectDeep(f, migrateShapeGroupFieldKeyToLabel);
              finalizeCanvasTextBoxes(f);
              reflowAllTextboxesOnCanvas(f);
              f.renderAll();
            } catch (_) {}
          },
          fabricCustomPropsReviver,
        );
      } catch (_) {
        if (fabricTemplateJsonApplyGenRef.current === applyGen) {
          isFabricTemplateJsonLoadingRef.current = false;
        }
      }
      setLayersKey((k) => k + 1);
      return;
    }
    if (isFabricTemplateJsonLoadingRef.current) return;
    const oldW = f.getWidth(),
      oldH = f.getHeight();
    const newW = template.width,
      newH = template.height;
    const scaleX = newW / oldW,
      scaleY = newH / oldH;
    scaleTopLevelObjectsForPageResize(f, oldW, oldH, newW, newH);
    f.setWidth(newW);
    f.setHeight(newH);
    f.renderAll();
    const tid = pendingTemplateIdRef.current;
    if (tid) {
      pendingTemplateIdRef.current = null;
      const templates = getTemplates();
      const t = templates.find((x) => x.id === tid);
      if (t) {
        f.clear();
        f.setBackgroundColor('#ffffff', f.renderAll.bind(f));
        t.build(f);
      }
    }
  }, [template.width, template.height, applyTemplateKey]);

  useEffect(() => {
    if (!USE_KONVA_EDITOR) return undefined;
    pushHistoryCanvasRef.current = pushKonvaHistory;
    const raw = pendingSavedTemplateJsonRef.current;
    if (raw) {
      pendingSavedTemplateJsonRef.current = null;
      const normalized = normalizeFabricJsonInput(raw);
      const { kind, pages: loadedPages } = parseEditorDocumentPages(normalized);
      if (kind === 'konva' && loadedPages.length) {
        setPages([loadedPages[0]]);
        setCurrentPageIndex(0);
        pagesRef.current = [loadedPages[0]];
        currentPageIndexRef.current = 0;

        const firstPage = loadedPages[0];
        const pm = firstPage.pageMeta;
        if (pm) {
          if (pm.unit) setUnit(pm.unit);
          if (pm.widthMm != null) setWidthMm(pm.widthMm);
          if (pm.heightMm != null) setHeightMm(pm.heightMm);
        }
        const sceneW = pm?.width || firstPage.scene?.width || template.width;
        const sceneH = pm?.height || firstPage.scene?.height || template.height;

        suppressKonvaHistoryRef.current = true;
        useEditorStore.getState().setScene(cloneScene({
          ...firstPage.scene,
          width: Math.max(1, Number(sceneW) || 800),
          height: Math.max(1, Number(sceneH) || 600),
        }));
        applyKonvaBackgroundMeta(firstPage.background);
        const firstObjects = firstPage.scene?.objects || [];
        void downloadSceneFontsLocally(firstObjects, konvaStageRef.current).then(() =>
          setLayersKey((k) => k + 1),
        );
        warmSceneFontsLocally(firstObjects, konvaStageRef.current);
      } else if (kind === 'fabric') {
        suppressKonvaHistoryRef.current = true;
        setPages([loadedPages[0] ?? null]);
        setCurrentPageIndex(0);
        const converted = fabricJsonToKonvaScene(loadedPages[0], template.width, template.height);
        useEditorStore.getState().setScene(cloneScene(converted));
        void downloadSceneFontsLocally(converted.objects || [], konvaStageRef.current).then(() =>
          setLayersKey((k) => k + 1),
        );
      } else {
        setPages([null]);
        setCurrentPageIndex(0);
        setToastMessage('Template design data could not be loaded.');
      }
      konvaTemplateLoadedKeyRef.current = applyTemplateKey;
      konvaHistoryBaselineRef.current = 0;
      setLayersKey((k) => k + 1);
      try {
        const histDoc = collectKonvaEditorJson();
        setHistoryState({ history: [histDoc], index: 0 });
      } catch (_) {}
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          suppressKonvaHistoryRef.current = false;
          // Re-save legacy templates so /api/flyers/event/pdf receives Fabric font fields.
          const tid = normalizeTemplateId(currentTemplateId);
          if (tid && templateJsonNeedsServerFontMigration(normalized)) {
            updateCurrentTemplateWithName(templateBarName || UNSAVED_TEMPLATE_LABEL, { silent: true }).catch(
              () => {},
            );
          }
        });
      });
      return undefined;
    }
    if (konvaTemplateLoadedKeyRef.current === applyTemplateKey) return undefined;
    useEditorStore.getState().setPageDimensions(template.width, template.height);
    return undefined;
  }, [template.width, template.height, applyTemplateKey]);

  /** When page pixel size is larger than the main area, reduce zoom (inch presets at 300 DPI). */
  useEffect(() => {
    const run = () => {
      const el = mainCanvasAreaRef.current;
      if (!el || !template.width || !template.height) return;
      const padX = 40;
      const padY = preview ? 48 : 140;
      const availW = Math.max(160, el.clientWidth - padX);
      const availH = Math.max(160, el.clientHeight - padY);
      const fitPct = Math.min((availW / template.width) * 100, (availH / template.height) * 100);
      if (Number.isFinite(fitPct) && fitPct < 99) {
        setZoom(snapZoomToLevel(fitPct));
      }
    };
    const id = requestAnimationFrame(() => requestAnimationFrame(run));
    return () => cancelAnimationFrame(id);
  }, [template.width, template.height, preview]);

  const updateSelectionBounds = useCallback(() => {
    if (USE_KONVA_EDITOR) {
      const ids = useEditorStore.getState().selectedIds;
      const nodes = ids.map((id) => useEditorStore.getState().getObjectById(id)).filter(Boolean);
      if (!nodes.length) {
        setSelectionBounds(null);
        return;
      }
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const node of nodes) {
        const t = node.transform;
        if (!t) continue;
        minX = Math.min(minX, t.x);
        minY = Math.min(minY, t.y);
        maxX = Math.max(maxX, t.x + Math.max(1, t.width || 0));
        maxY = Math.max(maxY, t.y + Math.max(1, t.height || 0));
      }
      if (!Number.isFinite(minX)) {
        setSelectionBounds(null);
        return;
      }
      setSelectionBounds({
        left: minX,
        top: minY,
        width: Math.max(1, maxX - minX),
        height: Math.max(1, maxY - minY),
      });
      return;
    }
    const obj = fabricRef.current?.getActiveObject();
    if (!obj) {
      setSelectionBounds(null);
      return;
    }
    try {
      obj.setCoords();
      const rect = obj.getBoundingRect(true);
      const cw = fabricRef.current?.getWidth?.();
      const ch = fabricRef.current?.getHeight?.();
      const safe = sanitizeBoundingRect(rect, cw, ch);
      if (!safe) {
        setSelectionBounds(null);
        return;
      }
      setSelectionBounds(safe);
    } catch (_) {
      setSelectionBounds(null);
    }
  }, []);
  updateSelectionBoundsRef.current = updateSelectionBounds;

  function resetCanvasSelectionHighlight(obj) {
    if (!obj || obj.type === 'activeSelection' || !obj.canvas) return;
    resetFabricControlVisibility(obj);
    applyFabricObjectSelectionChrome(obj, DEFAULT_CANVAS_SELECTION_STYLE);
    if (obj.type === 'textbox' || obj.type === 'i-text' || obj.type === 'text') {
      ensureTextObjectVisible(obj);
    }
  }
  function applyCanvasSelectionHighlight(obj) {
    if (!obj || obj.type === 'activeSelection' || !obj.canvas) return;
    resetFabricControlVisibility(obj);
    const inner = getInnerShapeLabelText(obj) || resolveEditorTextBoxLabel(obj);
    const hasPh = Boolean((inner && inner.fieldKey) || obj.fieldKey);
    if (isTextBoxFrame(obj) || isTextBoxGroup(obj) || fabricTypeIsTextOrImage(obj.type)) {
      let rect;
      try {
        rect = obj.getBoundingRect(true);
      } catch (_) {
        rect = { width: 0, height: 0 };
      }
      const safe = sanitizeBoundingRect(rect, fabricRef.current?.getWidth?.(), fabricRef.current?.getHeight?.()) || {
        width: 0,
        height: 0,
        left: 0,
        top: 0,
      };
      const longEdge = Math.max(safe.width || 0, safe.height || 0);
      // Never use the ultra-compact handle set for live text: it hides mt/mb so vertical
      // resize (font scaling) is impossible whenever the bbox is smaller than the threshold.
      const isLiveTextType =
        isTextBoxFrame(obj) ||
        isTextBoxGroup(obj) ||
        obj.type === 'textbox' ||
        obj.type === 'i-text' ||
        obj.type === 'text';
      const compact =
        !isLiveTextType && longEdge > 0 && longEdge < TEXT_IMAGE_COMPACT_SELECTION_PX;
      const base = hasPh
        ? compact
          ? TEXT_IMAGE_PLACEHOLDER_SELECTION_STYLE_COMPACT
          : TEXT_IMAGE_PLACEHOLDER_SELECTION_STYLE
        : compact
          ? TEXT_IMAGE_SELECTION_STYLE_COMPACT
          : TEXT_IMAGE_SELECTION_STYLE;
      applyFabricObjectSelectionChrome(obj, base);
      const isTextLike =
        isTextBoxFrame(obj) ||
        isTextBoxGroup(obj) ||
        obj.type === 'textbox' ||
        obj.type === 'i-text' ||
        obj.type === 'text';
      if (isTextLike && inner) ensureTextObjectVisible(inner);
      else if (isTextLike) ensureTextObjectVisible(obj);
      const isImage = obj.type === 'image';
      if (isTextBoxFrame(obj) || isTextBoxGroup(obj) || obj.type === 'textbox') {
        try {
          obj.set({ lockScalingY: false, lockScalingX: false, lockUniScaling: false });
        } catch (_) {}
      }
      if (isImage) {
        try {
          obj.set({ lockUniScaling: true, lockScalingX: false, lockScalingY: false });
        } catch (_) {}
      }
      if (compact) {
        obj.setControlsVisibility({
          tl: false,
          tr: false,
          bl: false,
          br: !isImage,
          ml: isImage,
          mr: isImage,
          mt: isImage,
          mb: isImage,
          mtr: false,
        });
      } else {
        obj.setControlsVisibility({
          tl: true,
          tr: true,
          bl: true,
          br: true,
          ml: true,
          mr: true,
          mt: true,
          mb: true,
          mtr: true,
        });
      }
      return;
    }
    obj.set(hasPh ? PLACEHOLDER_CANVAS_SELECTION_STYLE : DEFAULT_CANVAS_SELECTION_STYLE);
  }

  applyCanvasSelectionHighlightRef.current = applyCanvasSelectionHighlight;

  /** HTML overlays for placeholder labels on text/image when not selected (not in preview / export). */
  function syncUnselectedPlaceholderOverlays() {
    if (USE_KONVA_EDITOR) {
      if (previewRef.current) {
        if (lastPhOverlaySnapRef.current !== '[]') {
          lastPhOverlaySnapRef.current = '[]';
          setUnselectedPlaceholderOverlays([]);
        }
        return;
      }
      const selectedId = useEditorStore.getState().selectedId;
      const items = [];
      for (const obj of useEditorStore.getState().scene.objects) {
        if (!obj.fieldKey) continue;
        if (obj.type !== 'text' && obj.type !== 'image') continue;
        if (obj.id === selectedId) continue;
        const t = obj.transform || {};
        items.push({
          id: obj.id,
          left: Math.round(t.x || 0),
          top: Math.round(t.y || 0),
          width: Math.max(1, Math.round(t.width || 1)),
          height: Math.max(1, Math.round(t.height || 1)),
          label: resolvePlaceholderLabel(obj.fieldKey, obj.type, textPlaceholders, imagePlaceholders) || obj.fieldKey,
          fieldKey: obj.fieldKey,
        });
      }
      const snap = JSON.stringify(items);
      if (snap !== lastPhOverlaySnapRef.current) {
        lastPhOverlaySnapRef.current = snap;
        setUnselectedPlaceholderOverlays(items);
      }
      return;
    }
    const canvas = fabricRef.current;
    if (!canvas || previewRef.current) {
      if (lastPhOverlaySnapRef.current !== '[]') {
        lastPhOverlaySnapRef.current = '[]';
        setUnselectedPlaceholderOverlays([]);
      }
      return;
    }
    const selectedTargets = new Set(getActiveTargets(canvas));
    const items = [];
    forEachCanvasObjectDeep(canvas, (obj) => {
      if (!obj.fieldKey) return;
      const t = obj.type;
      if (t !== 'i-text' && t !== 'textbox' && t !== 'text' && t !== 'image') return;
      if (selectedTargets.has(obj)) return;
      if (!obj.canvas) return;
      try {
        const rect = obj.getBoundingRect(true);
        const cw = canvas.getWidth?.();
        const ch = canvas.getHeight?.();
        const safe = sanitizeBoundingRect(rect, cw, ch);
        if (!safe) return;
        const left = Math.round(safe.left);
        const top = Math.round(safe.top);
        const width = Math.max(1, Math.round(safe.width));
        const height = Math.max(1, Math.round(safe.height));
        const label = resolvePlaceholderLabel(obj.fieldKey, obj.type, textPlaceholders, imagePlaceholders) || obj.fieldKey;
        const id =
          obj.__unselectedPhBadgeId ||
          (obj.__unselectedPhBadgeId = `ph-badge-${Math.random().toString(36).slice(2, 11)}`);
        items.push({ id, left, top, width, height, label, fieldKey: obj.fieldKey });
      } catch (_) {}
    });
    const snap = JSON.stringify(items);
    if (snap !== lastPhOverlaySnapRef.current) {
      lastPhOverlaySnapRef.current = snap;
      setUnselectedPlaceholderOverlays(items);
    }
  }
  syncUnselectedPlaceholderOverlaysRef.current = syncUnselectedPlaceholderOverlays;

  useEffect(() => {
    requestAnimationFrame(() => {
      try {
        syncUnselectedPlaceholderOverlaysRef.current?.();
      } catch (_) {}
    });
  }, [layersKey, preview, template.width, template.height, textPlaceholders, imagePlaceholders]);

  function onObjectSelected(e) {
    let obj = e.target || (e.selected && e.selected[0]);
    const canvas = fabricRef.current;
    if (canvas && obj) obj = promoteTextBoxFrameSelection(canvas, obj);
    const prevStyled = lastCanvasSelectionStyledRef.current;
    if (prevStyled && prevStyled !== obj) resetCanvasSelectionHighlight(prevStyled);
    lastCanvasSelectionStyledRef.current = obj || null;

    setSelectedObj(obj);
    if (!obj) {
      setSelectionBounds(null);
      return;
    }
    if (obj.type === 'group' && obj._shapeLabelGroup) {
      migrateShapeGroupFieldKeyToLabel(obj);
    }
    applyCanvasSelectionHighlight(obj);
    const innerLabel = getInnerShapeLabelText(obj);
    const textObj =
      resolveEditorTextBoxLabel(obj) ||
      (obj.type === 'i-text' || obj.type === 'textbox' || obj.type === 'text' ? obj : innerLabel);
    if (textObj) ensureTextObjectVisible(textObj);
    fabricRef.current?.renderAll();
    if (obj.type === 'textbox') {
      obj.__lastSyncedTextboxWidth = obj.width;
    }
    if (textObj) {
      setTextProps({
        text: textObj.text,
        fontSize: textObj.fontSize || 56,
        fontFamily: textObj.fontFamily || 'Arial',
        fill: textObj.fill || '#111827',
        fontWeight: textObj.fontWeight || 'normal',
        fontStyle: textObj.fontStyle || 'normal',
        underline: textObj.underline || false,
        textAlign: textObj.textAlign || 'left',
      });
    } else {
      setTextProps({ text: '', fontSize: 56, fontFamily: 'Arial', fill: '#111827', fontWeight: 'normal', fontStyle: 'normal', underline: false, textAlign: 'left' });
    }
    if (obj.type !== 'i-text' && obj.type !== 'textbox' && obj.type !== 'text' && obj.type !== 'image' && (obj.fill != null || obj.stroke != null)) {
      if (obj.type === 'group' && obj.getObjects?.().length) {
        const shapePart = obj.getObjects().find((c) => c.type !== 'i-text' && c.type !== 'textbox' && c.type !== 'text') || obj.getObjects()[0];
        setShapeFillColor(shapePart?.fill != null && typeof shapePart.fill === 'string' ? shapePart.fill : shapeFillColor);
        setShapeStrokeColor(shapePart?.stroke != null && typeof shapePart.stroke === 'string' ? shapePart.stroke : shapeStrokeColor);
      } else {
        setShapeFillColor(typeof obj.fill === 'string' ? obj.fill : shapeFillColor);
        setShapeStrokeColor(typeof obj.stroke === 'string' ? obj.stroke : shapeStrokeColor);
      }
    }
    setDynamicKey((textObj && textObj.fieldKey) || obj.fieldKey || '');
    requestAnimationFrame(() => updateSelectionBoundsRef.current?.());
  }

  const undo = useCallback(() => {
    const baseline = USE_KONVA_EDITOR ? konvaHistoryBaselineRef.current : 0;
    if (historyIndex <= baseline) return;
    const prev = historyIndex - 1;
    setHistoryState((s) => ({ ...s, index: prev }));
    const snap = history[prev];
    if (!snap) return;
    if (USE_KONVA_EDITOR) {
      isRestoringHistoryRef.current = true;
      const doc = parseEditorDocument(snap);
      if (doc.kind === 'konva') {
        if (snap.pageMeta) applyPageDimensions(snap.pageMeta);
        const pm = snap.pageMeta;
        const sceneW = pm?.width || doc.scene.width || template.width;
        const sceneH = pm?.height || doc.scene.height || template.height;
        useEditorStore.getState().setScene(cloneScene({
          ...doc.scene,
          width: Math.max(1, Number(sceneW) || 800),
          height: Math.max(1, Number(sceneH) || 600),
        }));
        applyKonvaBackgroundMeta(doc.background);
      }
      isRestoringHistoryRef.current = false;
      setSelectedObj(null);
      setSelectionBounds(null);
      setLayersKey((k) => k + 1);
      return;
    }
    const f = fabricRef.current;
    if (f) {
      isRestoringHistoryRef.current = true;
      f.loadFromJSON(
        snap,
        () => {
          try {
            finalizeCanvasTextBoxes(f);
          } catch (_) {}
          f.renderAll();
          isRestoringHistoryRef.current = false;
        },
        fabricCustomPropsReviver,
      );
    }
  }, [history, historyIndex]);

  const redo = useCallback(() => {
    if (historyIndex >= history.length - 1 || !history[historyIndex + 1]) return;
    const next = historyIndex + 1;
    setHistoryState((s) => ({ ...s, index: next }));
    const snap = history[next];
    if (!snap) return;
    if (USE_KONVA_EDITOR) {
      isRestoringHistoryRef.current = true;
      const doc = parseEditorDocument(snap);
      if (doc.kind === 'konva') {
        if (snap.pageMeta) applyPageDimensions(snap.pageMeta);
        const pm = snap.pageMeta;
        const sceneW = pm?.width || doc.scene.width || template.width;
        const sceneH = pm?.height || doc.scene.height || template.height;
        useEditorStore.getState().setScene(cloneScene({
          ...doc.scene,
          width: Math.max(1, Number(sceneW) || 800),
          height: Math.max(1, Number(sceneH) || 600),
        }));
        applyKonvaBackgroundMeta(doc.background);
      }
      isRestoringHistoryRef.current = false;
      setSelectedObj(null);
      setSelectionBounds(null);
      setLayersKey((k) => k + 1);
      return;
    }
    const f = fabricRef.current;
    if (f) {
      isRestoringHistoryRef.current = true;
      f.loadFromJSON(
        snap,
        () => {
          try {
            finalizeCanvasTextBoxes(f);
          } catch (_) {}
          f.renderAll();
          isRestoringHistoryRef.current = false;
        },
        fabricCustomPropsReviver,
      );
    }
  }, [history, historyIndex]);

  function addCanvasTextbox(text, style = {}) {
    if (USE_KONVA_EDITOR) {
      useEditorStore.getState().addTextBox({
        text,
        fontSize: style.fontSize || 56,
        fontFamily: style.fontFamily,
        fontWeight: style.fontWeight,
        fontStyle: style.fontStyle,
        fill: style.fill,
        textAlign: style.textAlign,
        height: style.minHeight,
      });
      const node = useEditorStore.getState().getObjectById(useEditorStore.getState().selectedId);
      if (node) handleKonvaSelection(node);
      pushKonvaHistory();
      setLayersKey((k) => k + 1);
      return;
    }
    const { fabric } = require('fabric');
    const canvas = fabricRef.current;
    if (!canvas) return;
    const cw = template.width || 800;
    const ch = template.height || 600;
    const fontSize = style.fontSize || 56;
    const boxW = Math.min(Math.max(120, cw * 0.55), 520);
    const boxH = Math.max(fontSize * 1.4, style.minHeight || fontSize * 2);
    const frame = addResizableTextboxToCanvas(canvas, fabric, {
      text,
      left: cw / 2,
      top: ch / 2,
      originX: 'center',
      originY: 'center',
      width: boxW,
      height: boxH,
      fontSize,
      fontFamily: style.fontFamily || 'Arial',
      fontWeight: style.fontWeight || 'normal',
      fontStyle: style.fontStyle || 'normal',
      fill: style.fill || '#111827',
      textAlign: style.textAlign || 'left',
    });
    if (!frame) return;
    const label = resolveEditorTextBoxLabel(frame);
    if (label) label.fieldConfig = snapshotTextFieldConfig(label);
    frame.fieldConfig = label?.fieldConfig;
    canvas.setActiveObject(frame);
    if (label) ensureTextObjectVisible(label);
    canvas.renderAll();
  }

  function addText() {
    addCanvasTextbox('Double-click to edit', { fontSize: 56 });
  }

  function addTextTitle() {
    addCanvasTextbox('Title', { fontSize: 72, fontWeight: 'bold', textAlign: 'center' });
  }
  function addTextHeadline() {
    addCanvasTextbox('Headline', { fontSize: 40, fontWeight: '600', fill: '#374151', textAlign: 'center' });
  }
  function addTextParagraph() {
    addCanvasTextbox('Add a paragraph here.', {
      fontSize: 20,
      fill: '#4b5563',
      textAlign: 'left',
      minHeight: 80,
    });
  }

  function applyTemplateDimensionsFromRecord(tpl) {
    const u = tpl.unit || 'Inch';
    const res = tpl.resolution || resolution;
    setUnit(u);
    if (tpl.width == null || tpl.height == null) return;
    const w = clampTemplatePx(tpl.width);
    const h = clampTemplatePx(tpl.height);
    if (u === 'Inch') {
      setWidthMm(Math.round((w / res) * 1000) / 1000);
      setHeightMm(Math.round((h / res) * 1000) / 1000);
    } else if (u === 'Millimeter') {
      setWidthMm(pxToMm(w, res));
      setHeightMm(pxToMm(h, res));
    } else {
      setWidthMm(w);
      setHeightMm(h);
    }
  }

  function applyPageDimensions(pageMeta) {
    if (!pageMeta) return;
    if (pageMeta.unit) setUnit(pageMeta.unit);
    if (pageMeta.widthMm != null) setWidthMm(pageMeta.widthMm);
    if (pageMeta.heightMm != null) setHeightMm(pageMeta.heightMm);
    const w = Math.max(1, Number(pageMeta.width) || template.width || 800);
    const h = Math.max(1, Number(pageMeta.height) || template.height || 600);
    useEditorStore.getState().setPageDimensions(w, h);
  }

  function buildPageMetaFromCurrent(sceneWidth, sceneHeight) {
    const sw = Math.max(1, Number(sceneWidth) || template.width || 800);
    const sh = Math.max(1, Number(sceneHeight) || template.height || 600);
    const matchesUi =
      Math.round(sw) === Math.round(template.width) &&
      Math.round(sh) === Math.round(template.height);
    return {
      width: sw,
      height: sh,
      unit: matchesUi ? unit : 'Pixel',
      widthMm: matchesUi ? widthMm : sw,
      heightMm: matchesUi ? heightMm : sh,
      resolution,
    };
  }

  /** Snapshot all pages, flushing the live canvas into the current page slot. */
  function getPagesSnapshot() {
    const idx = currentPageIndexRef.current;
    const prev = [...pagesRef.current];
    if (USE_KONVA_EDITOR) {
      prev[idx] = collectKonvaEditorJson();
      return prev;
    }
    const f = fabricRef.current;
    if (f) {
      prev[idx] = f.toJSON([
        'fieldKey', 'fieldConfig', '_shapeLabelGroup', TEXT_BOX_GROUP_FLAG, TEXT_BOX_PAIR_ID,
        TEXT_BOX_FRAME_FLAG, TEXT_BOX_LABEL_FLAG, '_boxWidth', '_boxHeight',
      ]);
    }
    return prev;
  }

  /** All pages with content for export (flushes live canvas, trims trailing empty slots). */
  function getExportPageDocs() {
    const snapshot = getPagesSnapshot();
    let last = snapshot.length - 1;
    while (last > 0 && !snapshot[last]) last -= 1;
    return snapshot.slice(0, last + 1).filter(Boolean);
  }

  function applyTemplate(tpl) {
    // Isolate page state per template — prevents cross-template page bleed.
    setPages([null]);
    setCurrentPageIndex(0);
    pagesRef.current = [null];
    currentPageIndexRef.current = 0;
    if (tpl.json) {
      const tid = normalizeTemplateId(tpl._id ?? tpl.id);
      setCurrentTemplateId(tid);
      setIsUnsavedDraft(!tid);
      setTemplateBarName(tid ? tpl.name || 'Untitled template' : UNSAVED_TEMPLATE_LABEL);
      if (tpl.preset) setPresetName(tpl.preset);
      applyTemplateDimensionsFromRecord(tpl);
      pendingSavedTemplateJsonRef.current = normalizeFabricJsonInput(tpl.json);
      konvaTemplateLoadedKeyRef.current = -1;
      if (USE_KONVA_EDITOR) {
        suppressKonvaHistoryRef.current = true;
        konvaHistoryBaselineRef.current = 0;
        setHistoryState({ history: [], index: -1 });
      }
      setApplyTemplateKey((k) => k + 1);
    } else {
      setCurrentTemplateId(null);
      setIsUnsavedDraft(true);
      setTemplateBarName(UNSAVED_TEMPLATE_LABEL);
      applyPreset(tpl.preset);
      pendingTemplateIdRef.current = tpl.id;
    }
  }

  async function openSavedTemplate(tpl) {
    const tid = tpl?._id != null ? String(tpl._id) : tpl?.id != null ? String(tpl.id) : '';
    if (tpl?.json) {
      applyTemplate(tpl);
      return;
    }
    if (!tid || !/^\d+$/.test(tid)) {
      applyTemplate(tpl);
      return;
    }
    try {
      const r = await apiAxios.post('/api/templates/open', { id: tid });
      if (r.status < 200 || r.status >= 300 || !r.data?.json) {
        const msg =
          (r.data && typeof r.data === 'object' && r.data.error) ||
          `Could not open template (HTTP ${r.status}).`;
        setToastMessage(msg);
        return;
      }
      applyTemplate({ ...tpl, ...r.data });
    } catch (err) {
      console.error('[templates] open failed:', err?.message || err);
      setToastMessage(err?.message || 'Could not open template');
    }
  }

  function assertUniqueTemplateName(name, excludeTemplateId) {
    const n = (name || '').trim().toLowerCase();
    if (!n) throw new Error('Please enter a template name');
    const taken = savedTemplates.some(
      (t) =>
        String(t._id) !== String(excludeTemplateId ?? '') &&
        (t.name || '').trim().toLowerCase() === n
    );
    if (taken) throw new Error('A template with this name already exists');
  }

  function collectFabricJson() {
    const f = fabricRef.current;
    if (!f) return null;
    return f.toJSON(['fieldKey', 'fieldConfig', '_shapeLabelGroup', TEXT_BOX_GROUP_FLAG, TEXT_BOX_PAIR_ID, TEXT_BOX_FRAME_FLAG, TEXT_BOX_LABEL_FLAG, '_boxWidth', '_boxHeight']);
  }

  function collectKonvaEditorJson() {
    const scene = useEditorStore.getState().getSerializedScene();
    const sceneW = Math.max(1, Number(scene.width) || Number(template.width) || 800);
    const sceneH = Math.max(1, Number(scene.height) || Number(template.height) || 600);
    scene.width = sceneW;
    scene.height = sceneH;
    return serializeKonvaDocument(scene, {
      background: {
        bgType,
        bgColor,
        bgGradientFrom,
        bgGradientTo,
        bgImageUrl,
      },
      pageMeta: buildPageMetaFromCurrent(sceneW, sceneH),
    });
  }

  function collectEditorJson() {
    if (USE_KONVA_EDITOR) {
      return serializeKonvaMultiPageDocument(getPagesSnapshot());
    }
    return collectFabricJson();
  }

  function pushKonvaHistory() {
    if (isRestoringHistoryRef.current || suppressKonvaHistoryRef.current) return;
    try {
      const doc = collectKonvaEditorJson();
      setHistoryState((prev) => {
        const next = prev.history.slice(0, prev.index + 1);
        next.push(doc);
        if (next.length > HISTORY_LIMIT) next.shift();
        return { history: next, index: next.length - 1 };
      });
    } catch (_) {}
  }

  function applyKonvaBackgroundMeta(meta) {
    if (!meta) return;
    if (meta.bgType) setBgType(meta.bgType);
    if (meta.bgColor) setBgColor(meta.bgColor);
    if (meta.bgGradientFrom) setBgGradientFrom(meta.bgGradientFrom);
    if (meta.bgGradientTo) setBgGradientTo(meta.bgGradientTo);
    if (meta.bgImageUrl !== undefined) setBgImageUrl(meta.bgImageUrl);
  }

  /** Fast list thumbnail: rasterize the live canvas in-browser. */
  function collectTemplatePreviewDataUrl() {
    if (USE_KONVA_EDITOR) {
      const stage = konvaStageRef.current;
      if (!stage) return null;
      try {
        const prevSelected = [...useEditorStore.getState().selectedIds];
        useEditorStore.getState().clearSelection();
        stage.find('Transformer').forEach((tr) => tr.visible(false));
        stage.batchDraw();
        const w = stage.width();
        const h = stage.height();
        const long = Math.max(1, w, h);
        const pixelRatio = Math.min(0.42, 560 / long);
        const dataUrl = stage.toDataURL({ pixelRatio, mimeType: 'image/jpeg', quality: 0.84 });
        stage.find('Transformer').forEach((tr) => tr.visible(true));
        if (prevSelected.length) useEditorStore.getState().setSelection(prevSelected);
        stage.batchDraw();
        return dataUrl;
      } catch (e) {
        console.warn('[template] Konva preview skipped:', e?.message || e);
        return null;
      }
    }
    const c = fabricRef.current;
    if (!c) return null;
    try {
      c.renderAll();
      const w = c.getWidth();
      const h = c.getHeight();
      const long = Math.max(1, w, h);
      const mult = Math.min(0.42, 560 / long);
      return c.toDataURL({ format: 'jpeg', quality: 0.84, multiplier: mult });
    } catch (e) {
      console.warn('[template] Client preview skipped (tainted canvas or export error):', e?.message || e);
      return null;
    }
  }

  function collectTemplatePreviewDataUrlAsync() {
    if (!USE_KONVA_EDITOR) return Promise.resolve(collectTemplatePreviewDataUrl());
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => resolve(collectTemplatePreviewDataUrl()));
      });
    });
  }

  function assertKonvaSceneReadyForSave() {
    if (!USE_KONVA_EDITOR) return;
    const snapshot = getPagesSnapshot();
    const hasContent = snapshot.some((page) => {
      if (!page) return false;
      const parsed = parseEditorDocument(page);
      return parsed.kind === 'konva' && (parsed.scene?.objects?.length || 0) > 0;
    });
    if (!hasContent) {
      throw new Error('Canvas is empty. Add text or images and assign Data fields before saving.');
    }
  }

  async function updateCurrentTemplateWithName(name, options = {}) {
    const tid = normalizeTemplateId(currentTemplateId);
    if (!tid) throw new Error('No template to update');
    assertKonvaSceneReadyForSave();
    const pageSnapshot = getPagesSnapshot();
    const json = serializeKonvaMultiPageDocument(pageSnapshot) || collectEditorJson();
    if (!json) throw new Error('Canvas not ready');
    const n = name.trim() || 'My template';
    if (!options.silent) setTemplateBarError(null);
    setPages(pageSnapshot);
    pagesRef.current = pageSnapshot;
    const firstMeta = pageSnapshot[0]?.pageMeta;
    const saveWidth = firstMeta?.width || template.width;
    const saveHeight = firstMeta?.height || template.height;
    const saveUnit = firstMeta?.unit || unit;
    const preview_image = await collectTemplatePreviewDataUrlAsync();
    const res = await apiAxios.put(
      '/api/templates/update',
      {
        id: tid,
        name: n,
        category: 'Flyers',
        preset: presetName,
        width: saveWidth,
        height: saveHeight,
        resolution,
        unit: saveUnit,
        json,
        ...(preview_image ? { preview_image } : {}),
      },
      { skipGlobalLoader: Boolean(options.silent) },
    );
    const updated = assertAxiosJsonOk(res);
    setSavedTemplates((prev) =>
      prev.map((t) => (String(t._id) === String(tid) ? { ...updated, _id: String(updated._id ?? updated.id), id: String(updated._id ?? updated.id) } : t))
    );
    setTemplateBarName(updated.name || n);
    setTemplateBarError(null);
    return updated;
  }

  async function saveNewTemplateWithName(name, options = {}) {
    assertKonvaSceneReadyForSave();
    const pageSnapshot = getPagesSnapshot();
    const json = serializeKonvaMultiPageDocument(pageSnapshot) || collectEditorJson();
    if (!json) throw new Error('Canvas not ready');
    const n = name.trim() || 'My template';
    const st = options.status === 'active' ? 'active' : 'inactive';
    setTemplateBarError(null);
    setPages(pageSnapshot);
    pagesRef.current = pageSnapshot;
    const firstMeta = pageSnapshot[0]?.pageMeta;
    const saveWidth = firstMeta?.width || template.width;
    const saveHeight = firstMeta?.height || template.height;
    const saveUnit = firstMeta?.unit || unit;
    const preview_image = await collectTemplatePreviewDataUrlAsync();
    const res = await apiAxios.post('/api/templates', {
      name: n,
      category: 'Flyers',
      preset: presetName,
      width: saveWidth,
      height: saveHeight,
      resolution,
      unit: saveUnit,
      json,
      status: st,
      ...(preview_image ? { preview_image } : {}),
    });
    const saved = assertAxiosJsonOk(res);
    const newId = String(saved._id ?? saved.id ?? '');
    setCurrentTemplateId(newId || null);
    setTemplateBarName(saved.name || n);
    setIsUnsavedDraft(false);
    setSavedTemplates((prev) => [
      { ...saved, _id: newId, id: newId },
      ...prev.filter((t) => String(t._id) !== newId),
    ]);
    setTemplateBarError(null);
    return saved;
  }

  async function patchTemplateStatus(templateId, status) {
    const tid = normalizeTemplateId(templateId);
    if (!tid) throw new Error('Invalid template id');
    if (status !== 'active' && status !== 'inactive') throw new Error('Invalid status');
    const res = await apiAxios.post('/api/templates/status', { id: tid, status });
    const updated = assertAxiosJsonOk(res);
    const idStr = String(updated._id ?? updated.id ?? tid);
    setSavedTemplates((prev) =>
      prev.map((t) =>
        String(t._id) === idStr ? { ...t, ...updated, _id: idStr, id: idStr, status: updated.status } : t
      )
    );
    return updated;
  }

  async function renameTemplateById(templateId, newName) {
    const tid = normalizeTemplateId(templateId);
    const n = (newName || '').trim();
    if (!tid || !n) throw new Error('Invalid name');
    assertUniqueTemplateName(n, tid);
    const res = await apiAxios.post('/api/templates/rename', { id: tid, name: n });
    const updated = assertAxiosJsonOk(res);
    const idStr = String(tid);
    setSavedTemplates((prev) =>
      prev.map((t) => (String(t._id) === idStr ? { ...t, ...updated, name: updated.name, _id: idStr, id: idStr } : t))
    );
    if (String(currentTemplateId) === idStr) setTemplateBarName(updated.name || n);
  }

  async function deleteTemplateById(templateId) {
    const tid = normalizeTemplateId(templateId);
    if (!tid) throw new Error('Invalid template id');
    const res = await apiAxios.post('/api/templates/remove', { id: tid });
    assertAxiosJsonOk(res);
    setSavedTemplates((prev) => prev.filter((t) => String(t._id) !== tid));
    setDeleteConfirm(null);
    if (String(currentTemplateId) === tid) startNewTemplate();
  }

  function openStatusChangeDialog() {
    const isActive = currentTemplateId ? currentTemplateStatus === 'active' : draftStatusActive;
    setStatusConfirm({
      draft: !currentTemplateId,
      next: isActive ? 'inactive' : 'active',
    });
  }

  async function applyStatusConfirm() {
    const pending = statusConfirm;
    if (!pending) return;
    if (pending.draft) {
      setDraftStatusActive(pending.next === 'active');
      setStatusConfirm(null);
      return;
    }
    const tid = normalizeTemplateId(currentTemplateId);
    if (!tid) {
      setStatusConfirm(null);
      return;
    }
    setTemplateStatusBusy(true);
    setTemplateBarError(null);
    try {
      await patchTemplateStatus(tid, pending.next);
    } catch (err) {
      setTemplateBarError(err.message || 'Failed to update status');
    } finally {
      setTemplateStatusBusy(false);
      setStatusConfirm(null);
    }
  }

  function suggestTemplateModalName(mode) {
    const boundId = normalizeTemplateId(currentTemplateId);
    const bar = templateBarName.trim();
    const hasDisplayName = bar && bar !== UNSAVED_TEMPLATE_LABEL;

    if (mode === 'newSave') {
      // Unbound draft: always start fresh — do not reuse a previously opened template name.
      if (!boundId) return 'My template';
      return hasDisplayName ? bar : 'My template';
    }

    if (mode === 'update') {
      return hasDisplayName ? bar : 'My template';
    }

    if (mode === 'saveAsNew') {
      const base = hasDisplayName ? bar.replace(/\s+\(copy\)$/i, '') : 'My template';
      return `${base} (copy)`;
    }

    return 'My template';
  }

  function openNewSaveModal() {
    setTemplateModalName(suggestTemplateModalName('newSave'));
    setTemplateModalMode('newSave');
    setTemplateModalError(null);
    setTemplateModalOpen(true);
  }

  function openUpdateTemplateModal() {
    setTemplateModalName(suggestTemplateModalName('update'));
    setTemplateModalMode('update');
    setTemplateModalError(null);
    setTemplateModalOpen(true);
  }

  function openSaveAsNewTemplateModal() {
    setTemplateModalName(suggestTemplateModalName('saveAsNew'));
    setTemplateModalMode('saveAsNew');
    setTemplateModalError(null);
    setTemplateModalOpen(true);
  }

  async function submitTemplateModal() {
    const name = templateModalName.trim() || 'My template';
    setTemplateModalBusy(true);
    setTemplateModalError(null);
    try {
      if (templateModalMode === 'update') {
        assertUniqueTemplateName(name, currentTemplateId);
        await updateCurrentTemplateWithName(name);
      } else if (templateModalMode === 'saveAsNew') {
        assertUniqueTemplateName(name, null);
        const copyStatus = currentTemplateStatus === 'active' ? 'active' : 'inactive';
        await saveNewTemplateWithName(name, { status: copyStatus });
      } else if (templateModalMode === 'newSave') {
        assertUniqueTemplateName(name, null);
        await saveNewTemplateWithName(name, { status: draftStatusActive ? 'active' : 'inactive' });
      }
      setTemplateModalOpen(false);
    } catch (err) {
      setTemplateModalError(err.message || 'Request failed');
      setTemplateBarError(err.message || 'Request failed');
    } finally {
      setTemplateModalBusy(false);
    }
  }

  async function downloadFlyerBlobFromBackend() {
    const data = getMappings();
    const editorJson = collectEditorJson();
    const res = await apiFetch(apiUrl(`/api/flyers/generate?_=${Date.now()}`), {
      method: 'POST',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/pdf,application/octet-stream,*/*',
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
      body: JSON.stringify({
        templateId: currentTemplateId,
        data,
        ...(editorJson ? { json: editorJson } : {}),
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || res.statusText || `Server returned HTTP ${res.status}`);
    }
    const blob = await res.blob();
    if (!blob || !blob.size) throw new Error('Server returned an empty PDF');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flyer-${Date.now()}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function downloadFlyerWithData() {
    if (!currentTemplateId) {
      setDownloadError('Save the template first before downloading with dynamic data.');
      return;
    }
    if (USE_KONVA_EDITOR) {
      const objCount = useEditorStore.getState().scene?.objects?.length || 0;
      const keyCount = (useEditorStore.getState().scene?.objects || []).filter((o) => o.fieldKey).length;
      if (objCount === 0 || keyCount === 0) {
        setDownloadError(
          'Template has no placeholders. Add text boxes, assign Data fields in the sidebar, then Save before downloading.',
        );
        return;
      }
    }
    setDownloadError(null);
    try {
      await updateCurrentTemplateWithName(templateBarName || UNSAVED_TEMPLATE_LABEL, { silent: true });
      clearMeasureCache();
      // Canva-style: always export from the live canvas (WYSIWYG) for Konva/Fabric.
      if (USE_KONVA_EDITOR || fabricRef.current) {
        await exportPdfWithMappedScene();
        return;
      }
      await downloadFlyerBlobFromBackend();
    } catch (err) {
      setDownloadError(err.message || 'Failed to generate flyer');
    }
  }

  function addShapeRect() {
    if (addShapeByKey('rect')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const cx = template.width / 2;
    const cy = template.height / 2;
    const r = new fabric.Rect({
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      width: 120,
      height: 80,
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(r).setActiveObject(r);
    fabricRef.current.renderAll();
  }
  function addShapeCircle() {
    if (addShapeByKey('circle')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const cx = template.width / 2;
    const cy = template.height / 2;
    const c = new fabric.Circle({
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      radius: 50,
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(c).setActiveObject(c);
    fabricRef.current.renderAll();
  }
  function addShapeTriangle() {
    if (addShapeByKey('triangle')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const cx = template.width / 2;
    const cy = template.height / 2;
    const t = new fabric.Triangle({
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      width: 100,
      height: 90,
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(t).setActiveObject(t);
    fabricRef.current.renderAll();
  }
  function addShapeStar() {
    if (addShapeByKey('star')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const cx = template.width / 2;
    const cy = template.height / 2;
    const star = new fabric.Polygon(
      [ { x: 50, y: 0 }, { x: 61, y: 35 }, { x: 98, y: 35 }, { x: 68, y: 57 }, { x: 79, y: 92 }, { x: 50, y: 72 }, { x: 21, y: 92 }, { x: 32, y: 57 }, { x: 2, y: 35 }, { x: 39, y: 35 } ],
      { left: cx, top: cy, originX: 'center', originY: 'center', fill: style.fill, stroke: style.stroke }
    );
    fabricRef.current.add(star).setActiveObject(star);
    fabricRef.current.renderAll();
  }
  function addShapeLine() {
    if (addShapeByKey('line')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const cx = template.width / 2;
    const cy = template.height / 2;
    const line = new fabric.Line([0, 0, 200, 0], {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      stroke: shapeStrokeColor,
      strokeWidth: 3,
    });
    fabricRef.current.add(line).setActiveObject(line);
    fabricRef.current.renderAll();
  }
  function addShapeDottedLine() {
    if (addShapeByKey('dottedLine')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const cx = template.width / 2;
    const cy = template.height / 2;
    const line = new fabric.Line([0, 0, 200, 0], {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      stroke: shapeStrokeColor,
      strokeWidth: 3,
      strokeDashArray: [10, 8],
      fill: '',
    });
    fabricRef.current.add(line).setActiveObject(line);
    fabricRef.current.renderAll();
  }
  function addShapeArrow() {
    if (addShapeByKey('arrow')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const cx = template.width / 2;
    const cy = template.height / 2;
    const line = new fabric.Line([0, 0, 140, 0], { stroke: shapeStrokeColor, strokeWidth: 3 });
    const arrowHead = new fabric.Triangle({
      width: 20,
      height: 20,
      fill: shapeStrokeColor,
      angle: 90,
      left: 140,
      top: -10,
    });
    const group = new fabric.Group([line, arrowHead], {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
    });
    fabricRef.current.add(group).setActiveObject(group);
    fabricRef.current.renderAll();
  }
  function addShapeRoundedRect() {
    if (addShapeByKey('roundedRect')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const cx = template.width / 2;
    const cy = template.height / 2;
    const r = new fabric.Rect({
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      width: 120,
      height: 80,
      fill: style.fill,
      stroke: style.stroke,
      rx: 12,
      ry: 12,
    });
    fabricRef.current.add(r).setActiveObject(r);
    fabricRef.current.renderAll();
  }
  function addShapeEllipse() {
    if (addShapeByKey('ellipse')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const cx = template.width / 2;
    const cy = template.height / 2;
    const e = new fabric.Ellipse({
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      rx: 60,
      ry: 40,
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(e).setActiveObject(e);
    fabricRef.current.renderAll();
  }
  function addShapePentagon() {
    if (addShapeByKey('pentagon')) return;
    if (!fabricRef.current) return;
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const pcx = 50; const pcy = 50; const r = 45;
    const points = [];
    for (let i = 0; i < 5; i++) {
      const a = (i * 72 - 90) * (Math.PI / 180);
      points.push({ x: pcx + r * Math.cos(a), y: pcy + r * Math.sin(a) });
    }
    const cx = template.width / 2;
    const cy = template.height / 2;
    const poly = new fabric.Polygon(points, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(poly).setActiveObject(poly);
    fabricRef.current.renderAll();
  }
  function addShapeHexagon() {
    if (addShapeByKey('hexagon')) return;
    if (!fabricRef.current) return;
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const pcx = 50; const pcy = 50; const r = 45;
    const points = [];
    for (let i = 0; i < 6; i++) {
      const a = (i * 60 - 90) * (Math.PI / 180);
      points.push({ x: pcx + r * Math.cos(a), y: pcy + r * Math.sin(a) });
    }
    const cx = template.width / 2;
    const cy = template.height / 2;
    const poly = new fabric.Polygon(points, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(poly).setActiveObject(poly);
    fabricRef.current.renderAll();
  }
  function addShapeDiamond() {
    if (addShapeByKey('diamond')) return;
    if (!fabricRef.current) return;
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const points = [ { x: 50, y: 0 }, { x: 100, y: 50 }, { x: 50, y: 100 }, { x: 0, y: 50 } ];
    const cx = template.width / 2;
    const cy = template.height / 2;
    const poly = new fabric.Polygon(points, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(poly).setActiveObject(poly);
    fabricRef.current.renderAll();
  }
  function addShapeCross() {
    if (addShapeByKey('cross')) return;
    if (!fabricRef.current) return;
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const v = new fabric.Rect({ width: 24, height: 80, left: 28, top: 0, fill: style.fill, stroke: style.stroke });
    const h = new fabric.Rect({ width: 80, height: 24, left: 0, top: 28, fill: style.fill, stroke: style.stroke });
    const cx = template.width / 2;
    const cy = template.height / 2;
    const group = new fabric.Group([v, h], {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
    });
    fabricRef.current.add(group).setActiveObject(group);
    fabricRef.current.renderAll();
  }
  function addShapeHeart() {
    if (addShapeByKey('heart')) return;
    if (!fabricRef.current) return;
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const path = 'M 50 85 C 10 50 0 10 50 35 C 100 10 90 50 50 85 Z';
    const cx = template.width / 2;
    const cy = template.height / 2;
    const pathObj = new fabric.Path(path, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      fill: style.fill,
      stroke: style.stroke,
      scaleX: 0.9,
      scaleY: 0.9,
    });
    fabricRef.current.add(pathObj).setActiveObject(pathObj);
    fabricRef.current.renderAll();
  }
  function addShapeRightTriangle() {
    if (addShapeByKey('rightTriangle')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const cx = template.width / 2;
    const cy = template.height / 2;
    const t = new fabric.Triangle({
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      width: 100,
      height: 90,
      fill: style.fill,
      stroke: style.stroke,
      angle: 0,
    });
    fabricRef.current.add(t).setActiveObject(t);
    fabricRef.current.renderAll();
  }
  function addShapeCrescent() {
    if (addShapeByKey('crescent')) return;
    if (!fabricRef.current) return;
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const path = 'M 50 0 A 50 50 0 1 1 49.99 0 A 40 40 0 1 0 50 0 Z';
    const cx = template.width / 2;
    const cy = template.height / 2;
    const pathObj = new fabric.Path(path, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(pathObj).setActiveObject(pathObj);
    fabricRef.current.renderAll();
  }
  function addShapeRing() {
    if (addShapeByKey('ring')) return;
    if (!fabricRef.current) return;
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const outer = new fabric.Circle({ radius: 50, fill: style.fill, stroke: style.stroke });
    const inner = new fabric.Circle({ radius: 35, fill: '#ffffff', stroke: '#ffffff', left: 15, top: 15 });
    const cx = template.width / 2;
    const cy = template.height / 2;
    const group = new fabric.Group([outer, inner], {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
    });
    fabricRef.current.add(group).setActiveObject(group);
    fabricRef.current.renderAll();
  }
  function addShapeSemicircle() {
    if (addShapeByKey('semicircle')) return;
    if (!fabricRef.current) return;
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const path = 'M 40 0 A 40 40 0 0 1 40 80 L 0 80 L 0 0 Z';
    const cx = template.width / 2;
    const cy = template.height / 2;
    const pathObj = new fabric.Path(path, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(pathObj).setActiveObject(pathObj);
    fabricRef.current.renderAll();
  }
  function addShapeBlob() {
    if (addShapeByKey('blob')) return;
    if (!fabricRef.current) return;
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const path = 'M 50 10 Q 90 10 90 50 Q 90 90 50 90 Q 10 90 10 50 Q 10 10 50 10 Z';
    const cx = template.width / 2;
    const cy = template.height / 2;
    const pathObj = new fabric.Path(path, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(pathObj).setActiveObject(pathObj);
    fabricRef.current.renderAll();
  }
  function addShapeWave() {
    if (addShapeByKey('wave')) return;
    if (!fabricRef.current) return;
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const path = 'M 0 40 Q 25 0 50 40 T 100 40';
    const cx = template.width / 2;
    const cy = template.height / 2;
    const pathObj = new fabric.Path(path, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      stroke: style.stroke,
      strokeWidth: 4,
      fill: 'transparent',
    });
    fabricRef.current.add(pathObj).setActiveObject(pathObj);
    fabricRef.current.renderAll();
  }
  function addShapeTear() {
    if (addShapeByKey('tear')) return;
    if (!fabricRef.current) return;
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const path = 'M 50 0 Q 100 40 50 100 Q 0 40 50 0 Z';
    const cx = template.width / 2;
    const cy = template.height / 2;
    const pathObj = new fabric.Path(path, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(pathObj).setActiveObject(pathObj);
    fabricRef.current.renderAll();
  }
  function addShapeOctagon() {
    if (addShapeByKey('octagon')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const pcx = 50;
    const pcy = 50;
    const r = 45;
    const points = [];
    for (let i = 0; i < 8; i++) {
      const a = (i * 45 - 90) * (Math.PI / 180);
      points.push({ x: pcx + r * Math.cos(a), y: pcy + r * Math.sin(a) });
    }
    const cx = template.width / 2;
    const cy = template.height / 2;
    const poly = new fabric.Polygon(points, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(poly).setActiveObject(poly);
    fabricRef.current.renderAll();
  }
  function addShapeTrapezoid() {
    if (addShapeByKey('trapezoid')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const points = [{ x: 20, y: 0 }, { x: 80, y: 0 }, { x: 100, y: 80 }, { x: 0, y: 80 }];
    const cx = template.width / 2;
    const cy = template.height / 2;
    const poly = new fabric.Polygon(points, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(poly).setActiveObject(poly);
    fabricRef.current.renderAll();
  }
  function addShapeParallelogram() {
    if (addShapeByKey('parallelogram')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const points = [{ x: 30, y: 0 }, { x: 130, y: 0 }, { x: 100, y: 70 }, { x: 0, y: 70 }];
    const cx = template.width / 2;
    const cy = template.height / 2;
    const poly = new fabric.Polygon(points, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(poly).setActiveObject(poly);
    fabricRef.current.renderAll();
  }
  function addShapeLightning() {
    if (addShapeByKey('lightning')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const path = 'M 45 5 L 20 45 L 35 45 L 12 88 L 58 38 L 40 38 Z';
    const cx = template.width / 2;
    const cy = template.height / 2;
    const pathObj = new fabric.Path(path, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(pathObj).setActiveObject(pathObj);
    fabricRef.current.renderAll();
  }

  function addShapeChevron() {
    if (addShapeByKey('chevron')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const pts = [
      { x: 0, y: 50 },
      { x: 45, y: 10 },
      { x: 45, y: 35 },
      { x: 100, y: 35 },
      { x: 100, y: 65 },
      { x: 45, y: 65 },
      { x: 45, y: 90 },
    ];
    const cx = template.width / 2;
    const cy = template.height / 2;
    const poly = new fabric.Polygon(pts, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(poly).setActiveObject(poly);
    fabricRef.current.renderAll();
  }

  function addShapeDoubleArrow() {
    if (addShapeByKey('doubleArrow')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const cx = template.width / 2;
    const cy = template.height / 2;
    const line = new fabric.Line([20, 0, 180, 0], { stroke: shapeStrokeColor, strokeWidth: 3 });
    const leftH = new fabric.Triangle({
      width: 20,
      height: 20,
      left: 10,
      top: -10,
      angle: -90,
      fill: shapeStrokeColor,
      stroke: shapeStrokeColor,
    });
    const rightH = new fabric.Triangle({
      width: 20,
      height: 20,
      left: 170,
      top: -10,
      angle: 90,
      fill: shapeStrokeColor,
      stroke: shapeStrokeColor,
    });
    const group = new fabric.Group([line, leftH, rightH], {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
    });
    fabricRef.current.add(group).setActiveObject(group);
    fabricRef.current.renderAll();
  }

  function addShapeCloud() {
    if (addShapeByKey('cloud')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const path =
      'M 25 60 C 8 60 8 38 24 32 C 22 14 42 10 55 22 C 65 8 92 12 98 32 C 118 34 124 52 108 62 C 102 72 35 72 25 60 Z';
    const cx = template.width / 2;
    const cy = template.height / 2;
    const pathObj = new fabric.Path(path, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(pathObj).setActiveObject(pathObj);
    fabricRef.current.renderAll();
  }

  function addShapeSpeechBubble() {
    if (addShapeByKey('speechBubble')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const path =
      'M 14 10 Q 14 4 22 4 L 108 4 Q 116 4 116 12 L 116 54 Q 116 62 108 62 L 50 62 L 32 82 L 40 62 L 22 62 Q 14 62 14 54 Z';
    const cx = template.width / 2;
    const cy = template.height / 2;
    const pathObj = new fabric.Path(path, {
      left: cx,
      top: cy,
      originX: 'center',
      originY: 'center',
      fill: style.fill,
      stroke: style.stroke,
    });
    fabricRef.current.add(pathObj).setActiveObject(pathObj);
    fabricRef.current.renderAll();
  }

  function addShapeZigzag() {
    if (addShapeByKey('zigzag')) return;
    if (!fabricRef.current) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const cx = template.width / 2;
    const cy = template.height / 2;
    const zig = new fabric.Polyline(
      [
        { x: 0, y: 24 },
        { x: 24, y: 0 },
        { x: 48, y: 24 },
        { x: 72, y: 0 },
        { x: 96, y: 24 },
        { x: 120, y: 0 },
        { x: 144, y: 24 },
      ],
      {
        left: cx,
        top: cy,
        originX: 'center',
        originY: 'center',
        stroke: shapeStrokeColor,
        strokeWidth: 4,
        fill: '',
        strokeLineCap: 'round',
        strokeLineJoin: 'round',
      }
    );
    fabricRef.current.add(zig).setActiveObject(zig);
    fabricRef.current.renderAll();
  }

  function addShapeAt(shapeKey, left, top) {
    if (USE_KONVA_EDITOR) {
      setActiveTool('shapes');
      tryAddKonvaShapeNode(konvaShapeSpecAt(shapeKey, left, top));
      return;
    }
    const f = fabricRef.current;
    if (!f) return;
    setActiveTool('shapes');
    const { fabric } = require('fabric');
    const style = getShapeStyle();
    const w = template.width;
    const h = template.height;
    const clampX = (x, bw) => Math.max(0, Math.min(x, w - bw));
    const clampY = (y, bh) => Math.max(0, Math.min(y, h - bh));
    const centerX = (x, bw) => clampX(x - bw / 2, bw);
    const centerY = (y, bh) => clampY(y - bh / 2, bh);
    let obj;
    switch (shapeKey) {
      case 'rect':
        obj = new fabric.Rect({ left: centerX(left, 120), top: centerY(top, 80), width: 120, height: 80, fill: style.fill, stroke: style.stroke });
        break;
      case 'circle':
        obj = new fabric.Circle({ left: Math.max(50, Math.min(left, w - 50)), top: Math.max(50, Math.min(top, h - 50)), originX: 'center', originY: 'center', radius: 50, fill: style.fill, stroke: style.stroke });
        break;
      case 'triangle':
        obj = new fabric.Triangle({ left: centerX(left, 100), top: centerY(top, 90), width: 100, height: 90, fill: style.fill, stroke: style.stroke });
        break;
      case 'star':
        obj = new fabric.Polygon(
          [ { x: 50, y: 0 }, { x: 61, y: 35 }, { x: 98, y: 35 }, { x: 68, y: 57 }, { x: 79, y: 92 }, { x: 50, y: 72 }, { x: 21, y: 92 }, { x: 32, y: 57 }, { x: 2, y: 35 }, { x: 39, y: 35 } ],
          { left: centerX(left, 100), top: centerY(top, 92), fill: style.fill, stroke: style.stroke }
        );
        break;
      case 'line':
        obj = new fabric.Line([0, 0, 200, 0], { left: centerX(left, 200), top: centerY(top, 3), stroke: shapeStrokeColor, strokeWidth: 3 });
        break;
      case 'dottedLine':
        obj = new fabric.Line([0, 0, 200, 0], {
          left: centerX(left, 200),
          top: centerY(top, 3),
          stroke: shapeStrokeColor,
          strokeWidth: 3,
          strokeDashArray: [10, 8],
          fill: '',
        });
        break;
      case 'arrow':
        const line = new fabric.Line([0, 0, 140, 0], { stroke: shapeStrokeColor, strokeWidth: 3 });
        const arrowHead = new fabric.Triangle({ width: 20, height: 20, fill: shapeStrokeColor, angle: 90, left: 140, top: -10 });
        obj = new fabric.Group([line, arrowHead], { left: centerX(left, 160), top: centerY(top, 20) });
        break;
      case 'roundedRect':
        obj = new fabric.Rect({ left: centerX(left, 120), top: centerY(top, 80), width: 120, height: 80, fill: style.fill, stroke: style.stroke, rx: 12, ry: 12 });
        break;
      case 'ellipse':
        obj = new fabric.Ellipse({ left: centerX(left, 120), top: centerY(top, 80), rx: 60, ry: 40, fill: style.fill, stroke: style.stroke });
        break;
      case 'pentagon': {
        const cx = 50, cy = 50, r = 45;
        const points = [];
        for (let i = 0; i < 5; i++) {
          const a = (i * 72 - 90) * (Math.PI / 180);
          points.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
        }
        obj = new fabric.Polygon(points, { left: centerX(left, 100), top: centerY(top, 100), fill: style.fill, stroke: style.stroke });
        break;
      }
      case 'hexagon': {
        const cx = 50, cy = 50, r = 45;
        const points = [];
        for (let i = 0; i < 6; i++) {
          const a = (i * 60 - 90) * (Math.PI / 180);
          points.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
        }
        obj = new fabric.Polygon(points, { left: centerX(left, 100), top: centerY(top, 100), fill: style.fill, stroke: style.stroke });
        break;
      }
      case 'diamond': {
        const points = [ { x: 50, y: 0 }, { x: 100, y: 50 }, { x: 50, y: 100 }, { x: 0, y: 50 } ];
        obj = new fabric.Polygon(points, { left: centerX(left, 100), top: centerY(top, 100), fill: style.fill, stroke: style.stroke });
        break;
      }
      case 'cross': {
        const v = new fabric.Rect({ width: 24, height: 80, left: 28, top: 0, fill: style.fill, stroke: style.stroke });
        const h = new fabric.Rect({ width: 80, height: 24, left: 0, top: 28, fill: style.fill, stroke: style.stroke });
        obj = new fabric.Group([v, h], { left: centerX(left, 80), top: centerY(top, 80) });
        break;
      }
      case 'heart':
        obj = new fabric.Path('M 50 85 C 10 50 0 10 50 35 C 100 10 90 50 50 85 Z', { left: centerX(left, 90), top: centerY(top, 85), fill: style.fill, stroke: style.stroke, scaleX: 0.9, scaleY: 0.9 });
        break;
      case 'rightTriangle':
        obj = new fabric.Triangle({ left: centerX(left, 100), top: centerY(top, 90), width: 100, height: 90, fill: style.fill, stroke: style.stroke, angle: 0 });
        break;
      case 'crescent':
        obj = new fabric.Path('M 50 0 A 50 50 0 1 1 49.99 0 A 40 40 0 1 0 50 0 Z', { left: centerX(left, 100), top: centerY(top, 100), fill: style.fill, stroke: style.stroke });
        break;
      case 'ring': {
        const outer = new fabric.Circle({ radius: 50, fill: style.fill, stroke: style.stroke });
        const inner = new fabric.Circle({ radius: 35, fill: '#ffffff', stroke: '#ffffff', left: 15, top: 15 });
        obj = new fabric.Group([outer, inner], { left: centerX(left, 100), top: centerY(top, 100) });
        break;
      }
      case 'semicircle':
        obj = new fabric.Path('M 40 0 A 40 40 0 0 1 40 80 L 0 80 L 0 0 Z', { left: centerX(left, 80), top: centerY(top, 80), fill: style.fill, stroke: style.stroke });
        break;
      case 'blob':
        obj = new fabric.Path('M 50 10 Q 90 10 90 50 Q 90 90 50 90 Q 10 90 10 50 Q 10 10 50 10 Z', { left: centerX(left, 100), top: centerY(top, 100), fill: style.fill, stroke: style.stroke });
        break;
      case 'wave':
        obj = new fabric.Path('M 0 40 Q 25 0 50 40 T 100 40', { left: centerX(left, 100), top: centerY(top, 40), stroke: style.stroke, strokeWidth: 4, fill: 'transparent' });
        break;
      case 'tear':
        obj = new fabric.Path('M 50 0 Q 100 40 50 100 Q 0 40 50 0 Z', { left: centerX(left, 100), top: centerY(top, 100), fill: style.fill, stroke: style.stroke });
        break;
      case 'octagon': {
        const cx = 50;
        const cy = 50;
        const r = 45;
        const points = [];
        for (let i = 0; i < 8; i++) {
          const a = (i * 45 - 90) * (Math.PI / 180);
          points.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
        }
        obj = new fabric.Polygon(points, { left: centerX(left, 100), top: centerY(top, 100), fill: style.fill, stroke: style.stroke });
        break;
      }
      case 'trapezoid': {
        const points = [{ x: 20, y: 0 }, { x: 80, y: 0 }, { x: 100, y: 80 }, { x: 0, y: 80 }];
        obj = new fabric.Polygon(points, { left: centerX(left, 100), top: centerY(top, 80), fill: style.fill, stroke: style.stroke });
        break;
      }
      case 'parallelogram': {
        const points = [{ x: 30, y: 0 }, { x: 130, y: 0 }, { x: 100, y: 70 }, { x: 0, y: 70 }];
        obj = new fabric.Polygon(points, { left: centerX(left, 130), top: centerY(top, 70), fill: style.fill, stroke: style.stroke });
        break;
      }
      case 'lightning':
        obj = new fabric.Path('M 45 5 L 20 45 L 35 45 L 12 88 L 58 38 L 40 38 Z', {
          left: centerX(left, 58),
          top: centerY(top, 83),
          fill: style.fill,
          stroke: style.stroke,
        });
        break;
      case 'chevron': {
        const pts = [
          { x: 0, y: 50 },
          { x: 45, y: 10 },
          { x: 45, y: 35 },
          { x: 100, y: 35 },
          { x: 100, y: 65 },
          { x: 45, y: 65 },
          { x: 45, y: 90 },
        ];
        obj = new fabric.Polygon(pts, {
          left: centerX(left, 100),
          top: centerY(top, 90),
          fill: style.fill,
          stroke: style.stroke,
        });
        break;
      }
      case 'doubleArrow': {
        const line = new fabric.Line([20, 0, 180, 0], { stroke: shapeStrokeColor, strokeWidth: 3 });
        const leftH = new fabric.Triangle({
          width: 20,
          height: 20,
          left: 10,
          top: -10,
          angle: -90,
          fill: shapeStrokeColor,
          stroke: shapeStrokeColor,
        });
        const rightH = new fabric.Triangle({
          width: 20,
          height: 20,
          left: 170,
          top: -10,
          angle: 90,
          fill: shapeStrokeColor,
          stroke: shapeStrokeColor,
        });
        obj = new fabric.Group([line, leftH, rightH], { left: centerX(left, 190), top: centerY(top, 20) });
        break;
      }
      case 'cloud':
        obj = new fabric.Path(
          'M 25 60 C 8 60 8 38 24 32 C 22 14 42 10 55 22 C 65 8 92 12 98 32 C 118 34 124 52 108 62 C 102 72 35 72 25 60 Z',
          { left: centerX(left, 120), top: centerY(top, 72), fill: style.fill, stroke: style.stroke }
        );
        break;
      case 'speechBubble':
        obj = new fabric.Path(
          'M 14 10 Q 14 4 22 4 L 108 4 Q 116 4 116 12 L 116 54 Q 116 62 108 62 L 50 62 L 32 82 L 40 62 L 22 62 Q 14 62 14 54 Z',
          { left: centerX(left, 118), top: centerY(top, 84), fill: style.fill, stroke: style.stroke }
        );
        break;
      case 'zigzag':
        obj = new fabric.Polyline(
          [
            { x: 0, y: 24 },
            { x: 24, y: 0 },
            { x: 48, y: 24 },
            { x: 72, y: 0 },
            { x: 96, y: 24 },
            { x: 120, y: 0 },
            { x: 144, y: 24 },
          ],
          {
            left: centerX(left, 144),
            top: centerY(top, 24),
            stroke: shapeStrokeColor,
            strokeWidth: 4,
            fill: '',
            strokeLineCap: 'round',
            strokeLineJoin: 'round',
          }
        );
        break;
      default:
        return;
    }
    f.add(obj).setActiveObject(obj);
    f.renderAll();
    setLayersKey((k) => k + 1);
  }

  function addImageAtCanvasPoint(dataUrl, x, y) {
    if (USE_KONVA_EDITOR) {
      placeKonvaImageFromSrc(dataUrl, x, y);
      return;
    }
    const { fabric } = require('fabric');
    fabric.Image.fromURL(
      dataUrl,
      (img, isError) => {
        if (!fabricRef.current || isError || !img) {
          setToastMessage('Could not read that image file.');
          return;
        }
        placeFabricImageOnCanvas(img, x, y);
        fabricRef.current.renderAll();
      },
      fabricImageFromUrlOptions(dataUrl),
    );
  }

  function handleCanvasDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    const wrapper = canvasWrapperRef.current;
    if (!wrapper) return;
    const rect = wrapper.getBoundingClientRect();
    const zoom = Number.isFinite(zoomScale) ? zoomScale : 1;
    const pointer = {
      x: (e.clientX - rect.left) / zoom,
      y: (e.clientY - rect.top) / zoom,
    };
    if (USE_KONVA_EDITOR) {
      const shapeKey = e.dataTransfer.getData('application/x-shape-key');
      if (shapeKey) {
        addShapeAt(shapeKey, pointer.x, pointer.y);
        return;
      }
      const gcpObjectKey = e.dataTransfer.getData('application/x-gcp-object-key');
      if (gcpObjectKey) {
        const libItem = gcpLibrary.items.find((it) => it.objectKey === gcpObjectKey);
        loadGcpImageForCanvas(libItem || { objectKey: gcpObjectKey })
          .then((url) => placeKonvaImageFromSrc(url, pointer.x, pointer.y, gcpObjectKey))
          .catch((err) => setToastMessage(err?.message || 'Could not load image from library.'));
        return;
      }
      const imageUrl = e.dataTransfer.getData('application/x-image-url');
      if (imageUrl) {
        placeKonvaImageFromSrc(imageUrl, pointer.x, pointer.y);
        return;
      }
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        Array.from(files).forEach((file, i) => {
          if (!String(file.type || '').startsWith('image/')) return;
          const reader = new FileReader();
          reader.onload = (fr) => {
            addImageAtCanvasPoint(fr.target.result, pointer.x + i * 16, pointer.y + i * 16);
          };
          reader.readAsDataURL(file);
        });
      }
      return;
    }
    const f = fabricRef.current;
    if (!f) return;
    const fabricPointer = f.getPointer(e);

    const shapeKey = e.dataTransfer.getData('application/x-shape-key');
    if (shapeKey) {
      addShapeAt(shapeKey, fabricPointer.x, fabricPointer.y);
      return;
    }

    const imageUrl = e.dataTransfer.getData('application/x-image-url');
    if (imageUrl) {
      const { fabric } = require('fabric');
      fabric.Image.fromURL(
        imageUrl,
        (img, isError) => {
          if (!fabricRef.current || isError || !img) return;
          placeFabricImageOnCanvas(img, fabricPointer.x, fabricPointer.y);
          fabricRef.current.renderAll();
        },
        fabricImageFromUrlOptions(imageUrl),
      );
      return;
    }

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      Array.from(files).forEach((file, i) => {
        if (!String(file.type || '').startsWith('image/')) return;
        const reader = new FileReader();
        reader.onload = (fr) => {
          addImageAtCanvasPoint(fr.target.result, fabricPointer.x + i * 16, fabricPointer.y + i * 16);
        };
        reader.readAsDataURL(file);
      });
    }
  }

  function handleCanvasDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }

  /**
   * Options for fabric.Image.fromURL. Do not force crossOrigin on cross-origin URLs unless the
   * host sends CORS — e.g. GCS signed URLs often omit it, which yields a "loaded" 0×0 image
   * (only selection handles visible on the canvas).
   */
  function fabricImageFromUrlOptions(url) {
    if (typeof window === 'undefined') return {};
    try {
      const u = new URL(String(url || ''), window.location.href);
      if (u.protocol === 'data:' || u.protocol === 'blob:') return {};
      if (u.origin === window.location.origin) return {};
    } catch {
      return {};
    }
    return {};
  }

  function getFabricImagePixelSize(img) {
    if (!img) return { width: 0, height: 0 };
    if (typeof img.getOriginalSize === 'function') {
      const o = img.getOriginalSize();
      const w = Number(o && o.width) || 0;
      const h = Number(o && o.height) || 0;
      if (w > 0 && h > 0) return { width: w, height: h };
    }
    const el = typeof img.getElement === 'function' ? img.getElement() : null;
    if (el && (el.naturalWidth > 0 || el.width > 0)) {
      return {
        width: el.naturalWidth || el.width || 0,
        height: el.naturalHeight || el.height || 0,
      };
    }
    return {
      width: Math.max(0, Number(img.width) || 0),
      height: Math.max(0, Number(img.height) || 0),
    };
  }

  /** Scale image to fit inside the page (with inset) and place at canvas center. */
  function placeFabricImageOnCanvas(img, left, top) {
    const f = fabricRef.current;
    if (!f || !img) return false;
    const cw = f.getWidth();
    const ch = f.getHeight();
    const { width: iw0, height: ih0 } = getFabricImagePixelSize(img);
    if (!iw0 || !ih0) {
      setToastMessage('Image did not load. Try another file or pick it again from the library.');
      return false;
    }
    const iw = Math.max(1, iw0);
    const ih = Math.max(1, ih0);
    const inset = Math.min(cw, ch) * 0.08;
    const maxW = Math.max(1, cw - inset * 2);
    const maxH = Math.max(1, ch - inset * 2);
    const fit = Math.min(maxW / iw, maxH / ih);
    const scale = Math.min(fit, 48);
    img.set({
      scaleX: scale,
      scaleY: scale,
      originX: 'center',
      originY: 'center',
      left: left ?? cw / 2,
      top: top ?? ch / 2,
    });
    f.add(img).setActiveObject(img);
    if (typeof img.setCoords === 'function') img.setCoords();
    return true;
  }

  function addFabricImageToCanvasCentered(img) {
    const f = fabricRef.current;
    if (!f || !img) return false;
    return placeFabricImageOnCanvas(img, f.getWidth() / 2, f.getHeight() / 2);
  }

  function addImageFile(e, asBackground = false) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (USE_KONVA_EDITOR) {
      const reader = new FileReader();
      reader.onload = (fr) => {
        const data = fr.target?.result;
        if (!data) {
          setToastMessage('Could not read that image file.');
          return;
        }
        if (asBackground) {
          setBgType('image');
          setBgImageUrl(data);
          setToastMessage('Background image set.');
        } else {
          placeKonvaImageFromSrc(data, template.width / 2, template.height / 2);
        }
      };
      reader.readAsDataURL(file);
      e.target.value = null;
      return;
    }
    const reader = new FileReader();
    reader.onload = function (fr) {
      const data = fr.target.result;
      const { fabric } = require('fabric');
      fabric.Image.fromURL(
        data,
        (img, isError) => {
          const f = fabricRef.current;
          if (!f || isError || !img) {
            setToastMessage('Could not read that image file.');
            return;
          }
          if (asBackground) {
            img.set({ left: 0, top: 0, selectable: false, evented: false });
            img.scaleToWidth(f.getWidth());
            img.scaleToHeight(f.getHeight());
            f.setBackgroundImage(img, f.renderAll.bind(f));
          } else {
            addFabricImageToCanvasCentered(img);
          }
          f.renderAll();
        },
        fabricImageFromUrlOptions(data)
      );
    };
    reader.readAsDataURL(file);
    e.target.value = null;
  }

  function addGcpImageFromLibrary(item) {
    if (!item?.objectKey) return;
    const place = (url) => {
      if (USE_KONVA_EDITOR) {
        placeKonvaImageFromSrc(url, template.width / 2, template.height / 2, item.objectKey);
      } else {
        addImageFromUrl(url, false);
      }
    };
    loadGcpImageForCanvas(item)
      .then(place)
      .catch((err) => setToastMessage(err?.message || 'Could not load image from library.'));
  }

  function addImageFromUrl(url, asBackground = false) {
    if (!url) return alert('Provide image URL');
    if (USE_KONVA_EDITOR) {
      if (asBackground) {
        setBgType('image');
        setBgImageUrl(url);
        setToastMessage('Background image set.');
      } else {
        placeKonvaImageFromSrc(url, template.width / 2, template.height / 2);
      }
      return;
    }
    const { fabric } = require('fabric');
    fabric.Image.fromURL(
      url,
      (img, isError) => {
        const f = fabricRef.current;
        if (!f || isError || !img) {
          setToastMessage('Could not load image from URL.');
          return;
        }
        if (asBackground) {
          img.set({ left: 0, top: 0, selectable: false, evented: false });
          img.scaleToWidth(f.getWidth());
          img.scaleToHeight(f.getHeight());
          f.setBackgroundImage(img, f.renderAll.bind(f));
        } else {
          addFabricImageToCanvasCentered(img);
        }
        f.renderAll();
      },
      fabricImageFromUrlOptions(url)
    );
  }

  const refreshGcpLibrary = useCallback(async () => {
    setGcpLibraryLoading(true);
    setGcpLibraryError(null);
    try {
      const data = await listGcpImages();
      seedGcpSignedUrlCache(data.items || []);
      setGcpLibrary({
        items: data.items || [],
        count: data.count ?? 0,
        max: data.max ?? GCP_IMAGES_MAX,
      });
      return data;
    } catch (err) {
      setGcpLibraryError(err.message || 'Could not load GCP image library');
      setGcpLibrary({ items: [], count: 0, max: GCP_IMAGES_MAX });
      return null;
    } finally {
      setGcpLibraryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTool !== 'images') return;
    refreshGcpLibrary();
  }, [activeTool, refreshGcpLibrary]);

  function isGcpImageLimitError(message) {
    const m = String(message || '').toLowerCase();
    return m.includes('maximum') && m.includes('image') && m.includes('reached');
  }

  function openLibraryGcpFilePicker() {
    if (gcpLibraryLoading) return;
    if (gcpLibrary.count >= gcpLibrary.max) {
      setGcpLimitModalOpen(true);
      return;
    }
    gcpUploadInputRef.current?.click();
  }

  async function handleLibraryGcpFileChange(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = null;
    if (!file || !String(file.type || '').startsWith('image/')) return;
    if (gcpLibrary.count >= gcpLibrary.max) {
      setGcpLimitModalOpen(true);
      return;
    }
    const scrollEl = libraryScrollRef.current;
    const savedScrollTop = scrollEl ? scrollEl.scrollTop : 0;
    setGcpLibraryLoading(true);
    setGcpLibraryError(null);
    try {
      await uploadGcpImageFile(file);
      await refreshGcpLibrary();
      setToastMessage('Image saved to cloud library. Click a thumbnail below to add it to the canvas.');
    } catch (err) {
      const msg = err.message || 'Upload failed';
      if (isGcpImageLimitError(msg)) {
        setGcpLimitModalOpen(true);
        await refreshGcpLibrary();
      } else {
        setGcpLibraryError(msg);
      }
    } finally {
      setGcpLibraryLoading(false);
      requestAnimationFrame(() => {
        const el = libraryScrollRef.current;
        if (el) el.scrollTop = savedScrollTop;
      });
    }
  }

  function updateSelectedTextProps(newProps) {
    if (USE_KONVA_EDITOR) {
      const id = useEditorStore.getState().selectedId;
      if (!id) return;
      const partial = {};
      if (newProps.text != null) partial.text = newProps.text;
      const typo = {};
      if (newProps.fontSize != null) typo.fontSize = newProps.fontSize;
      if (newProps.fontFamily != null) typo.fontFamily = newProps.fontFamily;
      if (newProps.fill != null) typo.fill = newProps.fill;
      if (newProps.fontWeight != null) typo.fontWeight = newProps.fontWeight;
      if (newProps.fontStyle != null) typo.fontStyle = newProps.fontStyle;
      if (newProps.underline != null) typo.underline = newProps.underline;
      if (newProps.textAlign != null) typo.textAlign = newProps.textAlign;
      if (Object.keys(typo).length) partial.typography = typo;
      useEditorStore.getState().updateTextNode(id, partial);
      const node = useEditorStore.getState().getObjectById(id);
      if (node) handleKonvaSelection(node);
      setTextProps((prev) => ({ ...prev, ...newProps }));
      pushKonvaHistory();
      return;
    }
    const canvas = fabricRef.current;
    const raw = canvas?.getActiveObject();
    if (!raw) return;
    const inner = getInnerShapeLabelText(raw);
    const obj =
      resolveEditorTextBoxLabel(raw) ||
      (raw.type === 'i-text' || raw.type === 'textbox' || raw.type === 'text' ? raw : inner);
    if (!obj) return;
    if (newProps.fontSize != null && obj.type === 'textbox') {
      const oldFs = obj.fontSize || 12;
      const n = Number(newProps.fontSize);
      if (Number.isFinite(n) && n > 0) {
        const r = n / oldFs;
        if (Math.abs(r - 1) > 0.02 && obj.width) {
          obj.set({ width: Math.max(24, Math.round(obj.width * r)) });
        }
      }
    }
    if (newProps.text != null && !isShapeLabelPlaceholderText(newProps.text)) {
      delete obj._isShapePlaceholder;
      if (obj.fill === '#64748b') obj.set({ fill: '#111827' });
    }
    obj.set(newProps);
    obj.setCoords();
    if (obj.type === 'textbox') {
      ensureTextObjectVisible(obj);
      obj.__lastSyncedTextboxWidth = obj.width;
      const parent = obj.group;
      if (isTextBoxLabel(obj) || isTextBoxFrame(resolveTextBoxFrame(obj))) {
        syncTextBoxGroupAfterTextEdit(canvas, obj);
      }
      if (parent && parent._shapeLabelGroup) fitShapeLabelInGroup(parent, canvas);
    }
    const active = canvas?.getActiveObject();
    if (active && (active === raw || active === obj)) applyCanvasSelectionHighlight(active);
    canvas?.renderAll();
    setTextProps((prev) => ({ ...prev, ...newProps }));
  }

  function setObjectDynamicKey() {
    const keyVal = dynamicKey.trim() || undefined;
    if (USE_KONVA_EDITOR) {
      const id = useEditorStore.getState().selectedId;
      const node = id ? useEditorStore.getState().getObjectById(id) : null;
      if (!node || node.type !== 'text') return alert('Select a text box first');
      const fc = keyVal ? snapshotKonvaTextFieldConfig(node) : undefined;
      useEditorStore.getState().updateTextNode(id, { fieldKey: keyVal, fieldConfig: fc });
      handleKonvaSelection(useEditorStore.getState().getObjectById(id));
      setLayersKey((n) => n + 1);
      return;
    }
    const f = fabricRef.current;
    const raw = f?.getActiveObject();
    const obj = getPlaceholderTargetFromSelection(f);
    if (!obj) return alert('Select an object first');
    obj.fieldKey = keyVal;
    if (!keyVal) obj.fieldConfig = undefined;
    const frame = resolveTextBoxFrame(raw) || (isTextBoxFrame(raw) ? raw : null);
    if (frame) {
      frame.fieldKey = keyVal;
      frame.fieldConfig = keyVal ? snapshotTextFieldConfig(obj) : undefined;
    }
    if (keyVal && (obj.type === 'i-text' || obj.type === 'textbox' || obj.type === 'text')) {
      obj.fieldConfig = snapshotTextFieldConfig(obj);
    }
    const hi = f.getActiveObject();
    if (hi) applyCanvasSelectionHighlight(hi);
    f.renderAll();
    setLayersKey((n) => n + 1);
  }

  function updateField(canvas, obj, value, cfg) {
    const str = String(value);
    const isText = obj.type === 'i-text' || obj.type === 'textbox' || obj.type === 'text';
    if (!isText) return;
    if (str && obj._isShapePlaceholder) {
      delete obj._isShapePlaceholder;
      if (obj.fill === '#64748b') obj.set({ fill: '#111827' });
    }
    let merged = { ...(cfg || {}) };
    if (!merged.maxWidth || !merged.maxHeight) {
      const snap = snapshotTextFieldConfig(obj);
      if (snap) merged = { ...snap, ...merged };
    }
    applyTextLayout(canvas, obj, { text: str, config: merged });
  }

  async function assignPlaceholderToSelected(key) {
    if (USE_KONVA_EDITOR) {
      const st = useEditorStore.getState();
      const id = st.selectedId || st.selectedIds[0];
      const node = id ? st.getObjectById(id) : null;
      if (!node) return;
      if (node.type === 'text') {
        if (key) {
          const fc = snapshotKonvaTextFieldConfig(node);
          useEditorStore.getState().updateTextNode(id, { fieldKey: key, fieldConfig: fc });
        } else {
          useEditorStore.getState().updateTextNode(id, { fieldKey: undefined, fieldConfig: undefined });
        }
      } else if (node.type === 'image') {
        const t = node.transform || {};
        if (key) {
          const fc = {
            maxWidth: Math.max(1, t.width || 100),
            maxHeight: Math.max(1, t.height || 100),
            behavior: 'none',
          };
          useEditorStore.getState().updateImageNode(id, { fieldKey: key, fieldConfig: fc });
        } else {
          useEditorStore.getState().updateImageNode(id, { fieldKey: undefined, fieldConfig: undefined });
        }
      } else {
        return;
      }
      const updated = useEditorStore.getState().getObjectById(id);
      handleKonvaSelection(updated);
      setDynamicKey(key || '');
      pushKonvaHistory();
      setLayersKey((k) => k + 1);
      setPlaceholderDropdownOpen(false);
      return;
    }
    const f = fabricRef.current;
    const obj = getPlaceholderTargetFromSelection(f);
    if (!obj || !selectedObj) return;
    if (key) {
      obj.set?.('fieldKey', key);
      obj.fieldKey = key;
    } else {
      obj.set?.('fieldKey', undefined);
      obj.set?.('fieldConfig', undefined);
      obj.fieldKey = undefined;
      obj.fieldConfig = undefined;
    }
    if (key && (obj.type === 'i-text' || obj.type === 'textbox' || obj.type === 'text')) {
      const fc = snapshotTextFieldConfig(obj);
      obj.fieldConfig = fc;
      obj.set?.('fieldConfig', fc);
    } else if (key && obj.type === 'image') {
      obj.setCoords();
      const rect = obj.getBoundingRect();
      const fc = { maxWidth: rect.width, maxHeight: rect.height, behavior: 'none' };
      obj.fieldConfig = fc;
      obj.set?.('fieldConfig', fc);
    }
    const highlightTarget = f.getActiveObject();
    if (highlightTarget) applyCanvasSelectionHighlight(highlightTarget);
    f.renderAll();
    setDynamicKey(key || '');
    setLayersKey((k) => k + 1);
    setPlaceholderDropdownOpen(false);
  }

  function addShapeByKey(shapeKey) {
    if (!USE_KONVA_EDITOR) return false;
    setActiveTool('shapes');
    const spec = getKonvaShapeSpec(shapeKey);
    if (!spec) {
      setToastMessage('This shape is not available in the Konva editor yet.');
      return true;
    }
    return tryAddKonvaShapeNode(spec);
  }

  function tryAddKonvaShapeNode(spec) {
    if (!USE_KONVA_EDITOR) return false;
    const style = getShapeStyle();
    const id = useEditorStore.getState().addShape({
      ...spec,
      fill: spec.fill ?? style.fill,
      stroke: spec.stroke ?? style.stroke,
    });
    useEditorStore.getState().selectObject(id);
    const added = useEditorStore.getState().getObjectById(id);
    if (added) handleKonvaSelection(added);
    pushKonvaHistory();
    setLayersKey((k) => k + 1);
    return true;
  }

  function applyKonvaImageCrossOrigin(img, src) {
    const srcStr = String(src || '');
    if (isGcpDisplayOnlyUrl(srcStr)) return;
    if (!/^https?:\/\//i.test(srcStr)) return;
    try {
      const u = new URL(srcStr, window.location.href);
      if (u.origin !== window.location.origin) img.crossOrigin = 'anonymous';
    } catch {
      img.crossOrigin = 'anonymous';
    }
  }

  function placeKonvaImageFromSrc(src, left, top, gcpObjectKey) {
    const persistSrc = gcpObjectKey ? gcpImageStreamUrl(gcpObjectKey) : src;
    const srcStr = String(src || '').trim();
    const loadPromise =
      srcStr && isGcpDisplayOnlyUrl(srcStr)
        ? Promise.resolve(srcStr)
        : resolveGcpImageForCanvas(srcStr || persistSrc, gcpObjectKey);

    loadPromise
      .then((displaySrc) => {
        const img = new window.Image();
        applyKonvaImageCrossOrigin(img, displaySrc);
        img.onload = () => {
          const id = useEditorStore.getState().addImage({
            src: persistSrc,
            objectKey: gcpObjectKey,
            left,
            top,
            naturalWidth: img.naturalWidth || img.width,
            naturalHeight: img.naturalHeight || img.height,
          });
          const node = useEditorStore.getState().getObjectById(id);
          if (node) handleKonvaSelection(node);
          pushKonvaHistory();
          setLayersKey((k) => k + 1);
          warmSceneFontsLocally(
            useEditorStore.getState().scene.objects,
            konvaStageRef.current,
          );
        };
        img.onerror = () =>
          setToastMessage('Could not load image. Check that the flyer API is running and GCP storage is configured.');
        img.src = displaySrc;
      })
      .catch(() =>
        setToastMessage('Could not load image. Check that the flyer API is running and GCP storage is configured.'),
      );
  }

  /** Remove one canvas object, the current selection, or all objects in an active multiselection. */
  function deleteCanvasObject(obj) {
    if (USE_KONVA_EDITOR) {
      const ids = obj?.id
        ? [obj.id]
        : useEditorStore.getState().selectedIds.length
          ? [...useEditorStore.getState().selectedIds]
          : useEditorStore.getState().selectedId
            ? [useEditorStore.getState().selectedId]
            : [];
      if (!ids.length) return;
      ids.forEach((id) => useEditorStore.getState().removeObject(id));
      setSelectedObj(null);
      setSelectionBounds(null);
      pushKonvaHistory();
      setLayersKey((k) => k + 1);
      return;
    }
    const f = fabricRef.current;
    const target = obj !== undefined && obj !== null ? obj : f?.getActiveObject?.();
    if (!f || !target) return;
    if (target.type === 'activeSelection') {
      const objs = target.getObjects?.() ? [...target.getObjects()] : [];
      f.discardActiveObject();
      suppressObjectRemovedHistoryRef.current = true;
      try {
        objs.forEach((o) => f.remove(o));
      } finally {
        suppressObjectRemovedHistoryRef.current = false;
      }
      pushHistoryCanvasRef.current();
      setSelectedObj(null);
      setSelectionBounds(null);
      f.requestRenderAll();
      setLayersKey((k) => k + 1);
      return;
    }
    try {
      if (typeof target.exitEditing === 'function' && target.isEditing) target.exitEditing();
    } catch (_) {}
    if (f.getActiveObject() === target) f.discardActiveObject();
    f.remove(target);
    setSelectedObj(null);
    setSelectionBounds(null);
    f.requestRenderAll();
    setLayersKey((k) => k + 1);
  }

  function copySelected() {
    if (USE_KONVA_EDITOR) {
      const ids = useEditorStore.getState().selectedIds;
      if (!ids.length) return;
      const nodes = ids
        .map((id) => useEditorStore.getState().getObjectById(id))
        .filter(Boolean);
      if (!nodes.length) return;
      setClipboard({ __konva: true, nodes: JSON.parse(JSON.stringify(nodes)) });
      return;
    }

    const canvas = fabricRef.current;
    const obj = canvas?.getActiveObject();
    if (!obj) return;
    const props = ['fieldKey', 'fieldConfig', '_shapeLabelGroup', TEXT_BOX_GROUP_FLAG, TEXT_BOX_PAIR_ID, TEXT_BOX_FRAME_FLAG, TEXT_BOX_LABEL_FLAG, '_boxWidth', '_boxHeight'];
    if (obj.type === 'activeSelection' && typeof obj.getObjects === 'function') {
      setClipboard({
        __multi: true,
        objects: obj.getObjects().map((o) => o.toObject(props)),
      });
      return;
    }
    obj.clone((cloned) => {
      setClipboard(cloned.toJSON(props));
    });
  }

  function pasteFromClipboard() {
    if (USE_KONVA_EDITOR) {
      if (!clipboard?.__konva || !Array.isArray(clipboard.nodes) || !clipboard.nodes.length) return;
      const PASTE_OFFSET = 20;
      const store = useEditorStore.getState();
      const currentObjects = store.scene.objects;
      const newNodes = clipboard.nodes.map((node, i) => {
        const prefix = node.type === 'text' ? 'text' : node.type === 'image' ? 'img' : 'shape';
        const newId = `${prefix}_paste_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 6)}`;
        return {
          ...JSON.parse(JSON.stringify(node)),
          id: newId,
          zIndex: currentObjects.length + i,
          transform: {
            ...node.transform,
            x: (node.transform?.x || 0) + PASTE_OFFSET,
            y: (node.transform?.y || 0) + PASTE_OFFSET,
          },
          layout: undefined,
        };
      });
      const newScene = {
        ...store.scene,
        objects: [...currentObjects, ...newNodes],
      };
      store.setScene(newScene);
      const newIds = newNodes.map((n) => n.id);
      store.setSelection(newIds);
      const firstNode = store.getObjectById(newIds[0]);
      if (firstNode) handleKonvaSelection(firstNode);
      pushKonvaHistory();
      setLayersKey((k) => k + 1);
      return;
    }

    if (!clipboard || !fabricRef.current) return;
    const { fabric } = require('fabric');
    const canvas = fabricRef.current;
    const props = ['fieldKey', 'fieldConfig', '_shapeLabelGroup', TEXT_BOX_GROUP_FLAG, TEXT_BOX_PAIR_ID, TEXT_BOX_FRAME_FLAG, TEXT_BOX_LABEL_FLAG, '_boxWidth', '_boxHeight'];

    if (clipboard.__multi && Array.isArray(clipboard.objects) && clipboard.objects.length > 0) {
      fabric.util.enlivenObjects(clipboard.objects, (objs) => {
        if (!objs?.length) return;
        objs.forEach((o, i) => {
          o.set({ left: (o.left || 0) + 20 + i * 8, top: (o.top || 0) + 20 + i * 8 });
          canvas.add(o);
        });
        if (objs.length === 1) {
          canvas.setActiveObject(objs[0]);
        } else {
          const sel = new fabric.ActiveSelection(objs, { canvas });
          canvas.setActiveObject(sel);
        }
        canvas.requestRenderAll();
        setLayersKey((k) => k + 1);
      }, '');
      return;
    }

    fabric.util.enlivenObjects([clipboard], (objs) => {
      const obj = objs[0];
      if (!obj) return;
      obj.set({ left: (obj.left || 0) + 20, top: (obj.top || 0) + 20 });
      canvas.add(obj).setActiveObject(obj);
      canvas.requestRenderAll();
      setLayersKey((k) => k + 1);
    }, '');
  }

  const canvasKeyActionsRef = useRef({});
  canvasKeyActionsRef.current = { undo, redo, copySelected, pasteFromClipboard };

  useEffect(() => {
    function onKeyDown(e) {
      if (previewRef.current) return;
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      const mod = e.metaKey || e.ctrlKey;
      const f = fabricRef.current;
      const active = f?.getActiveObject?.();
      const key = (e.key || '').toLowerCase();

      if (!mod && (key === 'delete' || key === 'backspace')) {
        if (active?.isEditing) return;
        if (USE_KONVA_EDITOR) {
          const editingId = useEditorStore.getState().editingTextId;
          if (editingId) return;
          const selIds = useEditorStore.getState().selectedIds;
          if (selIds.length) { e.preventDefault(); deleteCanvasObject(); }
        } else if (active) {
          e.preventDefault();
          deleteCanvasObject();
        }
        return;
      }

      if (!mod) return;
      if (!USE_KONVA_EDITOR && active?.isEditing && (key === 'z' || key === 'y' || key === 'c' || key === 'v')) return;
      const { undo: u, redo: r, copySelected: cp, pasteFromClipboard: pa } = canvasKeyActionsRef.current;
      if (key === 'z' && !e.shiftKey) {
        e.preventDefault();
        u?.();
      } else if ((key === 'z' && e.shiftKey) || key === 'y') {
        e.preventDefault();
        r?.();
      } else if (key === 'c') {
        e.preventDefault();
        cp?.();
      } else if (key === 'v') {
        e.preventDefault();
        pa?.();
      }
    }
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  useEffect(() => {
    if (!autoSaveEnabled || !currentTemplateId || previewRef.current) return undefined;
    if (USE_KONVA_EDITOR && (useEditorStore.getState().scene?.objects?.length || 0) === 0) {
      return undefined;
    }
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        await updateCurrentTemplateWithName(templateBarName || UNSAVED_TEMPLATE_LABEL, { silent: true });
      } catch (err) {
        console.warn('[auto-save]', err?.message || err);
      }
    }, 10000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [historyState.index, autoSaveEnabled, currentTemplateId, template.width, template.height, templateBarName]);

  function bumpDocumentSize(axis, direction) {
    const step = documentSizeStep(unit) * direction;
    const min = documentSizeMin(unit);
    if (axis === 'width') {
      setWidthMm((v) => Math.max(min, Math.round((Number(v) + step) * 1000) / 1000));
    } else {
      setHeightMm((v) => Math.max(min, Math.round((Number(v) + step) * 1000) / 1000));
    }
    setPresetName('Custom size');
  }

  function applyPreset(name) {
    const p = PRESETS.find((x) => x.name === name);
    setPresetName(name);
    if (!p || name === 'Custom size') return;
    if (p.widthIn != null && p.heightIn != null) {
      setWidthMm(p.widthIn);
      setHeightMm(p.heightIn);
      setUnit('Inch');
    } else if (p.widthMm != null) {
      setWidthMm(p.widthMm);
      setHeightMm(p.heightMm);
      setUnit('Millimeter');
    } else if (p.widthPx != null) {
      setWidthMm(p.widthPx);
      setHeightMm(p.heightPx);
      setUnit('Pixel');
    }
  }

  function exportTemplateJson() {
    const f = fabricRef.current;
    const json = f.toDatalessJSON(['fieldKey', 'fieldConfig', '_shapeLabelGroup', TEXT_BOX_GROUP_FLAG, TEXT_BOX_PAIR_ID, TEXT_BOX_FRAME_FLAG, TEXT_BOX_LABEL_FLAG, '_boxWidth', '_boxHeight']);
    const blob = new Blob([JSON.stringify({ template, json }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  function importTemplateJson(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = function (fr) {
      try {
        const parsed = JSON.parse(fr.target.result);
        if (parsed.template) {
          setWidthMm(pxToMm(clampTemplatePx(parsed.template.width), resolution));
          setHeightMm(pxToMm(clampTemplatePx(parsed.template.height), resolution));
        }
        if (parsed.json) {
          fabricRef.current.loadFromDatalessJSON(parsed.json, fabricRef.current.renderAll.bind(fabricRef.current));
        }
      } catch (err) {
        alert('Invalid template file');
      }
    };
    reader.readAsText(file);
    e.target.value = null;
  }

  function getMappings() {
    try {
      return JSON.parse(mappingsJson || '{}');
    } catch {
      return {};
    }
  }

  function setMapping(key, value) {
    const prev = getMappings();
    const next = { ...prev, [key]: value };
    if (value === '' || value == null) delete next[key];
    setMappingsJson(JSON.stringify(next, null, 2));
  }

  async function applyMappingsToCanvas(overrideMappings) {
    const mappings = overrideMappings !== undefined ? overrideMappings : getMappings();
    if (USE_KONVA_EDITOR) {
      useEditorStore.getState().applyMappings(mappings, lookupMappingValue);
      const id = useEditorStore.getState().selectedId;
      if (id) {
        const node = useEditorStore.getState().getObjectById(id);
        if (node) handleKonvaSelection(node);
      }
      setLayersKey((k) => k + 1);
      return;
    }
    const f = fabricRef.current;
    if (!f) return;
    await applyMappingsToFabricCanvas(f, mappings);
    setLayersKey((k) => k + 1);
  }

  async function applyDataToCanvas() {
    const mappings = getMappings();
    const f = fabricRef.current;
    if (!f) return;
    isRestoringHistoryRef.current = true;
    try {
      const json = f.toJSON(['fieldKey', 'fieldConfig', '_shapeLabelGroup', TEXT_BOX_GROUP_FLAG, TEXT_BOX_PAIR_ID, TEXT_BOX_FRAME_FLAG, TEXT_BOX_LABEL_FLAG, '_boxWidth', '_boxHeight']);
      setHistoryState((prev) => {
        const next = prev.history.slice(0, prev.index + 1);
        next.push(json);
        if (next.length > HISTORY_LIMIT) next.shift();
        return { history: next, index: next.length - 1 };
      });
    } finally {
      isRestoringHistoryRef.current = false;
    }
    await applyMappingsToFabricCanvas(f, mappings);
    setLayersKey((k) => k + 1);
  }

  function shrinkFabricTextToPdfBox(canvas, obj, boxW, boxH) {
    if (!obj || !canvas || boxW <= 0 || boxH <= 0) return;
    const cfg = obj.fieldConfig || {};
    let maxW = boxW;
    let maxH = boxH;
    if (cfg.maxWidth != null) maxW = Math.min(maxW, cfg.maxWidth);
    if (cfg.maxHeight != null) maxH = Math.min(maxH, cfg.maxHeight);
    const pad = 4;
    const w = Math.max(1, maxW - pad);
    const h = Math.max(1, maxH - pad);
    if (obj.type === 'textbox' || obj.type === 'i-text' || obj.type === 'text') {
      obj.set({ width: Math.max(1, w), splitByGrapheme: true });
    }
    canvas.renderAll();
    let r = obj.getBoundingRect(true);
    if (r.width > w && obj.fontSize) {
      const ratio = w / (r.width + 1);
      obj.set('fontSize', Math.max(6, Math.floor((obj.fontSize || 12) * ratio)));
      obj.set('width', w);
      canvas.renderAll();
    }
    r = obj.getBoundingRect(true);
    while (r.height > h && (obj.fontSize || 12) > 6) {
      obj.set('fontSize', Math.max(6, (obj.fontSize || 12) - 1));
      canvas.renderAll();
      r = obj.getBoundingRect(true);
    }
    obj.setCoords();
  }

  function capturePdfTextLayoutBoxes(canvas) {
    forEachCanvasObjectDeep(canvas, (o) => {
      if (o.type !== 'i-text' && o.type !== 'textbox' && o.type !== 'text') return;
      o.setCoords();
      const br = o.getBoundingRect(true);
      o.__pdfLayoutW = br.width;
      o.__pdfLayoutH = Math.max(8, br.height);
    });
  }

  function shrinkAllCapturedPdfTextBoxes(canvas) {
    forEachCanvasObjectDeep(canvas, (o) => {
      if (o.type !== 'i-text' && o.type !== 'textbox' && o.type !== 'text') return;
      const bw = o.__pdfLayoutW;
      const bh = o.__pdfLayoutH;
      if (!bw || !bh) return;
      shrinkFabricTextToPdfBox(canvas, o, bw, bh);
      delete o.__pdfLayoutW;
      delete o.__pdfLayoutH;
    });
  }

  async function generatePdfWithMappings() {
    const mappings = getMappings();
    const f = fabricRef.current;
    if (!f) return;
    f.clone(async (cloned) => {
      cloned.renderAll();
      await applyMappingsToFabricCanvas(cloned, mappings);
      refreshFabricGroupsForExport(cloned);
      cloned.renderAll();
      try {
        await exportFabricCanvasToPdfSelectableText(cloned, template.width, template.height, 'design.pdf', {
          resolution,
          unit,
          reflowDynamicFields: false,
        });
      } catch (err) {
        setToastMessage(err?.message || 'PDF export failed');
      }
    }, FABRIC_JSON_EXTRA_PROPS);
  }

  function startNewTemplate() {
    pendingSavedTemplateJsonRef.current = null;
    pendingTemplateIdRef.current = null;
    fabricTemplateJsonApplyGenRef.current += 1;
    konvaTemplateLoadedKeyRef.current = -1;
    setTemplateModalOpen(false);
    setTemplateModalName('');
    setTemplateModalMode(null);
    setTemplateModalError(null);

    if (USE_KONVA_EDITOR) {
      const { createEmptyScene } = require('./sceneGraph/schema');
      useEditorStore.getState().setScene(createEmptyScene(template.width, template.height));
      useEditorStore.getState().selectObject(null);
      setBgType('solid');
      setBgColor('#ffffff');
      setBgImageUrl(null);
      setCurrentTemplateId(null);
      setTemplateBarName(UNSAVED_TEMPLATE_LABEL);
      setIsUnsavedDraft(true);
      setDraftStatusActive(false);
      setTemplateBarError(null);
      setSelectedObj(null);
      setSelectionBounds(null);
      setPages([null]);
      setCurrentPageIndex(0);
      pagesRef.current = [null];
      currentPageIndexRef.current = 0;
      lastPhOverlaySnapRef.current = '[]';
      setUnselectedPlaceholderOverlays([]);
      setTimeout(() => {
        try {
          setHistoryState({ history: [collectKonvaEditorJson()], index: 0 });
        } catch (_) {
          setHistoryState({ history: [], index: 0 });
        }
      }, 0);
      setLayersKey((k) => k + 1);
      return;
    }
    const f = fabricRef.current;
    if (!f) return;
    f.discardActiveObject();
    f.clear();
    f.setBackgroundImage(null, () => {
      f.setBackgroundColor('#ffffff', () => {
        f.renderAll();
      });
    });

    setBgType('solid');
    setBgColor('#ffffff');
    setBgImageUrl(null);

    setCurrentTemplateId(null);
    setTemplateBarName(UNSAVED_TEMPLATE_LABEL);
    setIsUnsavedDraft(true);
    setDraftStatusActive(false);
    setTemplateBarError(null);
    setSelectedObj(null);
    setSelectionBounds(null);
    lastCanvasSelectionStyledRef.current = null;

    setPages([null]);
    setCurrentPageIndex(0);

    lastPhOverlaySnapRef.current = '[]';
    setUnselectedPlaceholderOverlays([]);

    setTimeout(() => {
      try {
        const json = f.toJSON(['fieldKey', 'fieldConfig', '_shapeLabelGroup', TEXT_BOX_GROUP_FLAG, TEXT_BOX_PAIR_ID, TEXT_BOX_FRAME_FLAG, TEXT_BOX_LABEL_FLAG, '_boxWidth', '_boxHeight']);
        setHistoryState({ history: [json], index: 0 });
      } catch (_) {
        setHistoryState({ history: [], index: 0 });
      }
    }, 0);

    setLayersKey((k) => k + 1);
  }

  function getShapeStyle() {
    return { fill: shapeFillColor, stroke: shapeStrokeColor };
  }

  function applyShapeColorToSelected(fill, stroke) {
    if (USE_KONVA_EDITOR) {
      const id = useEditorStore.getState().selectedId;
      const obj = id ? useEditorStore.getState().getObjectById(id) : null;
      if (!obj || obj.type !== 'shape') return;
      const isLine = obj.fabricType === 'line' || obj.fabricType === 'polyline';
      const patch = { stroke };
      if (!isLine) patch.fill = fill;
      if (stroke && stroke !== 'transparent') patch.strokeWidth = obj.strokeWidth || 2;
      useEditorStore.getState().updateShapeNode(id, patch);
      const updated = useEditorStore.getState().getObjectById(id);
      if (updated) handleKonvaSelection(updated);
      pushKonvaHistory();
      setLayersKey((k) => k + 1);
      return;
    }
    const f = fabricRef.current;
    if (!f) return;
    const obj = f.getActiveObject() ?? selectedObj;
    if (!obj) return;
    const type = obj.type;
    if (type === 'i-text' || type === 'textbox' || type === 'text' || type === 'image') return;
    if (type === 'group' && obj.getObjects) {
      obj.getObjects().forEach((child) => {
        if (child.type === 'i-text' || child.type === 'textbox' || child.type === 'text') return;
        if (child.type === 'line' || child.type === 'polyline') {
          if (child.stroke != null) child.set('stroke', stroke);
          return;
        }
        if (child.fill != null) child.set('fill', fill);
        if (child.stroke != null) child.set('stroke', stroke);
      });
    } else {
      if (type !== 'line' && type !== 'polyline' && type !== 'path') obj.set('fill', fill);
      obj.set('stroke', stroke);
    }
    if (obj.setCoords) obj.setCoords();
    f.discardActiveObject();
    f.setActiveObject(obj);
    f.renderAll();
    setLayersKey((k) => k + 1);
  }

  function applyShapeNoFillToSelected() {
    if (USE_KONVA_EDITOR) {
      const id = useEditorStore.getState().selectedId;
      const obj = id ? useEditorStore.getState().getObjectById(id) : null;
      if (!obj || obj.type !== 'shape') return;
      if (obj.fabricType === 'line' || obj.fabricType === 'polyline') return;
      useEditorStore.getState().updateShapeNode(id, { fill: 'transparent' });
      const updated = useEditorStore.getState().getObjectById(id);
      if (updated) handleKonvaSelection(updated);
      pushKonvaHistory();
      setLayersKey((k) => k + 1);
      return;
    }
    const f = fabricRef.current;
    const obj = f?.getActiveObject() ?? selectedObj;
    if (!f || !obj) return;
    if (obj.type === 'group' && obj.getObjects) {
      obj.getObjects().forEach((child) => {
        if (child.type === 'line' || child.type === 'polyline') return;
        child.set('fill', 'transparent');
      });
    } else if (obj.type !== 'line' && obj.type !== 'polyline') {
      obj.set('fill', 'transparent');
    }
    f.renderAll();
    setLayersKey((k) => k + 1);
  }

  function applyShapeNoBorderToSelected() {
    if (USE_KONVA_EDITOR) {
      const id = useEditorStore.getState().selectedId;
      const obj = id ? useEditorStore.getState().getObjectById(id) : null;
      if (!obj || obj.type !== 'shape') return;
      useEditorStore.getState().updateShapeNode(id, { stroke: 'transparent', strokeWidth: 0 });
      const updated = useEditorStore.getState().getObjectById(id);
      if (updated) handleKonvaSelection(updated);
      pushKonvaHistory();
      setLayersKey((k) => k + 1);
      return;
    }
    const f = fabricRef.current;
    const obj = f?.getActiveObject() ?? selectedObj;
    if (!f || !obj) return;
    const clearStroke = (o) => {
      o.set({ stroke: 'transparent', strokeWidth: 0 });
    };
    if (obj.type === 'group' && obj.getObjects) {
      obj.getObjects().forEach(clearStroke);
    } else {
      clearStroke(obj);
    }
    f.renderAll();
    setLayersKey((k) => k + 1);
  }

  function bringToFront() {
    if (USE_KONVA_EDITOR) {
      const id = useEditorStore.getState().selectedId;
      if (!id) return;
      useEditorStore.getState().reorderObject(id, 'front');
      pushKonvaHistory();
      setLayersKey((k) => k + 1);
      return;
    }
    const obj = fabricRef.current?.getActiveObject();
    if (!obj) return;
    obj.bringToFront();
    fabricRef.current?.renderAll();
    setLayersKey((k) => k + 1);
  }
  function sendToBack() {
    if (USE_KONVA_EDITOR) {
      const id = useEditorStore.getState().selectedId;
      if (!id) return;
      useEditorStore.getState().reorderObject(id, 'back');
      pushKonvaHistory();
      setLayersKey((k) => k + 1);
      return;
    }
    const obj = fabricRef.current?.getActiveObject();
    if (!obj) return;
    obj.sendToBack();
    fabricRef.current?.renderAll();
    setLayersKey((k) => k + 1);
  }
  function bringForward() {
    if (USE_KONVA_EDITOR) {
      const id = useEditorStore.getState().selectedId;
      if (!id) return;
      useEditorStore.getState().reorderObject(id, 'forward');
      pushKonvaHistory();
      setLayersKey((k) => k + 1);
      return;
    }
    const obj = fabricRef.current?.getActiveObject();
    if (!obj) return;
    obj.bringForward();
    fabricRef.current?.renderAll();
    setLayersKey((k) => k + 1);
  }
  function sendBackward() {
    if (USE_KONVA_EDITOR) {
      const id = useEditorStore.getState().selectedId;
      if (!id) return;
      useEditorStore.getState().reorderObject(id, 'backward');
      pushKonvaHistory();
      setLayersKey((k) => k + 1);
      return;
    }
    const obj = fabricRef.current?.getActiveObject();
    if (!obj) return;
    obj.sendBackward();
    fabricRef.current?.renderAll();
    setLayersKey((k) => k + 1);
  }
  function toggleLock(obj) {
    if (USE_KONVA_EDITOR) {
      const id = obj?.id || useEditorStore.getState().selectedId;
      if (!id) return;
      useEditorStore.getState().toggleLock(id);
      pushKonvaHistory();
      setLayersKey((k) => k + 1);
      return;
    }
    const target = obj || fabricRef.current?.getActiveObject();
    if (!target) return;
    target.set({ selectable: !target.selectable, evented: !target.evented });
    fabricRef.current?.renderAll();
    setLayersKey((k) => k + 1);
    if (target === selectedObj) setSelectedObj({ ...target });
  }
  function selectObjectByIndex(index) {
    if (USE_KONVA_EDITOR) {
      const objs = useEditorStore.getState().scene.objects;
      const obj = objs[index];
      if (obj) {
        useEditorStore.getState().selectObject(obj.id);
        handleKonvaSelection(obj);
      }
      return;
    }
    const f = fabricRef.current;
    if (!f) return;
    const objs = f.getObjects();
    const obj = objs[index];
    if (obj) {
      f.setActiveObject(obj);
      f.renderAll();
    }
  }

  function applyBackground() {
    const f = fabricRef.current;
    if (!f) return;
    const { fabric } = require('fabric');
    if (bgType === 'solid') {
      f.setBackgroundColor(bgColor, f.renderAll.bind(f));
      return;
    }
    if (bgType === 'gradient-linear') {
      const g = new fabric.Gradient({
        type: 'linear',
        coords: { x1: 0, y1: 0, x2: f.getWidth(), y2: f.getHeight() },
        colorStops: [
          { offset: 0, color: bgGradientFrom },
          { offset: 1, color: bgGradientTo },
        ],
      });
      f.setBackgroundColor(g, f.renderAll.bind(f));
      return;
    }
    if (bgType === 'gradient-radial') {
      const w = f.getWidth();
      const h = f.getHeight();
      const g = new fabric.Gradient({
        type: 'radial',
        coords: { x1: w / 2, y1: h / 2, r1: 0, x2: w / 2, y2: h / 2, r2: Math.max(w, h) / 2 },
        colorStops: [
          { offset: 0, color: bgGradientFrom },
          { offset: 1, color: bgGradientTo },
        ],
      });
      f.setBackgroundColor(g, f.renderAll.bind(f));
      return;
    }
    if (bgType === 'image') {
      if (bgImageUrl) {
        fabric.Image.fromURL(bgImageUrl, (img) => {
          if (!fabricRef.current) return;
          const w = f.getWidth();
          const h = f.getHeight();
          img.set({ scaleX: w / (img.width || 1), scaleY: h / (img.height || 1), selectable: false, evented: false });
          f.setBackgroundImage(img, f.renderAll.bind(f));
        }, { crossOrigin: 'anonymous' });
      } else {
        f.setBackgroundImage(null, f.renderAll.bind(f));
        f.setBackgroundColor('#ffffff', f.renderAll.bind(f));
      }
    }
  }

  useEffect(() => {
    if (fabricRef.current) applyBackground();
  }, [bgType, bgColor, bgGradientFrom, bgGradientTo, bgImageUrl]);

  const zoomSafe = ZOOM_LEVELS.includes(zoom) ? zoom : snapZoomToLevel(zoom);
  const zoomScale = zoomSafe / 100;
  /** Layout box matches scaled visual size (CSS scale does not affect layout flow). */
  const scaledLayoutW = template.width * zoomScale;
  const scaledLayoutH = template.height * zoomScale;

  const currentTemplateStatus =
    savedTemplates.find((x) => String(x._id) === String(currentTemplateId))?.status || 'inactive';

  useEffect(() => {
    if (!templateMenuOpenId) return;
    function onDoc(e) {
      if (typeof e.target.closest === 'function' && e.target.closest('[data-template-menu-root]')) return;
      setTemplateMenuOpenId(null);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [templateMenuOpenId]);

  useEffect(() => {
    if (!renamingTemplateId) return;
    const t = requestAnimationFrame(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    });
    return () => cancelAnimationFrame(t);
  }, [renamingTemplateId]);

  useEffect(() => {
    if (!toastMessage) return;
    const id = setTimeout(() => setToastMessage(null), 3200);
    return () => clearTimeout(id);
  }, [toastMessage]);

  async function exportKonvaDocumentToPdf(fileName, { applyMappings = false } = {}) {
    useEditorStore.getState().endTextEdit();
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    const stage = konvaStageRef.current;
    if (!stage) throw new Error('Canvas not ready');

    const pageDocs = getExportPageDocs();
    if (!pageDocs.length) throw new Error('Nothing to export');

    // Clear stale cached data URLs so every download fetches fresh images from GCS
    clearGcpExportImageCache();

    try {
      const lib = await listGcpImages();
      seedGcpSignedUrlCache(lib.items || []);
    } catch {
      seedGcpSignedUrlCache(gcpLibrary.items || []);
    }

    const exportOpts = {
      applyMappings,
      mappings: applyMappings ? getMappings() : null,
      lookupFn: applyMappings ? lookupMappingValue : null,
      resolution,
      unit,
    };

    const restorePageOnStage = async (pageDoc, scene, background, pageW, pageH) => {
      let exportScene = scene;
      let exportBg = background;

      if (applyMappings && exportOpts.mappings) {
        ({ scene: exportScene, background: exportBg } = buildKonvaSceneForPdf(pageDoc, pageW, pageH, {
          applyMappings: true,
          mappings: exportOpts.mappings,
          lookupFn: exportOpts.lookupFn,
        }));
      } else {
        exportScene = cloneScene({
          ...scene,
          width: Math.max(1, Number(pageW) || scene?.width || 800),
          height: Math.max(1, Number(pageH) || scene?.height || 600),
        });
      }

      ({ scene: exportScene, background: exportBg } = await inlineSceneAssetsForPdf(exportScene, exportBg));

      useEditorStore.getState().setScene(exportScene);
      applyKonvaBackgroundMeta(exportBg);
      useEditorStore.getState().clearSelection();
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const liveStage = konvaStageRef.current;
      if (liveStage) {
        await ensureKonvaExportImagesReady(liveStage, exportScene);
      }
      return exportScene;
    };

    const wysiwygOpts = {
      ...exportOpts,
      stage,
      restorePageOnStage,
      getCurrentPageIndex: () => currentPageIndexRef.current,
      setCurrentPageIndex: (idx) => {
        setCurrentPageIndex(idx);
        currentPageIndexRef.current = idx;
      },
    };

    if (!applyMappings && pageDocs.length === 1) {
      const pageDoc = pageDocs[0];
      const pageW = pageDoc.pageMeta?.width || pageDoc.scene?.width || template.width;
      const pageH = pageDoc.pageMeta?.height || pageDoc.scene?.height || template.height;
      const pageUnit = pageDoc.pageMeta?.unit || unit;
      const liveBg = pageDoc.background || null;

      // Clone for mutation — never mutate the live store scene during export.
      const exportScene = cloneScene(pageDoc.scene || useEditorStore.getState().scene);

      await downloadSceneFontsLocally(exportScene.objects || [], stage, { forPdf: true });

      // Server-side: objectKey → base64 data URL via /api/gcp-images/batch-data (no CORS needed).
      const { background: exportBg } = await inlineSceneAssetsForPdf(exportScene, liveBg);

      // Pre-load every data URL image into an HTMLImageElement BEFORE the synchronous
      // capture block. This decouples the async network+decode phase from the capture.
      const preloadedNodeImages = new Map();
      await Promise.all(
        (exportScene.objects || [])
          .filter((o) => o?.type === 'image' && String(o.src || '').startsWith('data:'))
          .map(
            (obj) =>
              new Promise((resolve) => {
                const el = new window.Image();
                el.onload = () => {
                  preloadedNodeImages.set(String(obj.id), el);
                  resolve();
                };
                el.onerror = resolve; // skip failures — fall back to live canvas image
                el.src = String(obj.src);
              }),
          ),
      );

      // Apply inlined background (Konva layer update only, no store change).
      const bgImageChanged = exportBg?.bgImageUrl !== liveBg?.bgImageUrl;
      if (bgImageChanged) {
        applyKonvaBackgroundMeta(exportBg);
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      }

      // captureKonvaDesignSurface will inject preloadedNodeImages synchronously right
      // before stage.draw() + toDataURL() — no React re-render window in between.
      let dataUrl;
      try {
        dataUrl = await captureKonvaDesignSurface(
          stage,
          exportScene.objects || [],
          pageW,
          pageH,
          { preloadedNodeImages },
        );
      } finally {
        // Restore background and let React re-take ownership of Konva Image nodes.
        if (bgImageChanged) applyKonvaBackgroundMeta(liveBg);
        const liveScene = useEditorStore.getState().scene;
        useEditorStore.getState().setScene({ ...liveScene });
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      }

      const { jsPdfUnit, pageW: physW, pageH: physH } = getJsPdfPageFormat(pageW, pageH, {
        resolution,
        unit: pageUnit,
      });
      const pdf = new jsPDF({ unit: jsPdfUnit, format: [physW, physH], compress: true });
      pdf.addImage(dataUrl, 'PNG', 0, 0, physW, physH);
      pdf.save(fileName || 'design.pdf');
      return;
    }

    try {
      await exportKonvaPagesWysiwygPdf(pageDocs, fileName || 'design.pdf', wysiwygOpts);
    } catch (wysiwygErr) {
      console.warn('[pdf export] Live canvas failed, trying HTML rebuild:', wysiwygErr?.message || wysiwygErr);
      await exportKonvaPagesToPdf(pageDocs, fileName || 'design.pdf', exportOpts);
    }
  }

  function exportPdf() {
    if (USE_KONVA_EDITOR) {
      (async () => {
        try {
          await exportKonvaDocumentToPdf(`design-${Date.now()}.pdf`, { applyMappings: false });
        } catch (err) {
          setToastMessage(err?.message || 'PDF export failed');
        }
      })();
      return;
    }
    const f = fabricRef.current;
    if (!f) return;
    (async () => {
      try {
        await exportFabricCanvasToPdfSelectableText(f, template.width, template.height, 'design.pdf', {
          resolution,
          unit,
        });
      } catch (err) {
        setToastMessage(err?.message || 'PDF export failed');
      }
    })();
  }

  /** PDF with mapped placeholder/event data applied before export. */
  async function exportPdfWithMappedScene() {
    if (USE_KONVA_EDITOR) {
      await exportKonvaDocumentToPdf(`flyer-${Date.now()}.pdf`, { applyMappings: true });
      return;
    }
    const f = fabricRef.current;
    if (!f) return;
    await new Promise((resolve, reject) => {
      f.clone(
        async (cloned) => {
          try {
            cloned.renderAll();
            await applyMappingsToFabricCanvas(cloned, getMappings());
            refreshFabricGroupsForExport(cloned);
            cloned.renderAll();
            await exportFabricCanvasToPdfSelectableText(cloned, template.width, template.height, `flyer-${Date.now()}.pdf`, {
              resolution,
              unit,
            });
            resolve();
          } catch (err) {
            reject(err);
          }
        },
        FABRIC_JSON_EXTRA_PROPS,
      );
    });
  }

  return (
    <div className="h-screen min-h-0 min-w-0 max-w-[100vw] overflow-x-hidden overflow-y-hidden flex flex-col bg-gray-200">
      {globalApiBusy && (
        <div
          className="fixed inset-0 z-[150] flex items-center justify-center bg-slate-950/40 backdrop-blur-[2px]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="rounded-2xl bg-white/95 px-10 py-8 shadow-2xl border border-slate-200/90 flex flex-col items-center gap-4">
            <img
              src={cfsLogo}
              alt=""
              className="h-16 w-auto max-w-[220px] object-contain select-none animate-pulse"
              draggable={false}
            />
            <span className="text-xs font-semibold text-slate-500 tracking-wide">Loading…</span>
          </div>
        </div>
      )}
      {/* Top bar — tools | template name & actions | zoom & export */}
      <header className="flex flex-col shrink-0 bg-white border-b border-slate-200/90 shadow-[0_1px_0_rgba(15,23,42,0.06)]">
        <div className="flex flex-wrap items-center justify-between gap-3 min-h-[3.25rem] px-4 sm:px-5 py-2.5">
          <div className="flex items-center gap-3 shrink-0 min-w-0">
            <div className="flex items-center gap-2.5 pr-3 sm:pr-4 border-r border-slate-200/90 shrink-0">
              <img src={cfsLogo} alt="CFS" className="h-9 w-auto object-contain select-none" draggable={false} />
              <div className="hidden sm:flex flex-col leading-tight">
                <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Dynamic Flyers</span>
                <span className="text-sm font-semibold text-[#2a318b]">Editor</span>
              </div>
            </div>
          <div
            className="flex items-center gap-1 rounded-xl border border-slate-200/90 bg-slate-50/90 p-1 shrink-0 shadow-sm"
            role="toolbar"
            aria-label="Editing"
          >
            <button
              onClick={undo}
              disabled={historyIndex <= (USE_KONVA_EDITOR ? konvaHistoryBaselineRef.current : 0)}
              className="rounded-lg px-2.5 py-2 text-sm font-medium text-[#2a318b] hover:bg-white disabled:opacity-40 disabled:pointer-events-none flex items-center gap-1.5 transition-colors"
              title="Undo"
            >
              <Undo2 size={17} strokeWidth={2} />
              <span className="hidden md:inline">Undo</span>
            </button>
            <button onClick={redo} disabled={historyIndex >= history.length - 1 || !history[historyIndex + 1]} className="rounded-lg px-2.5 py-2 text-sm font-medium text-[#2a318b] hover:bg-white disabled:opacity-40 disabled:pointer-events-none flex items-center gap-1.5 transition-colors" title="Redo">
              <Redo2 size={17} strokeWidth={2} />
              <span className="hidden md:inline">Redo</span>
            </button>
            <span className="w-px h-6 bg-slate-200 mx-0.5 hidden sm:block" aria-hidden="true" />
            <button
              type="button"
              onClick={startNewTemplate}
              className="rounded-lg px-2.5 py-2 text-sm font-medium text-[#2a318b] hover:bg-white flex items-center gap-1.5 transition-colors"
              title="New blank canvas (current page size)"
            >
              <FilePlus size={17} strokeWidth={2} />
              <span className="hidden md:inline">New</span>
            </button>
          </div>
          </div>

          {!preview && (currentTemplateId || isUnsavedDraft) && (
            <div className="flex flex-1 min-w-[12rem] justify-center order-3 sm:order-none w-full sm:w-auto">
              <div className="flex flex-col lg:flex-row lg:items-center gap-2.5 min-w-0 max-w-full w-full sm:w-auto rounded-xl border border-slate-200 bg-gradient-to-b from-white to-slate-50/90 px-3.5 py-2.5 shadow-md shadow-slate-200/50">
                <div className="flex items-center gap-2.5 min-w-0 shrink">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 shrink-0">
                    <LayoutTemplate className="w-5 h-5" aria-hidden />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Current template</div>
                    <span
                      className={`text-sm font-semibold truncate block max-w-[14rem] sm:max-w-[18rem] lg:max-w-[22rem] ${isUnsavedDraft && !currentTemplateId ? 'text-amber-800 font-mono text-xs' : 'text-slate-900'}`}
                      title={templateBarName || ''}
                    >
                      {isUnsavedDraft && !currentTemplateId
                        ? UNSAVED_TEMPLATE_LABEL
                        : templateBarName?.trim() || 'Untitled template'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 lg:pl-3 lg:border-l lg:border-slate-200 shrink-0 justify-start">
                  {currentTemplateId ? (
                    <>
                      <button
                        type="button"
                        onClick={openStatusChangeDialog}
                        disabled={templateStatusBusy}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-sm disabled:opacity-60"
                        title={currentTemplateStatus === 'active' ? 'Deactivate this template' : 'Activate this template'}
                      >
                        {currentTemplateStatus === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        type="button"
                        onClick={openUpdateTemplateModal}
                        className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-2.5 py-2 text-xs font-medium hover:bg-indigo-700 shadow-sm"
                        title="Save changes — set the template name in the dialog"
                      >
                        <Save size={13} />
                        Update
                      </button>
                      <button
                        type="button"
                        onClick={openSaveAsNewTemplateModal}
                        className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 px-2.5 py-2 text-xs font-medium hover:bg-indigo-100"
                        title="Save a copy — name it in the dialog"
                      >
                        <FilePlus size={13} />
                        Save as new
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={openStatusChangeDialog}
                        className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-2 text-xs font-semibold shadow-sm ${
                          draftStatusActive
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                            : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                        }`}
                        title={
                          draftStatusActive
                            ? 'New template will save as Active. Click to switch to Inactive.'
                            : 'New template will save as Inactive. Click to switch to Active.'
                        }
                      >
                        {draftStatusActive ? 'Active' : 'Inactive'}
                      </button>
                      <button
                        type="button"
                        onClick={openNewSaveModal}
                        className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 text-white px-2.5 py-2 text-xs font-semibold shadow-sm hover:bg-indigo-700"
                        title="Enter a unique name to save this template"
                      >
                        <Save size={13} />
                        Save
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          {!preview && !currentTemplateId && !isUnsavedDraft && (
            <div className="flex-1 min-w-0 order-3 sm:order-none w-full sm:w-auto" aria-hidden="true" />
          )}
          {preview && <div className="flex-1 min-w-0 order-3 sm:order-none hidden sm:block" aria-hidden="true" />}

        <div className="flex flex-wrap items-center justify-end gap-2 shrink-0 order-last sm:order-none w-full sm:w-auto">
          <div className="flex items-center rounded-xl bg-slate-50 border border-slate-200/90 p-0.5 shadow-sm">
            <button onClick={() => setZoom((z) => stepZoomLevel(z, -1))} className="p-2 rounded-lg text-slate-600 hover:bg-white hover:text-[#2a318b] transition-colors" title="Zoom out">
              <ZoomOut size={17} />
            </button>
            <select
              value={zoomSafe}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="bg-transparent border-0 text-sm font-semibold text-[#2a318b] min-w-[4.5rem] py-2 px-2 focus:ring-0 cursor-pointer"
            >
              {ZOOM_LEVELS.map((n) => (
                <option key={n} value={n}>{n}%</option>
              ))}
            </select>
            <button onClick={() => setZoom((z) => stepZoomLevel(z, 1))} className="p-2 rounded-lg text-slate-600 hover:bg-white hover:text-[#2a318b] transition-colors" title="Zoom in">
              <ZoomIn size={17} />
            </button>
          </div>
          <span className="hidden lg:block w-px h-8 bg-slate-200" aria-hidden="true" />
          <button
            onClick={() => setPreview(!preview)}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition-all shadow-sm border ${preview ? 'text-white border-[#2a318b] bg-[#2a318b]' : 'text-[#2a318b] border-slate-200 bg-white hover:bg-slate-50'}`}
          >
            Preview
          </button>
          <a
            href="https://dev-cfs.royalcyber.org/webapp/member-homepage/events/event-calendar"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white bg-[#c41e3a] hover:bg-[#a6192e] shadow-sm border border-transparent transition-colors"
            title="Open CFS event calendar"
          >
            <CalendarDays size={18} strokeWidth={2} className="shrink-0 opacity-95" aria-hidden />
            Events
          </a>
          {/* <button
            onClick={downloadFlyerWithData}
            className="rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:opacity-90 border border-transparent"
            style={{ background: '#c41e3a' }}
            title="Generate flyer with dynamic placeholder data (template must be saved)"
          >
            Download flyer
          </button> */}
        </div>
        </div>
        {!preview && templateBarError && (
          <p className="px-4 sm:px-5 py-1.5 text-xs text-red-600 bg-red-50 border-t border-red-100" role="alert">
            {templateBarError}
          </p>
        )}
      </header>

      {templateModalOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="template-modal-title"
          onClick={(e) => e.target === e.currentTarget && !templateModalBusy && setTemplateModalOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-white shadow-xl border border-slate-200 p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="template-modal-title" className="text-lg font-bold text-slate-900">
              {templateModalMode === 'newSave' && 'Save template'}
              {templateModalMode === 'update' && 'Update template'}
              {templateModalMode === 'saveAsNew' && 'Save as new template'}
            </h2>
            {templateModalMode === 'newSave' && (
              <p className="text-sm text-slate-600">Unique name. Use Active / Inactive in the bar for save status.</p>
            )}
            {(templateModalMode === 'update' || templateModalMode === 'saveAsNew') && (
              <p className="text-sm text-slate-600">Unique template name.</p>
            )}
            <div>
              <label htmlFor="template-modal-name" className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                Template name
              </label>
              <input
                id="template-modal-name"
                type="text"
                value={templateModalName}
                onChange={(e) => setTemplateModalName(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                disabled={templateModalBusy}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !templateModalBusy) {
                    e.preventDefault();
                    submitTemplateModal();
                  }
                }}
              />
            </div>
            {templateModalError && (
              <p className="text-sm text-red-600" role="alert">
                {templateModalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => !templateModalBusy && setTemplateModalOpen(false)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitTemplateModal}
                disabled={templateModalBusy}
                className="rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
              >
                {templateModalBusy
                  ? 'Working…'
                  : templateModalMode === 'update'
                    ? 'Update'
                    : templateModalMode === 'saveAsNew'
                      ? 'Save copy'
                      : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {statusConfirm && (
        <div
          className="fixed inset-0 z-[205] flex items-center justify-center p-4 bg-slate-900/50"
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.target === e.currentTarget && !templateStatusBusy && setStatusConfirm(null)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white shadow-xl border border-slate-200 p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-slate-900">
              {statusConfirm.draft
                ? statusConfirm.next === 'active'
                  ? 'Save new templates as Active?'
                  : 'Save new templates as Inactive?'
                : statusConfirm.next === 'active'
                  ? 'Activate?'
                  : 'Deactivate?'}
            </h2>
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => !templateStatusBusy && setStatusConfirm(null)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => applyStatusConfirm()}
                disabled={templateStatusBusy}
                className="rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50"
              >
                {templateStatusBusy ? '…' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div
          className="fixed inset-0 z-[205] flex items-center justify-center p-4 bg-slate-900/50"
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.target === e.currentTarget && !deleteBusy && setDeleteConfirm(null)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white shadow-xl border border-slate-200 p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-slate-900">Delete template?</h2>
            <p className="text-sm text-slate-600">{`Are you sure you want to delete "${deleteConfirm.name}"?`}</p>
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => !deleteBusy && setDeleteConfirm(null)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  setDeleteBusy(true);
                  setTemplateBarError(null);
                  try {
                    await deleteTemplateById(deleteConfirm.id);
                  } catch (err) {
                    setTemplateBarError(err.message || 'Delete failed');
                  } finally {
                    setDeleteBusy(false);
                  }
                }}
                disabled={deleteBusy}
                className="rounded-lg bg-red-600 text-white px-4 py-2 text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                {deleteBusy ? '…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {gcpLimitModalOpen && (
        <div
          className="fixed inset-0 z-[205] flex items-center justify-center p-4 bg-slate-900/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gcp-limit-title"
          onClick={(e) => e.target === e.currentTarget && setGcpLimitModalOpen(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white shadow-xl border border-slate-200 p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="gcp-limit-title" className="text-lg font-bold text-slate-900">
              Image limit reached
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              You can store at most {GCP_IMAGES_MAX} images in cloud storage. Remove an image from <strong className="text-slate-800">Previous images used</strong> before uploading another.
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setGcpLimitModalOpen(false)}
                className="rounded-lg bg-indigo-600 text-white px-4 py-2 text-sm font-semibold hover:bg-indigo-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {gcpImageDeleteConfirm && (
        <div
          className="fixed inset-0 z-[205] flex items-center justify-center p-4 bg-slate-900/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gcp-del-title"
          onClick={(e) => e.target === e.currentTarget && !gcpDeleteBusy && setGcpImageDeleteConfirm(null)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white shadow-xl border border-slate-200 p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="gcp-del-title" className="text-lg font-bold text-slate-900">
              Remove image?
            </h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              This permanently deletes the file from cloud storage. It cannot be undone.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => !gcpDeleteBusy && setGcpImageDeleteConfirm(null)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const key = gcpImageDeleteConfirm?.objectKey;
                  if (!key) return;
                  setGcpDeleteBusy(true);
                  setGcpLibraryError(null);
                  try {
                    await removeGcpImage(key);
                    await refreshGcpLibrary();
                    setGcpImageDeleteConfirm(null);
                  } catch (err) {
                    setGcpLibraryError(err.message || 'Delete failed');
                  } finally {
                    setGcpDeleteBusy(false);
                  }
                }}
                disabled={gcpDeleteBusy}
                className="rounded-lg bg-red-600 text-white px-4 py-2 text-sm font-semibold hover:bg-red-700 disabled:opacity-50"
              >
                {gcpDeleteBusy ? '…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1 min-h-0 min-w-0 max-w-full overflow-x-hidden overflow-y-hidden">
        {/* Left sidebar — tools + panels */}
        <aside className="flex border-r border-gray-300 shadow-sm shrink-0" style={{ background: '#f1f5f9' }}>
          <div className="w-[4.25rem] flex flex-col py-2 shrink-0" style={{ background: '#2a318b' }}>
            {[
              { id: 'templates', label: 'Templates', Icon: LayoutTemplate, onClick: () => { setActiveTool('templates'); setLibrarySearch(''); } },
              { id: 'images', label: 'Images', Icon: ImageIcon, onClick: () => { setActiveTool('images'); setLibrarySearch(''); } },
              { id: 'text', label: 'Text', Icon: Type, onClick: () => { setActiveTool('text'); setLibrarySearch(''); } },
              { id: 'shapes', label: 'Shapes', Icon: Shapes, onClick: () => { setActiveTool('shapes'); setLibrarySearch(''); } },
              { id: 'uploads', label: 'Uploads', Icon: Upload, onClick: () => uploadInputRef.current?.click() },
            ].map(({ id, label, Icon, onClick, disabled, title }) => (
              <button
                key={id}
                onClick={onClick}
                disabled={disabled}
                className={`flex flex-col items-center gap-1 py-2.5 px-1 rounded-lg mx-1.5 transition-colors ${
                  disabled
                    ? 'opacity-60 cursor-wait text-white/70'
                    : activeTool === id
                      ? 'bg-white/20 text-white shadow-sm'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
                title={title || label}
              >
                <Icon size={22} strokeWidth={1.8} />
                <span className="text-[9px] font-medium leading-tight max-w-full truncate">{label}</span>
              </button>
            ))}
            <input ref={uploadInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => addImageFile(e, false)} />
          </div>
          {/* Library panel — unified for Templates, Shapes, Text */}
          {(activeTool === 'templates' || activeTool === 'shapes' || activeTool === 'text' || activeTool === 'images') && (
            <div className="w-80 flex flex-col overflow-hidden bg-gray-50 border-l border-gray-200">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h2 className="text-base font-bold text-slate-800">Library</h2>
                <button
                  onClick={() => setActiveTool(null)}
                  className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                  aria-label="Close"
                >
                  <X size={20} strokeWidth={2} />
                </button>
              </div>
              <div className="px-3 pt-2 pb-3 border-b border-slate-100">
                <label className="flex items-center gap-3 rounded-full bg-slate-100 pl-4 pr-3 py-2 border border-slate-200/80 focus-within:bg-white focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
                  <Search size={17} className="text-slate-500 shrink-0" aria-hidden />
                  <input
                    type="search"
                    value={librarySearch}
                    onChange={(e) => setLibrarySearch(e.target.value)}
                    placeholder="Search…"
                    className="flex-1 min-w-0 bg-transparent border-0 text-sm text-slate-800 placeholder-slate-500 focus:ring-0 py-0.5"
                    aria-label="Search library"
                  />
                </label>
              </div>
              <div ref={libraryScrollRef} className="flex-1 overflow-auto p-3 min-h-0" data-library-scroll>
                {/* Templates — category, grid / list views */}
                {activeTool === 'templates' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-end gap-2">
                      <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mr-auto">Templates</span>
                      <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 shadow-sm" role="group" aria-label="Template layout">
                        <button
                          type="button"
                          onClick={() => setTemplateViewMode('grid')}
                          className={`p-2 rounded-md transition-colors ${templateViewMode === 'grid' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                          title="Grid view"
                        >
                          <LayoutGrid size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setTemplateViewMode('list')}
                          className={`p-2 rounded-md transition-colors ${templateViewMode === 'list' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                          title="List view"
                        >
                          <List size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter templates by status">
                      {[
                        { id: 'all', label: 'All' },
                        { id: 'active', label: 'Active' },
                        { id: 'inactive', label: 'Inactive' },
                      ].map(({ id, label }) => (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setTemplateStatusFilter(id)}
                          className={`rounded-lg px-2.5 py-1 text-xs font-semibold border transition-colors ${
                            templateStatusFilter === id
                              ? id === 'active'
                                ? 'bg-emerald-600 text-white border-emerald-600 shadow-sm'
                                : id === 'inactive'
                                  ? 'bg-slate-600 text-white border-slate-600 shadow-sm'
                                  : 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                              : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    {templatesLoadError && (
                      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                        <p>{templatesLoadError}</p>
                        <button
                          type="button"
                          onClick={() => loadSavedTemplates({ force: true })}
                          className="mt-2 rounded-md bg-red-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-red-700"
                        >
                          Retry
                        </button>
                      </div>
                    )}
                    {(() => {
                      const allTemplates = [...getTemplates(), ...savedTemplates];
                      const templateList = allTemplates.filter(
                        (tpl) =>
                          templateMatchesStatusFilter(tpl, templateStatusFilter) &&
                          (!librarySearch ||
                            tpl.name.toLowerCase().includes(librarySearch.toLowerCase()) ||
                            (tpl.preset && tpl.preset.toLowerCase().includes(librarySearch.toLowerCase())))
                      );
                      if (templateList.length === 0) {
                        const hasAnySaved = allTemplates.length > 0;
                        let emptyMsg;
                        if (!hasAnySaved) {
                          emptyMsg = (
                            <>
                              No templates here yet. Saved templates from your server will list when available. After you open a
                              template, use <strong className="text-slate-700">Save as new</strong> in the top bar to save a copy.
                            </>
                          );
                        } else if (librarySearch) {
                          emptyMsg = 'No templates match your search.';
                        } else if (templateStatusFilter !== 'all') {
                          emptyMsg = `No ${templateStatusFilter} templates. Try another filter or change a template’s status in the top bar.`;
                        } else {
                          emptyMsg = 'No templates to show.';
                        }
                        return (
                          <p className="text-xs text-slate-500 py-4 text-center leading-relaxed">{emptyMsg}</p>
                        );
                      }
                      if (templateViewMode === 'grid') {
                        const resultsLabel =
                          templateStatusFilter === 'active'
                            ? 'Active templates'
                            : templateStatusFilter === 'inactive'
                              ? 'Inactive templates'
                              : 'All results';
                        return (
                          <div className="space-y-2">
                            <div className="text-xs font-bold text-slate-900">{resultsLabel}</div>
                            <div className="grid grid-cols-2 gap-3">
                            {templateList.map((tpl) => {
                              const rowId = normalizeTemplateId(tpl._id ?? tpl.id);
                              const isSaved = !!rowId;
                              const previewSrc = tpl.preview_image || tpl.previewImage;
                              const previewThumbKey =
                                rowId || `n:${String(tpl.name || '').trim() || 't'}:${String(tpl._id ?? tpl.id ?? '')}`;
                              const showPreviewThumb = previewSrc && !brokenTemplatePreviewKeys[previewThumbKey];
                              return (
                                <div
                                  key={tpl._id || tpl.id}
                                  className={`relative flex flex-col overflow-visible border border-slate-200 bg-white rounded-sm hover:border-slate-300 transition-colors group ${
                                    templateMenuOpenId === rowId ? 'z-20' : ''
                                  }`}
                                >
                                  {isSaved && (
                                    <span
                                      className={`absolute top-2 left-2 z-10 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-sm shadow-sm ${
                                        tpl.status === 'active' ? 'bg-emerald-600 text-white' : 'bg-slate-500 text-white'
                                      }`}
                                    >
                                      {tpl.status === 'active' ? 'Active' : 'Inactive'}
                                    </span>
                                  )}
                                  {isSaved && (
                                    <div
                                      className="absolute top-2 right-2 z-30 flex w-max max-w-[calc(100%-0.75rem)] flex-col items-end gap-0"
                                      data-template-menu-root
                                    >
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setTemplateMenuOpenId((id) => (id === rowId ? null : rowId));
                                        }}
                                        className="shrink-0 rounded-md p-1 text-slate-500 bg-white/90 border border-slate-200 shadow-sm hover:bg-slate-50 hover:text-slate-800"
                                        title="More"
                                        aria-label="Template actions"
                                      >
                                        <MoreVertical size={16} strokeWidth={2} />
                                      </button>
                                      {templateMenuOpenId === rowId && (
                                        <div className="mt-1 w-36 shrink-0 rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                                          <button
                                            type="button"
                                            className="w-full text-left px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setTemplateMenuOpenId(null);
                                              setRenamingTemplateId(rowId);
                                              setRenameDraft(tpl.name || '');
                                            }}
                                          >
                                            Rename
                                          </button>
                                          <button
                                            type="button"
                                            className="w-full text-left px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setTemplateMenuOpenId(null);
                                              setDeleteConfirm({ id: rowId, name: tpl.name || '' });
                                            }}
                                          >
                                            Delete
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (renamingTemplateId === rowId) return;
                                      openSavedTemplate(tpl);
                                    }}
                                    className="flex flex-col text-left w-full min-h-0 group/btn"
                                  >
                                    <div className="aspect-[3/4] min-h-[120px] w-full flex items-center justify-center overflow-hidden rounded-t-sm bg-slate-50">
                                      {showPreviewThumb ? (
                                        <img
                                          src={previewSrc}
                                          alt=""
                                          className="w-full h-full object-cover"
                                          onError={() =>
                                            setBrokenTemplatePreviewKeys((prev) => ({
                                              ...prev,
                                              [previewThumbKey]: true,
                                            }))
                                          }
                                        />
                                      ) : tpl.json ? (
                                        <TemplateFabricThumb template={tpl} listMode={false} />
                                      ) : (
                                        <LayoutTemplate size={28} strokeWidth={1.5} className="text-slate-300 group-hover:text-indigo-400 transition-colors" />
                                      )}
                                    </div>
                                    <div className="px-2 py-2 border-t border-slate-200 bg-white shrink-0">
                                      {renamingTemplateId === rowId ? (
                                        <div className="flex items-center gap-1 min-w-0">
                                          <input
                                            ref={renameInputRef}
                                            value={renameDraft}
                                            onChange={(e) => setRenameDraft(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={async (e) => {
                                              if (e.key === 'Enter') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                try {
                                                  await renameTemplateById(rowId, renameDraft);
                                                  setRenamingTemplateId(null);
                                                } catch (err) {
                                                  setTemplateBarError(err.message || 'Rename failed');
                                                }
                                              }
                                              if (e.key === 'Escape') {
                                                e.stopPropagation();
                                                setRenamingTemplateId(null);
                                              }
                                            }}
                                            onBlur={async () => {
                                              if (!rowId) return;
                                              const next = renameDraft.trim();
                                              if (!next || next === (tpl.name || '').trim()) {
                                                setRenamingTemplateId(null);
                                                return;
                                              }
                                              try {
                                                await renameTemplateById(rowId, renameDraft);
                                              } catch (err) {
                                                setTemplateBarError(err.message || 'Rename failed');
                                              } finally {
                                                setRenamingTemplateId(null);
                                              }
                                            }}
                                            className="flex-1 min-w-0 text-xs font-semibold text-slate-900 border border-indigo-300 rounded px-1.5 py-0.5"
                                          />
                                          <button
                                            type="button"
                                            aria-label="Save name"
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              if (!rowId) return;
                                              const next = renameDraft.trim();
                                              if (!next || next === (tpl.name || '').trim()) {
                                                setRenamingTemplateId(null);
                                                return;
                                              }
                                              try {
                                                await renameTemplateById(rowId, renameDraft);
                                                setRenamingTemplateId(null);
                                              } catch (err) {
                                                setTemplateBarError(err.message || 'Rename failed');
                                              }
                                            }}
                                            className="shrink-0 rounded-md bg-indigo-600 text-white p-1 hover:bg-indigo-700"
                                          >
                                            <Check size={14} strokeWidth={2.5} aria-hidden />
                                          </button>
                                        </div>
                                      ) : (
                                        <>
                                          <span className="text-xs font-semibold text-slate-900 truncate block group-hover/btn:text-indigo-900" title={tpl.name || undefined}>
                                            {tpl.name}
                                          </span>
                                          {tpl.preset && (
                                            <span className="text-[10px] text-slate-500 font-medium truncate block" title={tpl.preset}>{tpl.preset}</span>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </button>
                                </div>
                              );
                            })}
                            </div>
                          </div>
                        );
                      }
                      return (
                        <ul className="space-y-2" role="list">
                          {templateList.map((tpl) => {
                            const rowId = normalizeTemplateId(tpl._id ?? tpl.id);
                            const isSaved = !!rowId;
                            const previewSrc = tpl.preview_image || tpl.previewImage;
                            const previewThumbKey =
                              rowId || `n:${String(tpl.name || '').trim() || 't'}:${String(tpl._id ?? tpl.id ?? '')}`;
                            const showPreviewThumb = previewSrc && !brokenTemplatePreviewKeys[previewThumbKey];
                            return (
                              <li key={tpl._id || tpl.id} className="flex items-stretch gap-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (renamingTemplateId === rowId) return;
                                    openSavedTemplate(tpl);
                                  }}
                                  className="flex-1 min-w-0 text-left rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm hover:border-indigo-300 hover:shadow-md hover:bg-indigo-50/40 transition-all flex items-start gap-3 group"
                                >
                                  {showPreviewThumb ? (
                                    <img
                                      src={previewSrc}
                                      alt=""
                                      className="w-14 h-11 rounded-lg object-cover bg-slate-100 border border-slate-100 shrink-0 mt-0.5"
                                      onError={() =>
                                        setBrokenTemplatePreviewKeys((prev) => ({
                                          ...prev,
                                          [previewThumbKey]: true,
                                        }))
                                      }
                                    />
                                  ) : tpl.json ? (
                                    <div className="w-14 h-11 rounded-lg overflow-hidden bg-slate-100 border border-slate-100 shrink-0 mt-0.5">
                                      <TemplateFabricThumb template={tpl} listMode />
                                    </div>
                                  ) : (
                                    <div className="w-14 h-11 rounded-lg bg-slate-100 border border-slate-100 shrink-0 flex items-center justify-center text-slate-400 mt-0.5">
                                      <LayoutTemplate size={20} strokeWidth={1.5} className="opacity-60" />
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0 flex flex-col gap-0.5 pr-1">
                                    {renamingTemplateId === rowId ? (
                                      <div className="flex items-center gap-1.5 min-w-0 w-full">
                                        <input
                                          ref={renameInputRef}
                                          value={renameDraft}
                                          onChange={(e) => setRenameDraft(e.target.value)}
                                          onClick={(e) => e.stopPropagation()}
                                          onKeyDown={async (e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              try {
                                                await renameTemplateById(rowId, renameDraft);
                                                setRenamingTemplateId(null);
                                              } catch (err) {
                                                setTemplateBarError(err.message || 'Rename failed');
                                              }
                                            }
                                            if (e.key === 'Escape') setRenamingTemplateId(null);
                                          }}
                                          onBlur={async () => {
                                            const next = renameDraft.trim();
                                            if (!next || next === (tpl.name || '').trim()) {
                                              setRenamingTemplateId(null);
                                              return;
                                            }
                                            try {
                                              await renameTemplateById(rowId, renameDraft);
                                            } catch (err) {
                                              setTemplateBarError(err.message || 'Rename failed');
                                            } finally {
                                              setRenamingTemplateId(null);
                                            }
                                          }}
                                          className="flex-1 min-w-0 text-sm font-semibold text-slate-900 border border-indigo-300 rounded px-2 py-1"
                                        />
                                        <button
                                          type="button"
                                          aria-label="Save name"
                                          onMouseDown={(e) => e.preventDefault()}
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            const next = renameDraft.trim();
                                            if (!next || next === (tpl.name || '').trim()) {
                                              setRenamingTemplateId(null);
                                              return;
                                            }
                                            try {
                                              await renameTemplateById(rowId, renameDraft);
                                              setRenamingTemplateId(null);
                                            } catch (err) {
                                              setTemplateBarError(err.message || 'Rename failed');
                                            }
                                          }}
                                          className="shrink-0 rounded-md bg-indigo-600 text-white p-1.5 hover:bg-indigo-700"
                                        >
                                          <Check size={16} strokeWidth={2.5} aria-hidden />
                                        </button>
                                      </div>
                                    ) : (
                                      <>
                                        <div className="flex items-start gap-2 min-w-0">
                                          <span
                                            className="text-sm font-semibold text-slate-900 group-hover:text-indigo-900 min-w-0 flex-1 truncate text-left"
                                            title={tpl.name || undefined}
                                          >
                                            {tpl.name}
                                          </span>
                                          {isSaved && (
                                            <span
                                              className={`shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded whitespace-nowrap mt-0.5 ${
                                                tpl.status === 'active' ? 'bg-emerald-600 text-white' : 'bg-slate-500 text-white'
                                              }`}
                                            >
                                              {tpl.status === 'active' ? 'Active' : 'Inactive'}
                                            </span>
                                          )}
                                        </div>
                                        {tpl.preset && (
                                          <span className="text-[11px] text-slate-500 font-medium truncate" title={tpl.preset}>
                                            {tpl.preset}
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </div>
                                  <ChevronRight size={18} className="text-slate-300 group-hover:text-indigo-500 shrink-0 self-center ml-0.5" aria-hidden />
                                </button>
                                {isSaved && (
                                  <div className="relative shrink-0 flex items-center" data-template-menu-root>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setTemplateMenuOpenId((id) => (id === rowId ? null : rowId));
                                      }}
                                      className="rounded-lg p-2 text-slate-500 border border-slate-200 bg-white shadow-sm hover:bg-slate-50"
                                      title="More"
                                      aria-label="Template actions"
                                    >
                                      <MoreVertical size={18} strokeWidth={2} />
                                    </button>
                                    {templateMenuOpenId === rowId && (
                                      <div className="absolute right-0 top-full mt-1 w-36 rounded-lg border border-slate-200 bg-white py-1 shadow-lg z-30">
                                        <button
                                          type="button"
                                          className="w-full text-left px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTemplateMenuOpenId(null);
                                            setRenamingTemplateId(rowId);
                                            setRenameDraft(tpl.name || '');
                                          }}
                                        >
                                          Rename
                                        </button>
                                        <button
                                          type="button"
                                          className="w-full text-left px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTemplateMenuOpenId(null);
                                            setDeleteConfirm({ id: rowId, name: tpl.name || '' });
                                          }}
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      );
                    })()}
                  </div>
                )}

                {/* Shapes — Color, Basic & Abstract */}
                {activeTool === 'shapes' && (
                  <div className="space-y-4">
                    <section>
                      <div className="text-sm font-bold text-slate-800 mb-2">Shape color</div>
                      <div className="flex flex-wrap gap-2">
                        {SHAPE_COLORS.map(({ fill, stroke }) => (
                          <button
                            key={fill}
                            onClick={() => {
                              setShapeFillColor(fill);
                              setShapeStrokeColor(stroke);
                              applyShapeColorToSelected(fill, stroke);
                            }}
                            className={`w-9 h-9 rounded-md border-2 transition-all ${shapeFillColor === fill ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-slate-200 hover:border-slate-300'}`}
                            style={{ backgroundColor: fill }}
                            title={fill}
                          />
                        ))}
                      </div>
                      <ColorPickerField
                        label="Custom fill"
                        value={shapeFillColor}
                        onChange={(v) => {
                          setShapeFillColor(v);
                          applyShapeColorToSelected(v, shapeStrokeColor);
                        }}
                        swatches={SHAPE_COLORS.map((c) => c.fill)}
                      />
                      <ColorPickerField
                        label="Custom stroke"
                        value={shapeStrokeColor}
                        onChange={(v) => {
                          setShapeStrokeColor(v);
                          applyShapeColorToSelected(shapeFillColor, v);
                        }}
                        swatches={SHAPE_COLORS.map((c) => c.stroke)}
                        allowTransparent
                      />
                      <div className="flex flex-wrap gap-2 pt-1">
                        <button type="button" onClick={applyShapeNoFillToSelected} className="text-xs font-medium px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50">No fill</button>
                        <button type="button" onClick={applyShapeNoBorderToSelected} className="text-xs font-medium px-2.5 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50">No border</button>
                      </div>
                    </section>
                    <section>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-base font-bold text-slate-800">Basic</span>
                        <span className="text-xs text-slate-500">17 &gt;</span>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { label: 'Rectangle', shapeKey: 'rect', onClick: addShapeRect, Icon: Square },
                          { label: 'Line', shapeKey: 'line', onClick: addShapeLine, Icon: Minus },
                          { label: 'Dotted', shapeKey: 'dottedLine', onClick: addShapeDottedLine, Icon: Minus },
                          { label: 'Star', shapeKey: 'star', onClick: addShapeStar, Icon: Star },
                          { label: 'Pentagon', shapeKey: 'pentagon', onClick: addShapePentagon, Icon: Pentagon },
                          { label: 'Circle', shapeKey: 'circle', onClick: addShapeCircle, Icon: Circle },
                          { label: 'Triangle', shapeKey: 'triangle', onClick: addShapeTriangle, Icon: Triangle },
                          { label: 'Hexagon', shapeKey: 'hexagon', onClick: addShapeHexagon, Icon: Hexagon },
                          { label: 'Octagon', shapeKey: 'octagon', onClick: addShapeOctagon, Icon: Octagon },
                          { label: 'Diamond', shapeKey: 'diamond', onClick: addShapeDiamond, Icon: Diamond },
                          { label: 'Trapezoid', shapeKey: 'trapezoid', onClick: addShapeTrapezoid, Icon: Pentagon },
                          { label: 'Parallel.', shapeKey: 'parallelogram', onClick: addShapeParallelogram, Icon: Diamond },
                          { label: 'Cross', shapeKey: 'cross', onClick: addShapeCross, Icon: Plus },
                          { label: 'Heart', shapeKey: 'heart', onClick: addShapeHeart, Icon: Heart },
                          { label: 'Rounded', shapeKey: 'roundedRect', onClick: addShapeRoundedRect, Icon: Square },
                          { label: 'Ellipse', shapeKey: 'ellipse', onClick: addShapeEllipse, Icon: Circle },
                          { label: 'Arrow', shapeKey: 'arrow', onClick: addShapeArrow, Icon: ArrowRight },
                          { label: 'Right Tri', shapeKey: 'rightTriangle', onClick: addShapeRightTriangle, Icon: Triangle },
                        ]
                          .filter((s) => !librarySearch || s.label.toLowerCase().includes(librarySearch.toLowerCase()))
                          .map(({ label, shapeKey, onClick: fn, Icon }) => (
                            <button
                              key={label}
                              type="button"
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData('application/x-shape-key', shapeKey);
                                e.dataTransfer.effectAllowed = 'copy';
                              }}
                              onClick={fn}
                              className="min-h-[5.25rem] rounded-xl border border-slate-200 bg-slate-50 flex flex-col items-center justify-center gap-1 py-2 px-1 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all cursor-grab active:cursor-grabbing"
                              title={`${label} — drag to canvas or click to add`}
                            >
                              {Icon && <Icon size={28} strokeWidth={2} className="text-slate-600 shrink-0" />}
                              <span className="text-xs font-medium text-slate-600 text-center leading-tight line-clamp-2">{label}</span>
                            </button>
                          ))}
                      </div>
                    </section>
                    <section>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-base font-bold text-slate-800">More shapes</span>
                        <span className="text-xs text-slate-500">5 &gt;</span>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { label: 'Chevron', shapeKey: 'chevron', onClick: addShapeChevron, Icon: ChevronRight },
                          { label: 'Two-way', shapeKey: 'doubleArrow', onClick: addShapeDoubleArrow, Icon: ArrowLeftRight },
                          { label: 'Cloud', shapeKey: 'cloud', onClick: addShapeCloud, Icon: Cloud },
                          { label: 'Speech', shapeKey: 'speechBubble', onClick: addShapeSpeechBubble, Icon: MessageSquare },
                          { label: 'Zigzag', shapeKey: 'zigzag', onClick: addShapeZigzag, Icon: Activity },
                        ]
                          .filter((s) => !librarySearch || s.label.toLowerCase().includes(librarySearch.toLowerCase()))
                          .map(({ label, shapeKey, onClick: fn, Icon }) => (
                            <button
                              key={label}
                              type="button"
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData('application/x-shape-key', shapeKey);
                                e.dataTransfer.effectAllowed = 'copy';
                              }}
                              onClick={fn}
                              className="min-h-[5.25rem] rounded-xl border border-slate-200 bg-slate-50 flex flex-col items-center justify-center gap-1 py-2 px-1 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all cursor-grab active:cursor-grabbing"
                              title={`${label} — drag to canvas or click to add`}
                            >
                              {Icon && <Icon size={28} strokeWidth={2} className="text-slate-600 shrink-0" />}
                              <span className="text-xs font-medium text-slate-600 text-center leading-tight line-clamp-2">{label}</span>
                            </button>
                          ))}
                      </div>
                    </section>
                    <section>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-base font-bold text-slate-800">Abstract</span>
                        <span className="text-xs text-slate-500">7 &gt;</span>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { label: 'Semicircle', shapeKey: 'semicircle', onClick: addShapeSemicircle, Icon: Circle },
                          { label: 'Blob', shapeKey: 'blob', onClick: addShapeBlob, Icon: Circle },
                          { label: 'Crescent', shapeKey: 'crescent', onClick: addShapeCrescent, Icon: Circle },
                          { label: 'Ring', shapeKey: 'ring', onClick: addShapeRing, Icon: Circle },
                          { label: 'Wave', shapeKey: 'wave', onClick: addShapeWave, Icon: Minus },
                          { label: 'Tear', shapeKey: 'tear', onClick: addShapeTear, Icon: Triangle },
                          { label: 'Lightning', shapeKey: 'lightning', onClick: addShapeLightning, Icon: Zap },
                        ]
                          .filter((s) => !librarySearch || s.label.toLowerCase().includes(librarySearch.toLowerCase()))
                          .map(({ label, shapeKey, onClick: fn, Icon }) => (
                            <button
                              key={label}
                              type="button"
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData('application/x-shape-key', shapeKey);
                                e.dataTransfer.effectAllowed = 'copy';
                              }}
                              onClick={fn}
                              className="min-h-[5.25rem] rounded-xl border border-slate-200 bg-slate-50 flex flex-col items-center justify-center gap-1 py-2 px-1 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all cursor-grab active:cursor-grabbing"
                              title={`${label} — drag to canvas or click to add`}
                            >
                              {Icon && <Icon size={28} strokeWidth={2} className="text-slate-600 shrink-0" />}
                              <span className="text-xs font-medium text-slate-600 text-center leading-tight line-clamp-2">{label}</span>
                            </button>
                          ))}
                      </div>
                    </section>
                  </div>
                )}

                {/* Text — Title, Headline, Paragraph */}
                {activeTool === 'text' && (
                  <div className="space-y-2">
                    {[
                      { label: 'Title', sub: 'Large bold heading', onClick: addTextTitle },
                      { label: 'Headline', sub: 'Medium heading', onClick: addTextHeadline },
                      { label: 'Paragraph', sub: 'Body text', onClick: addTextParagraph },
                    ]
                      .filter((t) => !librarySearch || t.label.toLowerCase().includes(librarySearch.toLowerCase()))
                      .map(({ label, sub, onClick }) => (
                        <button
                          key={label}
                          onClick={onClick}
                          className="w-full text-left rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all"
                        >
                          <span className="text-sm font-bold text-slate-800 block">{label}</span>
                          <span className="text-xs text-slate-500 mt-0.5">{sub}</span>
                        </button>
                      ))}
                  </div>
                )}

                {/* Images — cloud library shown as previous-images grid */}
                {activeTool === 'images' && (() => {
                  const q = librarySearch.trim().toLowerCase();
                  const filteredGcpItems = gcpLibrary.items.filter((it) => !q || it.objectKey.toLowerCase().includes(q));
                  return (
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={openLibraryGcpFilePicker}
                      disabled={gcpLibraryLoading}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      <Upload size={18} />
                      Upload new image
                    </button>
                    <input
                      ref={gcpUploadInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                      className="hidden"
                      onChange={handleLibraryGcpFileChange}
                    />
                    <p className="text-[11px] text-slate-500">
                      Images are saved to cloud storage (max {GCP_IMAGES_MAX}). {gcpLibrary.count}/{gcpLibrary.max} used.
                    </p>
                    {gcpLibraryError && (
                      <p className="text-[11px] text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1">{gcpLibraryError}</p>
                    )}
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs font-bold text-slate-900">Previous images used</div>
                      <button
                        type="button"
                        onClick={() => refreshGcpLibrary()}
                        disabled={gcpLibraryLoading}
                        className="text-[11px] font-medium text-indigo-700 hover:underline disabled:opacity-40 shrink-0"
                      >
                        Refresh
                      </button>
                    </div>
                    {gcpLibraryLoading && gcpLibrary.items.length === 0 ? (
                      <div className="flex items-center justify-center gap-2 py-8 text-sm text-slate-500">
                        <Loader2 size={18} className="animate-spin text-indigo-600" aria-hidden />
                        Loading…
                      </div>
                    ) : gcpLibrary.items.length === 0 ? (
                      <p className="text-xs text-slate-500 leading-relaxed">
                        No images saved yet. Upload to add them here, then click a thumbnail to place it on the canvas.
                      </p>
                    ) : filteredGcpItems.length === 0 ? (
                      <p className="text-xs text-slate-500">No images match your search.</p>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-xs font-bold text-slate-900">All results</div>
                        <div className="grid grid-cols-2 gap-3">
                          {filteredGcpItems.map((it) => (
                            <div
                              key={it.objectKey}
                              className="relative group aspect-[3/4] rounded-sm border border-slate-200 bg-white overflow-hidden hover:border-slate-300 transition-colors"
                            >
                              <button
                                type="button"
                                className="absolute top-1.5 right-1.5 z-10 rounded-sm bg-white/95 p-1 shadow-sm border border-slate-200 opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remove from cloud"
                                onClick={() => setGcpImageDeleteConfirm({ objectKey: it.objectKey })}
                                disabled={gcpLibraryLoading || gcpDeleteBusy}
                              >
                                <Trash2 size={12} className="text-red-600" />
                              </button>
                              <button
                                type="button"
                                draggable
                                className="relative w-full h-full block min-h-0 cursor-grab active:cursor-grabbing"
                                onDragStart={(e) => {
                                  e.dataTransfer.setData('application/x-gcp-object-key', it.objectKey);
                                  e.dataTransfer.setData('application/x-image-url', gcpImageItemUrl(it));
                                  e.dataTransfer.effectAllowed = 'copy';
                                }}
                                onClick={() => addGcpImageFromLibrary(it)}
                                title="Drag to canvas or click to add"
                              >
                                <GcpLibraryThumbnail item={it} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })()}
              </div>
            </div>
          )}
        </aside>

        {/* Center — Canvas (layout-sized slot so large pages don’t break the flex layout; inner uses CSS scale) */}
        <main
          className={`flex-1 flex flex-col min-h-0 min-w-0 max-w-full ${preview ? 'bg-slate-900' : 'bg-gray-200'}`}
        >
          <div
            ref={mainCanvasAreaRef}
            className="flex-1 min-h-0 min-w-0 overflow-x-auto overflow-y-auto overscroll-contain"
            onDrop={preview ? undefined : handleCanvasDrop}
            onDragOver={preview ? undefined : handleCanvasDragOver}
          >
          <div
            className="inline-flex flex-col items-center gap-4 p-4 sm:p-6 lg:p-8 min-h-full box-border"
            style={{
              minWidth: `max(${Math.ceil(scaledLayoutW + 48)}px, 100%)`,
            }}
          >
          <div className="flex flex-col items-center gap-4 shrink-0">
            <div
              className={`relative mx-auto shrink-0 ${placeholderDropdownOpen ? 'overflow-visible' : 'overflow-hidden'}`}
              style={{
                width: Number.isFinite(scaledLayoutW) && scaledLayoutW > 0 ? scaledLayoutW : 400,
                height: Number.isFinite(scaledLayoutH) && scaledLayoutH > 0 ? scaledLayoutH : 600,
              }}
            >
              <div
                className="absolute left-0 top-0 origin-top-left"
                style={{
                  width: template.width,
                  height: template.height,
                  transform: `scale(${Number.isFinite(zoomScale) ? zoomScale : 1})`,
                }}
              >
            <div
              ref={canvasWrapperRef}
              className={`relative rounded-lg shadow-xl ring-1 ring-slate-200/80 bg-white ${preview ? 'pointer-events-none select-none' : ''}`}
              style={{
                width: template.width,
                height: template.height,
                overflow: clipContent ? 'hidden' : 'visible',
              }}
              onDrop={preview ? undefined : handleCanvasDrop}
              onDragOver={preview ? undefined : handleCanvasDragOver}
            >
              {USE_KONVA_EDITOR ? (
                <KonvaDesignCanvas
                  width={template.width}
                  height={template.height}
                  zoomScale={Number.isFinite(zoomScale) ? zoomScale : 1}
                  stageRef={konvaStageRef}
                  showGrid={showCanvasGrid && !preview}
                  background={{
                    bgType,
                    bgColor,
                    bgGradientFrom,
                    bgGradientTo,
                    bgImageUrl,
                  }}
                  onSelectionChange={handleKonvaSelection}
                  onLayoutChange={() => {
                    updateSelectionBoundsRef.current?.();
                    pushKonvaHistory();
                  }}
                />
              ) : (
                <canvas ref={canvasRef} className="block" />
              )}
            </div>
              </div>
              {/* Overlays outside CSS scale so toolbar & labels stay readable at any zoom / page size */}
              {!preview && (
                <div
                  className={`absolute inset-0 z-[40] ${placeholderDropdownOpen ? 'overflow-visible' : 'overflow-hidden'}`}
                  style={{ pointerEvents: 'none' }}
                >
                  {unselectedPlaceholderOverlays.map((b) => {
                    const boxW = Math.max(1, b.width * zoomScale);
                    const boxLeft = b.left * zoomScale;
                    const boxTop = b.top * zoomScale;
                    const boxH = Math.max(1, b.height * zoomScale);
                    const badgeTop = boxTop + boxH + 4;
                    return (
                      <React.Fragment key={b.id}>
                        <div
                          className="absolute pointer-events-none rounded-md border border-dashed border-indigo-400/30 bg-indigo-500/[0.03] opacity-50"
                          style={{
                            left: boxLeft,
                            top: boxTop,
                            width: boxW,
                            height: boxH,
                          }}
                          title={`${b.label} (${b.fieldKey})`}
                          aria-hidden
                        />
                        <div
                          className="absolute pointer-events-none"
                          style={{
                            left: boxLeft,
                            top: badgeTop,
                            width: boxW,
                            maxWidth: 'min(280px, calc(100vw - 2rem))',
                          }}
                        >
                          <span className="inline-flex items-center gap-1 rounded-md bg-slate-500/10 text-slate-500 border border-slate-200/70 text-[10px] font-medium px-1.5 py-0.5 shadow-sm truncate max-w-full">
                            <Tag size={11} className="shrink-0 opacity-70" aria-hidden />
                            <span className="truncate">{b.label}</span>
                          </span>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  {selectedObj && selectionBounds && (() => {
                    const innerPh = getInnerShapeLabelText(selectedObj);
                    const phTarget = getPlaceholderTargetFromSelection(fabricRef.current);
                    const phLabel =
                      phTarget && phTarget.fieldKey
                        ? resolvePlaceholderLabel(phTarget.fieldKey, phTarget.type, textPlaceholders, imagePlaceholders)
                        : null;
                    const isTextOrPlaceholderLayer =
                      selectedObj.type === 'i-text' ||
                      selectedObj.type === 'textbox' ||
                      isKonvaTextSelection(selectedObj) ||
                      isTextBoxFrame(selectedObj) ||
                      isTextBoxGroup(selectedObj) ||
                      selectedObj.type === 'text' ||
                      selectedObj.type === 'image' ||
                      Boolean(innerPh);
                    /** Same floating bar as text: centered under selection, compact width (no empty gutter). */
                    const floatToolbarBelow =
                      isTextOrPlaceholderLayer ||
                      isEmbeddableBareShape(selectedObj) ||
                      (selectedObj.type === 'group' && !selectedObj._shapeLabelGroup) ||
                      selectedObj.type === 'activeSelection';
                    const placeholderList =
                      selectedObj.type === 'image' && !innerPh ? imagePlaceholders : textPlaceholders;
                    const isImageToolbarStack = selectedObj.type === 'image' && !innerPh;
                    // Shape captions: use Document sidebar "Data field" <select> only — no empty
                    // placeholder chip on the canvas toolbar for labeled shapes.
                    const showPlaceholderToolbar =
                      selectedObj.type === 'i-text' ||
                      selectedObj.type === 'textbox' ||
                      selectedObj.type === 'text' ||
                      isKonvaTextSelection(selectedObj) ||
                      selectedObj.type === 'image';
                    const toolbarScreenW = Math.max(88, Math.min(300, Math.max(0, scaledLayoutW - 12)));
                    const selW = selectionBounds.width * zoomScale;
                    const selH = selectionBounds.height * zoomScale;
                    const selLeft = selectionBounds.left * zoomScale;
                    const selTop = selectionBounds.top * zoomScale;
                    const selRight = selLeft + selW;
                    const selBottom = selTop + selH;
                    const toolbarApproxH = isImageToolbarStack ? 88 : 34;
                    const gap = isImageToolbarStack ? 12 : 6;
                    const estToolbarW = isImageToolbarStack
                      ? Math.min(260, scaledLayoutW - CANVAS_TOOLBAR_PAD * 2)
                      : Math.min(300, Math.max(120, selW + 96));
                    let leftScreen;
                    let topScreen;
                    let transform;
                    if (floatToolbarBelow) {
                      const centerX = selLeft + selW / 2;
                      leftScreen = centerX;
                      let belowTop = selBottom + gap;
                      if (belowTop + toolbarApproxH > scaledLayoutH - CANVAS_TOOLBAR_PAD) {
                        belowTop = Math.max(CANVAS_TOOLBAR_PAD, selTop - toolbarApproxH - gap);
                      }
                      topScreen = belowTop;
                      transform = 'translateX(-50%)';
                    } else {
                      const spaceRight = scaledLayoutW - selRight - 8;
                      leftScreen = spaceRight >= toolbarScreenW ? selRight + 8 : selLeft - toolbarScreenW - 8;
                      topScreen = (selectionBounds.top + selectionBounds.height / 2) * zoomScale;
                      transform = 'translateY(-50%)';
                    }
                    const clamped = clampPlaceholderToolbarToCanvas({
                      leftScreen,
                      topScreen,
                      dropdownOpensUp: false,
                      dropdownOpen: false,
                      toolbarW: floatToolbarBelow ? estToolbarW : toolbarScreenW,
                      toolbarH: toolbarApproxH,
                      canvasW: scaledLayoutW,
                      canvasH: scaledLayoutH,
                      centerToolbarX: floatToolbarBelow,
                    });
                    leftScreen = clamped.left;
                    topScreen = clamped.top;
                    return (
                      <div
                        className={`absolute bg-white/95 backdrop-blur-sm rounded-lg shadow-md border border-slate-200/90 ${
                          isTextOrPlaceholderLayer && isImageToolbarStack
                            ? 'flex flex-col items-stretch gap-1 py-1 px-1 min-w-[min(260px,calc(100vw-2rem))]'
                            : `flex flex-row items-center gap-0.5 py-0.5 px-0.5 ${
                                floatToolbarBelow ? 'min-h-[32px] w-max' : 'min-h-[44px] py-1.5 px-1.5 rounded-xl shadow-xl'
                              }`
                        }`}
                        style={{
                          left: leftScreen,
                          top: topScreen,
                          transform,
                          ...(floatToolbarBelow && !isImageToolbarStack
                            ? { maxWidth: 'min(300px, calc(100vw - 2rem))' }
                            : !floatToolbarBelow
                              ? { width: toolbarScreenW, maxWidth: 'min(300px, calc(100vw - 2rem))' }
                              : { maxWidth: 'min(300px, calc(100vw - 2rem))' }),
                          pointerEvents: 'auto',
                          zIndex: placeholderDropdownOpen ? 10055 : 50,
                        }}
                      >
                        <div className="flex items-center gap-0.5 shrink-0 justify-center">
                        <button
                          type="button"
                          onClick={() => deleteCanvasObject()}
                          className={`rounded-md text-slate-500 hover:bg-slate-100 transition-colors shrink-0 ${floatToolbarBelow ? 'p-1' : 'p-2.5 rounded-lg'}`}
                          title="Delete"
                        >
                          <Trash2 size={floatToolbarBelow ? 14 : 18} />
                        </button>
                        <button
                          type="button"
                          onClick={copySelected}
                          className={`rounded-md text-slate-500 hover:bg-slate-100 transition-colors shrink-0 ${floatToolbarBelow ? 'p-1' : 'p-2.5 rounded-lg'}`}
                          title="Copy"
                        >
                          <Copy size={floatToolbarBelow ? 14 : 18} />
                        </button>
                        <button
                          type="button"
                          onClick={pasteFromClipboard}
                          disabled={!clipboard}
                          className={`rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0 ${floatToolbarBelow ? 'p-1' : 'p-2.5 rounded-lg'}`}
                          title="Paste"
                        >
                          <ClipboardPaste size={floatToolbarBelow ? 14 : 18} />
                        </button>
                        </div>
                        {showPlaceholderToolbar && (
                          <PlaceholderFieldDropdown
                            open={placeholderDropdownOpen}
                            onOpenChange={setPlaceholderDropdownOpen}
                            options={placeholderList}
                            fieldKey={phTarget?.fieldKey}
                            displayLabel={phLabel}
                            onSelect={(key) => assignPlaceholderToSelected(key)}
                            onClear={() => assignPlaceholderToSelected(undefined)}
                            isImageStack={isImageToolbarStack}
                            className={
                              isImageToolbarStack
                                ? 'relative flex w-full min-w-0'
                                : 'relative flex items-center shrink-0 w-auto max-w-[min(200px,calc(100vw-6rem))]'
                            }
                          />
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
          </div>
          </div>
          {!preview && (
            <CanvasHorizontalScrollbar
              viewportRef={mainCanvasAreaRef}
              contentWidth={scaledLayoutW + 48}
            />
          )}
        </main>

        {/* Right sidebar — Document */}
        <aside className="w-80 max-w-80 min-w-0 shrink-0 overflow-x-hidden bg-gray-100 border-l border-gray-300 shadow-sm flex flex-col min-h-0">
          <div className="p-4 border-b border-gray-200 shrink-0">
            <h2 className="text-sm font-semibold text-slate-800">Document</h2>
            <p className="text-xs text-slate-500 mt-0.5">Properties & layers</p>
          </div>
          <div className="flex-1 min-h-0 min-w-0 overflow-x-hidden overflow-y-auto p-4 space-y-5">
            {/* Page & document size (moved from top bar) */}
            <div className="rounded-xl bg-slate-50/80 p-3 border border-slate-200/80">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2.5">Page & size</div>
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 mb-1">Preset</label>
                  <select
                    className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    value={presetName}
                    onChange={(e) => applyPreset(e.target.value)}
                  >
                    {PRESETS.map((p) => (
                      <option key={p.name} value={p.name}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-[10px] text-slate-500 leading-snug">
                  Page size is set here. Loading a template does not change width or height.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] font-medium text-slate-500 mb-1">Width (horizontal)</label>
                    <div className="flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden focus-within:ring-1 focus-within:ring-indigo-400">
                        <input
                          type="number"
                          min={documentSizeMin(unit)}
                          step={documentSizeStep(unit)}
                          value={Number.isFinite(widthMm) ? widthMm : ''}
                          onChange={(e) => {
                            setWidthMm(Math.max(documentSizeMin(unit), Number(e.target.value) || documentSizeMin(unit)));
                            setPresetName('Custom size');
                          }}
                          className="w-full min-w-0 px-2 py-2 text-sm text-slate-900 tabular-nums border-0 focus:ring-0"
                          title="Width"
                        />
                        <span className="text-[10px] text-slate-400 pr-2 shrink-0">{unit === 'Millimeter' ? 'mm' : unit === 'Inch' ? 'in' : 'px'}</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-slate-500 mb-1">Height (vertical)</label>
                    <div className="flex items-center rounded-lg border border-slate-200 bg-white overflow-hidden focus-within:ring-1 focus-within:ring-indigo-400">
                        <input
                          type="number"
                          min={documentSizeMin(unit)}
                          step={documentSizeStep(unit)}
                          value={Number.isFinite(heightMm) ? heightMm : ''}
                          onChange={(e) => {
                            setHeightMm(Math.max(documentSizeMin(unit), Number(e.target.value) || documentSizeMin(unit)));
                            setPresetName('Custom size');
                          }}
                          className="w-full min-w-0 px-2 py-2 text-sm text-slate-900 tabular-nums border-0 focus:ring-0"
                          title="Height"
                        />
                        <span className="text-[10px] text-slate-400 pr-2 shrink-0">{unit === 'Millimeter' ? 'mm' : unit === 'Inch' ? 'in' : 'px'}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-slate-500 mb-1">Unit</label>
                  <select
                    className="w-full rounded-lg border border-slate-200 bg-white px-2 py-2 text-sm text-slate-800 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                    title="Unit"
                  >
                    <option value="Millimeter">mm</option>
                    <option value="Pixel">px</option>
                    <option value="Inch">in</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                  <input type="checkbox" checked={showCanvasGrid} onChange={(e) => setShowCanvasGrid(e.target.checked)} className="rounded border-slate-300" />
                  Show gridlines
                </label>
                {currentTemplateId && (
                  <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={autoSaveEnabled} onChange={(e) => setAutoSaveEnabled(e.target.checked)} className="rounded border-slate-300" />
                    Auto-save changes
                  </label>
                )}
              </div>
            </div>

            {/* Background */}
            <div className="rounded-xl bg-slate-50/80 p-3">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2.5">Background</div>
              <div className="space-y-2">
                <div className="inline-flex rounded-lg bg-slate-100 p-0.5 w-full">
                  <button onClick={() => setBgType('solid')} className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium ${bgType === 'solid' ? 'bg-white shadow-sm' : ''}`}>Solid</button>
                  <button onClick={() => setBgType('gradient-linear')} className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium ${bgType === 'gradient-linear' ? 'bg-white shadow-sm' : ''}`}>Linear</button>
                  <button onClick={() => setBgType('gradient-radial')} className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium ${bgType === 'gradient-radial' ? 'bg-white shadow-sm' : ''}`}>Radial</button>
                  <button onClick={() => setBgType('image')} className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium ${bgType === 'image' ? 'bg-white shadow-sm' : ''}`}>Image</button>
                </div>
                {bgType === 'solid' && (
                  <ColorPickerField label="Color" value={bgColor} onChange={setBgColor} compact />
                )}
                {(bgType === 'gradient-linear' || bgType === 'gradient-radial') && (
                  <div className="flex gap-2">
                    <div className="flex-1 min-w-0">
                      <ColorPickerField label="From" value={bgGradientFrom} onChange={setBgGradientFrom} compact />
                    </div>
                    <div className="flex-1 min-w-0">
                      <ColorPickerField label="To" value={bgGradientTo} onChange={setBgGradientTo} compact />
                    </div>
                  </div>
                )}
                {bgType === 'image' && (
                  <div className="space-y-2">
                    <input
                      ref={bgInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setBgImageUrl(ev.target?.result ?? null);
                          setBgType('image');
                        };
                        reader.readAsDataURL(file);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => bgInputRef.current?.click()}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      Choose image
                    </button>
                    {bgImageUrl && (
                      <button
                        type="button"
                        onClick={() => setBgImageUrl(null)}
                        className="w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-200"
                      >
                        Remove image
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Layers — up second */}
            <div className="rounded-xl bg-slate-50/80 p-3">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2.5 flex items-center gap-1.5">
                <Layers size={14} /> Layers
              </div>
              <div className="space-y-1 max-h-40 overflow-auto">
                {bgType === 'image' && bgImageUrl && (
                  <div
                    className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs bg-slate-200/60 border border-slate-300/80 text-slate-700"
                    title="Background image (not a movable layer)"
                  >
                    <ImageIcon size={12} className="shrink-0 opacity-80" aria-hidden />
                    <span className="flex-1 min-w-0 truncate font-medium">Background image</span>
                  </div>
                )}
                {(USE_KONVA_EDITOR
                  ? [...konvaSceneObjects].slice().reverse()
                  : (fabricRef.current?.getObjects() || []).slice().reverse()
                ).map((obj, revIndex) => {
                  if (USE_KONVA_EDITOR) {
                    const index = konvaSceneObjects.length - 1 - revIndex;
                    const name =
                      obj.type === 'text'
                        ? (obj.text || 'Text').slice(0, 20)
                        : obj.type === 'image'
                          ? 'Image'
                          : obj.fabricType || obj.type || 'Object';
                    const phKey = obj.fieldKey;
                    const phLayer = phKey
                      ? resolvePlaceholderLabel(phKey, obj.type, textPlaceholders, imagePlaceholders)
                      : null;
                    const isLocked = !!obj.locked;
                    const isActive = konvaSelectedIds.includes(obj.id);
                    return (
                      <div
                        key={`${obj.id}-${layersKey}`}
                        className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs ${isActive ? 'bg-indigo-100 text-indigo-900' : 'bg-white border border-slate-200'}`}
                      >
                        <span className="flex-1 min-w-0 cursor-pointer flex flex-col gap-0.5" onClick={() => selectObjectByIndex(index)}>
                          <span className="truncate font-medium">{name}</span>
                          {phLayer && (
                            <span className="truncate text-[10px] text-indigo-600 flex flex-col gap-0.5" title={`${phLayer} — ${phKey}`}>
                              <span className="flex items-center gap-0.5 font-semibold">
                                <Tag size={10} className="shrink-0 opacity-80" aria-hidden />
                                {phLayer}
                              </span>
                              <span className="font-mono text-[9px] text-slate-500 truncate pl-3.5">{phKey}</span>
                            </span>
                          )}
                        </span>
                        <button type="button" onClick={() => toggleLock(obj)} className="p-0.5 rounded text-slate-500 hover:bg-slate-200" title={isLocked ? 'Unlock' : 'Lock'}>
                          {isLocked ? <Lock size={12} /> : <Unlock size={12} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            useEditorStore.getState().selectObject(obj.id);
                            handleKonvaSelection(obj);
                            bringToFront();
                          }}
                          className="p-0.5 rounded text-slate-500 hover:bg-slate-200"
                          title="Bring to front"
                        >
                          <ArrowUp size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            useEditorStore.getState().selectObject(obj.id);
                            handleKonvaSelection(obj);
                            sendToBack();
                          }}
                          className="p-0.5 rounded text-slate-500 hover:bg-slate-200"
                          title="Send to back"
                        >
                          <ArrowDown size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteCanvasObject(obj);
                          }}
                          className="p-0.5 rounded text-slate-500 hover:bg-red-50 hover:text-red-600"
                          title="Delete layer"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    );
                  }
                  const index = fabricRef.current.getObjects().length - 1 - revIndex;
                  const innerL = getInnerShapeLabelText(obj);
                  const name =
                    obj.type === 'group' && obj._shapeLabelGroup
                      ? `${(innerL?.text || 'Label').slice(0, 16)} · shape`
                      : obj.type === 'i-text' || obj.type === 'text'
                        ? (obj.text || 'Text').slice(0, 20)
                        : obj.type || 'Object';
                  const phKey = innerL?.fieldKey || obj.fieldKey;
                  const phLayer = phKey ? resolvePlaceholderLabel(phKey, innerL?.type || obj.type, textPlaceholders, imagePlaceholders) : null;
                  const isLocked = obj.selectable === false;
                  const isActive = selectedObj === obj;
                  return (
                    <div
                      key={`${index}-${layersKey}`}
                      className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs ${isActive ? 'bg-indigo-100 text-indigo-900' : 'bg-white border border-slate-200'}`}
                    >
                      <span className="flex-1 min-w-0 cursor-pointer flex flex-col gap-0.5" onClick={() => selectObjectByIndex(index)}>
                        <span className="truncate font-medium">{name}</span>
                        {phLayer && (
                          <span className="truncate text-[10px] text-indigo-600 flex flex-col gap-0.5" title={`${phLayer} — ${phKey}`}>
                            <span className="flex items-center gap-0.5 font-semibold">
                              <Tag size={10} className="shrink-0 opacity-80" aria-hidden />
                              {phLayer}
                            </span>
                            <span className="font-mono text-[9px] text-slate-500 truncate pl-3.5">{phKey}</span>
                          </span>
                        )}
                      </span>
                      <button type="button" onClick={() => toggleLock(obj)} className="p-0.5 rounded text-slate-500 hover:bg-slate-200" title={isLocked ? 'Unlock' : 'Lock'}>
                        {isLocked ? <Lock size={12} /> : <Unlock size={12} />}
                      </button>
                      <button type="button" onClick={() => { fabricRef.current?.setActiveObject(obj); fabricRef.current?.renderAll(); bringToFront(); }} className="p-0.5 rounded text-slate-500 hover:bg-slate-200" title="Bring to front"><ArrowUp size={12} /></button>
                      <button type="button" onClick={() => { fabricRef.current?.setActiveObject(obj); fabricRef.current?.renderAll(); sendToBack(); }} className="p-0.5 rounded text-slate-500 hover:bg-slate-200" title="Send to back"><ArrowDown size={12} /></button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteCanvasObject(obj);
                        }}
                        className="p-0.5 rounded text-slate-500 hover:bg-red-50 hover:text-red-600"
                        title="Delete layer"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-1 mt-2">
                <button
                  type="button"
                  onClick={() => deleteCanvasObject()}
                  disabled={!selectedObj}
                  className="flex-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs hover:bg-red-50 hover:border-red-200 hover:text-red-700 disabled:opacity-40 disabled:pointer-events-none disabled:hover:bg-white disabled:hover:border-slate-200 disabled:hover:text-inherit"
                >
                  Delete
                </button>
                <button type="button" onClick={bringForward} className="flex-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs hover:bg-slate-50">Forward</button>
                <button type="button" onClick={sendBackward} className="flex-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs hover:bg-slate-50">Backward</button>
              </div>
            </div>

            {/* Context panel — selection-based properties */}
            <div className="rounded-xl bg-slate-50/80 p-3 min-w-0">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2.5">
                {selectedObj ? `Edit: ${selectedObj.type}` : 'Selection'}
              </div>
              {!selectedObj ? (
                <p className="text-xs text-slate-500">Select an object on the canvas to edit its properties here.</p>
              ) : (
                <div className="space-y-4 min-w-0">
                  {(() => {
                    const f = fabricRef.current;
                    const innerL = getInnerShapeLabelText(selectedObj);
                    const canDataField =
                      selectedObj.type === 'i-text' ||
                      selectedObj.type === 'textbox' ||
                      selectedObj.type === 'text' ||
                      selectedObj._konvaEditor ||
                      isTextBoxFrame(selectedObj) ||
                      isTextBoxGroup(selectedObj) ||
                      selectedObj.type === 'image' ||
                      Boolean(innerL);
                    if (!canDataField) return null;
                    const target = USE_KONVA_EDITOR
                      ? getKonvaSelectedSceneNode()
                      : getPlaceholderTargetFromSelection(f);
                    const opts =
                      selectedObj.type === 'image' && !innerL ? imagePlaceholders : textPlaceholders;
                    const cur = target?.fieldKey || '';
                    return (
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-slate-600">Placeholder</label>
                        <select
                          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                          value={cur}
                          onChange={(e) => {
                            const v = e.target.value;
                            assignPlaceholderToSelected(v ? v : undefined);
                          }}
                        >
                          <option value="">None</option>
                          {opts.map((opt) => (
                            <option key={opt.key} value={opt.key}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {cur ? (
                          <p className="text-[10px] text-slate-500 leading-snug">
                            {resolvePlaceholderLabel(cur, selectedObj.type, textPlaceholders, imagePlaceholders) || cur}
                          </p>
                        ) : null}
                      </div>
                    );
                  })()}
                  {selectedObj.type === 'image' && USE_KONVA_EDITOR && (
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-400"
                        checked={selectedObj.lockAspectRatio !== false}
                        onChange={(e) => {
                          const id = useEditorStore.getState().selectedId;
                          if (!id) return;
                          useEditorStore.getState().updateImageNode(id, { lockAspectRatio: e.target.checked });
                          setSelectedObj((prev) => (prev ? { ...prev, lockAspectRatio: e.target.checked } : prev));
                          pushKonvaHistory();
                          setLayersKey((k) => k + 1);
                        }}
                      />
                      <span className="text-xs font-medium text-slate-600">Lock aspect ratio</span>
                    </label>
                  )}
                  {selectedObj.type !== 'i-text' && selectedObj.type !== 'textbox' && selectedObj.type !== 'text' && selectedObj.type !== 'image' && (
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-slate-600">Shape color</div>
                      <ColorPickerField
                        label="Fill"
                        value={typeof selectedObj.fill === 'string' ? selectedObj.fill : shapeFillColor}
                        onChange={(v) => {
                          setShapeFillColor(v);
                          if (USE_KONVA_EDITOR && selectedObj.id) {
                            useEditorStore.getState().updateShapeNode(selectedObj.id, { fill: v, strokeWidth: selectedObj.strokeWidth || 2 });
                            pushKonvaHistory();
                            setLayersKey((k) => k + 1);
                            return;
                          }
                          if (selectedObj.type === 'group' && selectedObj.getObjects) {
                            selectedObj.getObjects().forEach((child) => {
                              if (child.type === 'i-text' || child.type === 'textbox' || child.type === 'text') return;
                              if (child.fill != null) child.set('fill', v);
                            });
                          } else if (selectedObj.type !== 'line') {
                            selectedObj.set('fill', v);
                          }
                          selectedObj.setCoords?.();
                          fabricRef.current?.renderAll();
                          setLayersKey((k) => k + 1);
                        }}
                        swatches={SHAPE_COLORS.map((c) => c.fill)}
                        compact
                      />
                      <ColorPickerField
                        label="Stroke"
                        value={typeof selectedObj.stroke === 'string' ? selectedObj.stroke : shapeStrokeColor}
                        onChange={(v) => {
                          setShapeStrokeColor(v);
                          if (USE_KONVA_EDITOR && selectedObj.id) {
                            useEditorStore.getState().updateShapeNode(selectedObj.id, { stroke: v, strokeWidth: v === 'transparent' ? 0 : selectedObj.strokeWidth || 2 });
                            pushKonvaHistory();
                            setLayersKey((k) => k + 1);
                            return;
                          }
                          if (selectedObj.type === 'group' && selectedObj.getObjects) {
                            selectedObj.getObjects().forEach((child) => {
                              if (child.type === 'i-text' || child.type === 'textbox' || child.type === 'text') return;
                              if (child.stroke != null) child.set('stroke', v);
                            });
                          } else {
                            selectedObj.set('stroke', v);
                          }
                          selectedObj.setCoords?.();
                          fabricRef.current?.renderAll();
                          setLayersKey((k) => k + 1);
                        }}
                        swatches={SHAPE_COLORS.map((c) => c.stroke)}
                        allowTransparent
                        compact
                      />
                    </div>
                  )}
                  {(selectedObj.type === 'i-text' ||
                    selectedObj.type === 'textbox' ||
                    selectedObj.type === 'text' ||
                    selectedObj._konvaEditor ||
                    isTextBoxFrame(selectedObj) ||
                    isTextBoxGroup(selectedObj) ||
                    getInnerShapeLabelText(selectedObj)) && (
                    <div className="space-y-3 min-w-0">
                      <label className="block text-sm font-medium text-slate-600">Text</label>
                      <input
                        className="w-full min-w-0 max-w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-base text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                        value={textProps.text}
                        onChange={(e) => updateSelectedTextProps({ text: e.target.value })}
                      />
                      <div className="flex gap-2 min-w-0">
                        <input
                          key={`fs-${selectedObj?.id}`}
                          type="number"
                          min="1"
                          max="9999"
                          className="min-w-0 flex-1 basis-0 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-base text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                          defaultValue={textProps.fontSize}
                          onChange={(e) => {
                            const n = parseInt(e.target.value, 10);
                            if (Number.isFinite(n) && n >= 1 && n <= 9999) {
                              updateSelectedTextProps({ fontSize: n });
                            }
                          }}
                          onBlur={(e) => {
                            const n = parseInt(e.target.value, 10);
                            if (!Number.isFinite(n) || n < 1) {
                              e.target.value = String(textProps.fontSize || 12);
                            } else if (n > 9999) {
                              e.target.value = '9999';
                              updateSelectedTextProps({ fontSize: 9999 });
                            }
                          }}
                        />
                        <select
                          className="min-w-0 flex-1 basis-0 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-base text-slate-900 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
                          value={textProps.fontFamily}
                          onChange={(e) => {
                            const font = e.target.value;
                            const nextTy = { ...textProps, fontFamily: font };
                            updateSelectedTextProps({ fontFamily: font });
                            void applyFontFamilyQuick(font, nextTy).then(() => {
                              const stage = konvaStageRef.current;
                              const objects = useEditorStore.getState().scene.objects;
                              refreshKonvaTextStyles(stage, objects);
                              setLayersKey((k) => k + 1);
                            });
                          }}
                        >
                          {GOOGLE_FONTS.map((f) => (
                            <option key={f} value={f}>{f}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-1.5">
                        <button type="button" onClick={() => updateSelectedTextProps({ fontWeight: textProps.fontWeight === 'bold' ? 'normal' : 'bold' })} className={`flex-1 rounded-md border px-2 py-2 text-sm font-bold ${textProps.fontWeight === 'bold' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white'}`}>B</button>
                        <button type="button" onClick={() => updateSelectedTextProps({ fontStyle: textProps.fontStyle === 'italic' ? 'normal' : 'italic' })} className={`flex-1 rounded-md border px-2 py-2 text-sm italic ${textProps.fontStyle === 'italic' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white'}`}>I</button>
                        <button type="button" onClick={() => updateSelectedTextProps({ underline: !textProps.underline })} className={`flex-1 rounded-md border px-2 py-2 text-sm underline ${textProps.underline ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white'}`}>U</button>
                      </div>
                      <div className="flex gap-1.5">
                        <button type="button" onClick={() => updateSelectedTextProps({ textAlign: 'left' })} className={`flex-1 rounded-md border px-2 py-2 text-sm font-medium ${textProps.textAlign === 'left' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white'}`} title="Align left">L</button>
                        <button type="button" onClick={() => updateSelectedTextProps({ textAlign: 'center' })} className={`flex-1 rounded-md border px-2 py-2 text-sm font-medium ${textProps.textAlign === 'center' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white'}`} title="Align center">C</button>
                        <button type="button" onClick={() => updateSelectedTextProps({ textAlign: 'right' })} className={`flex-1 rounded-md border px-2 py-2 text-sm font-medium ${textProps.textAlign === 'right' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 bg-white'}`} title="Align right">R</button>
                      </div>
                      <ColorPickerField
                        label="Color"
                        value={textProps.fill}
                        onChange={(v) => updateSelectedTextProps({ fill: v })}
                        compact
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

        </aside>
      </div>
      {toastMessage && (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed bottom-6 left-1/2 z-[250] max-w-[min(90vw,24rem)] -translate-x-1/2 rounded-xl border border-slate-200 bg-slate-900 px-4 py-3 text-center text-sm font-medium text-white shadow-lg"
        >
          {toastMessage}
        </div>
      )}
    </div>
  );
}
